
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Navigate, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { MAX_ATTEMPTS, WINNING_SCORE, getDeck } from './constants';
import { GameState, CardData, Difficulty, User, DailyCheckIn, CustomDeck, HomeAnnouncementConfig, OfficialWeChatLiveCodeConfig, OnlineAccessConfig } from './types';
import {
  storage,
  migrateUserData,
  fetchHomeAnnouncementConfig,
  fetchOnlineAccessConfig,
  fetchManagedTournamentMasterLists,
  fetchManagedDrawPresetDecks,
  fetchOfficialWeChatLiveCodeConfig,
  getActiveOfficialWeChatQrCode,
  sanitizeHomeAnnouncementConfig,
  sanitizeOnlineAccessConfig,
  sanitizeOfficialWeChatLiveCodeConfig
} from './storage';
import { TrickSlotMachine } from './components/TrickSlotMachine';
import PlayerPanel from './components/PlayerPanel';
import WinnerModal from './components/WinnerModal';
import AuthModal, { AUTH_AUTO_LOGIN_KEY } from './components/AuthModal';
import CustomDeckEditor from './components/CustomDeckEditor';
import DeckSelectionModal from './components/DeckSelectionModal';
import TournamentEditor from './components/TournamentEditor';
import TournamentGame from './components/TournamentGame';
import TournamentHistory from './components/TournamentHistory';
import TournamentFinalSubmission from './components/TournamentFinalSubmission';
import AdminDashboard from './components/AdminDashboard';
import { ONLINE_ACCESS_UPDATED_EVENT } from './components/AdminOnlineSettings';
import { FreestyleLobby } from './components/FreestyleLobby';
import { FreestyleRoomComponent } from './components/FreestyleRoom';
import { BattleModeSelection } from './components/BattleModeSelection';
import { GameLobby } from './components/GameLobby';
import { UserProfile, UserProfileTab } from './components/UserProfile';
import { KendamaMapMode } from './components/KendamaMapMode';
import { KendamaArchive } from './components/KendamaArchive';
import { KendamaWiki } from './components/KendamaWiki';
import { CKO2026WarmupMode } from './components/CKO2026WarmupMode';
import CKO2026WarmupAdmin from './components/CKO2026WarmupAdmin';
import KWC2026ActivityMode from './components/KWC2026ActivityMode';
import { KWC2026PointMode } from './components/KWC2026PointMode';
import Messages from './components/Messages';
import { FloatingPokerCard } from './components/CheckInSplash';
import { CheckInCalendar } from './components/CheckInCalendar';
import { MatrixRainLoader, MatrixRainCanvas, MATRIX_SPLASH_MIN_DURATION_MS, MATRIX_SPLASH_EXIT_DURATION_MS, MATRIX_SPLASH_PROGRESS_SETTLE_MS } from './components/MatrixRainLoader';
import { CheckCircle2, XCircle, Award, Globe, ArrowLeft, Home, User as UserIcon, LayoutGrid, Swords, Trophy, Settings, Play, History, Volume2, Volume1, VolumeX, Lightbulb, Gamepad2, MapPinned, RotateCcw, Undo2, Menu, X, BookOpen, UploadCloud, MessageCircle, Calendar, CalendarCheck, Loader2 } from 'lucide-react';
import ckoPartnerLogo from './ckopng/ckow1.png';
import csckPartnerLogo from './splogo/CSCklogo.png';
import officialWeChatIcon from './wxqr/vpng.png';
import officialWeChatQrModal from './wxqr/qunwailian.png';
import ktbLogo from './ckopng/ktb-logo.png';
import kwcLogoBackground from './kwc/kwclogo.png';
import beianLogo from './beianlogo.png';
import { useI18n } from './i18n';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { isLocalBackend, phpApiUrl, phpAuthHeaders, setAuthToken, isNativeApp } from './apiEnv';
import { parseRoomInvite, RoomInvite } from './deepLink';
import { LiquidGlass } from '@ajuarezso/capacitor-liquid-glass';

/**
 * 判断当前设备是否支持原生 Liquid Glass 底栏（iOS 26+）。
 * iOS 26 起 WebKit 把 UA 里的系统版本冻结在 18_x（UA 缩减），UA 已经不能反映真实版本，
 * 因此优先通过原生插件读取真实系统版本，仅在插件不可用时才退回 UA 判断。
 */
const detectLiquidGlassSupport = async (): Promise<boolean> => {
  try {
    const info = await Device.getInfo();
    const major = Number.parseInt(info.osVersion ?? '', 10);
    if (Number.isFinite(major)) return major >= 26;
  } catch {
    // 原生插件不可用，退回 UA 判断
  }
  const uaMajor = Number(navigator.userAgent.match(/OS (\d+)_/)?.[1] ?? 0);
  return uaMajor >= 26;
};

const MANAGED_MASTER_LISTS_VERSION_KEY = 'kendama-managed-master-lists-version';

// 退出登录提示层的最短展示时间：本地退出往往几十毫秒就结束，
// 不留一点时间的话提示会一闪而过，看起来像闪屏。
const LOGOUT_OVERLAY_MIN_DURATION_MS = 450;
const MANAGED_DRAW_PRESET_DECKS_VERSION_KEY = 'kendama-managed-draw-preset-decks-version';
const HOME_ANNOUNCEMENT_VERSION_KEY = 'kendama-home-announcement-version';
const HOME_ANNOUNCEMENT_UPDATED_EVENT = 'kendama-home-announcement-updated';
const HOME_ANNOUNCEMENT_LAST_SEEN_KEY = 'kendama-home-announcement-last-seen';
const OFFICIAL_WECHAT_LIVE_CODE_VERSION_KEY = 'kendama-official-wechat-live-code-version';
const OFFICIAL_WECHAT_LIVE_CODE_UPDATED_EVENT = 'kendama-official-wechat-live-code-updated';
const OFFICIAL_WECHAT_FLOAT_POSITION_KEY = 'kendama-official-wechat-float-position';
const HOME_MODE_LAYOUT_STORAGE_KEY = 'kendama-home-mode-layout-v2';
const CHECK_IN_LEGACY_KEY = 'kendama_daily_checkin';
const getCheckInStorageKey = (uid: string) => `kendama_daily_checkin_${uid}`;
const EMPTY_DAILY_CHECK_IN: DailyCheckIn = { checkedInDates: [], streak: 0, makeupChances: 0 };

const sanitizeDailyCheckIn = (raw: unknown): DailyCheckIn | null => {
  if (!raw || typeof raw !== 'object') return null;
  const parsed = raw as Partial<DailyCheckIn>;
  if (!Array.isArray(parsed.checkedInDates)) return null;
  return {
    checkedInDates: parsed.checkedInDates.filter((d: unknown): d is string => typeof d === 'string'),
    streak: typeof parsed.streak === 'number' ? parsed.streak : 0,
    makeupChances: typeof parsed.makeupChances === 'number' ? parsed.makeupChances : 0,
  };
};

// 读取设备本地的签到备份（按用户 key，其次旧版全局 key）。
// 这份备份是数据库之外的第二道保险：接口失败、换新安装包、或多设备覆盖时都能拿它把历史找回来。
function readLocalDailyCheckIn(uid: string): DailyCheckIn | null {
  try {
    const perUserRaw = window.localStorage.getItem(getCheckInStorageKey(uid));
    const raw = perUserRaw || window.localStorage.getItem(CHECK_IN_LEGACY_KEY);
    if (!raw) return null;
    return sanitizeDailyCheckIn(JSON.parse(raw));
  } catch {
    return null;
  }
}

// 每次签到/补签后都把最新签到数据写一份到设备本地，作为数据库之外的备份
function writeLocalDailyCheckIn(uid: string, data: DailyCheckIn) {
  try {
    window.localStorage.setItem(getCheckInStorageKey(uid), JSON.stringify(data));
  } catch {
    // 本地存储不可用时忽略（数据库仍然是主存储）
  }
}

// 合并两份签到数据：日期取并集（只能多不能少）；
// 补签机会只在「本次确实补签了历史日期」时才按次数减少，避免被陈旧/空数据清零。
function mergeDailyCheckInData(stored: DailyCheckIn | null | undefined, incoming: DailyCheckIn | null | undefined): DailyCheckIn {
  const storedDates = Array.isArray(stored?.checkedInDates) ? stored!.checkedInDates : [];
  const incomingDates = Array.isArray(incoming?.checkedInDates) ? incoming!.checkedInDates : [];

  const checkedInDates = Array.from(new Set([...storedDates, ...incomingDates])).sort();

  const storedChances = typeof stored?.makeupChances === 'number' ? stored.makeupChances : 0;
  const incomingChances = typeof incoming?.makeupChances === 'number' ? incoming.makeupChances : 0;
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  // 只有「确实补签了历史日期」才扣机会，一次扣一次；否则取两者最大值，绝不被陈旧数据清零
  const addedPastDates = incomingDates.filter((d) => !storedDates.includes(d) && d < todayStr);
  const makeupChances = addedPastDates.length > 0
    ? Math.max(0, storedChances - addedPastDates.length)
    : Math.max(storedChances, incomingChances);

  const storedStreak = typeof stored?.streak === 'number' ? stored.streak : 0;
  const incomingStreak = typeof incoming?.streak === 'number' ? incoming.streak : 0;

  return {
    checkedInDates,
    streak: incomingStreak > 0 ? incomingStreak : storedStreak,
    makeupChances,
  };
}
const FLOATING_ICON_PADDING = 12;
const FLOATING_ICON_TOP_OFFSET = 92;
const FEATURED_CARD_TRANSITION_MS = 280;
const FEATURED_CARD_AUTOPLAY_MS = 5000;
const canAccessWarmupAdmin = (user?: User | null) =>
  !!user && !!(user.isAdmin || user.roles?.includes('administrator') || user.roles?.includes('author'));

type HomeModeId = 'battle' | 'point' | 'freestyle' | 'online' | 'kendama-map' | 'kwc2026';
type HomeModeVariant = 'small' | 'tall';
type AppLanguage = 'zh' | 'en' | 'ja';
type UserCenterMenuItem = {
  key: UserProfileTab | 'admin' | 'logout';
  label: string;
  description: string;
};
type WakeLockSentinelLike = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener?: (type: 'release', listener: () => void) => void;
};
type NavigatorWithWakeLock = Navigator & {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>;
  };
};
type LocalizedHomeModeCopy = {
  titleLines: string[];
  titleLineClassNames?: string[];
  description: string;
  badge?: string;
  badgeClassName?: string;
};
type FeaturedActivityCardConfig = {
  id: string;
  copy: Record<AppLanguage, LocalizedHomeModeCopy>;
  action?: () => void;
  disabled?: boolean;
  cardClassName: string;
  labelClassName: string;
  descriptionClassName: string;
  actionClassName: string;
  showWarmupEffects?: boolean;
  backgroundImageUrl?: string;
  backgroundOverlayClassName?: string;
};
type HomeModeConfig = {
  id: HomeModeId;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  variant: HomeModeVariant;
  disabled?: boolean;
  copy: Record<AppLanguage, LocalizedHomeModeCopy>;
  accentClassName: string;
  activeAccentClassName: string;
  iconClassName: string;
  activeIconClassName: string;
  descriptionActiveClassName: string;
};

const HOME_MODE_SLOT_CLASSES = [
  'md:col-start-1 md:row-start-1',
  'md:col-start-1 md:row-start-2',
  'md:col-start-2 md:row-start-1',
  'md:col-start-2 md:row-start-2',
  'md:col-start-3 md:row-start-1 md:row-span-2',
  'md:col-start-4 md:row-start-1 md:row-span-2',
] as const;

const HOME_MODE_MOBILE_SLOT_CLASSES = [
  'col-span-1',
  'col-span-1',
  'col-span-1',
  'col-span-1',
  'col-span-1',
  'col-span-1',
] as const;

const DEFAULT_HOME_MODE_LAYOUT: HomeModeId[] = [
  'battle',
  'freestyle',
  'point',
  'online',
  'kendama-map',
  'kwc2026',
];

const isValidHomeModeLayout = (value: unknown): value is HomeModeId[] => {
  if (!Array.isArray(value) || value.length !== DEFAULT_HOME_MODE_LAYOUT.length) return false;

  const expectedIds = [...DEFAULT_HOME_MODE_LAYOUT].sort();
  const incomingIds = [...value].sort();
  return expectedIds.every((id, index) => incomingIds[index] === id);
};

const getFloatingIconSize = () => (window.innerWidth >= 768 ? 48 : 40);

const KendamaArchiveIcon: React.FC<{ className?: string; strokeWidth?: number }> = ({
  className,
  strokeWidth = 1.4,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <circle cx="12" cy="6.6" r="4.2" />
    <path d="M9.3 4.6c.6-1 1.7-1.7 2.9-1.8" />
    <path d="M7.2 8.7H5.5c-.8 0-1.5.7-1.5 1.5v4.2c0 .8.7 1.5 1.5 1.5h1.3" />
    <path d="M16.8 8.7h1.7c.8 0 1.5.7 1.5 1.5v4.2c0 .8-.7 1.5-1.5 1.5h-1.3" />
    <path d="M7.2 8.7v7" />
    <path d="M16.8 8.7v7" />
    <path d="M7.2 15.1c1.5-1.1 3-1.7 4.8-1.7s3.3.6 4.8 1.7" />
    <path d="M10.8 13.8 10 18.4" />
    <path d="M13.2 13.8 14 18.4" />
    <path d="M9.7 18.4h4.6" />
    <path d="M9.4 18.4c-.5.5-.8 1.2-.8 2v1.2c0 .6.5 1.1 1.1 1.1h4.6c.6 0 1.1-.5 1.1-1.1v-1.2c0-.8-.3-1.5-.8-2" />
    <path d="M8.5 19.8h7" />
    <path d="M8.2 22.7h7.6" />
    <path d="M12.3 10.6 11.2 11.9" />
  </svg>
);

type ProtectedRouteProps = {
  allow: boolean;
  isSessionLoading: boolean;
  onBlocked: () => void;
  children: React.ReactNode;
  fallbackPath?: string;
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allow,
  isSessionLoading,
  onBlocked,
  children,
  fallbackPath = '/',
}) => {
  const hasBlockedRef = useRef(false);

  useEffect(() => {
    if (allow) {
      hasBlockedRef.current = false;
      return;
    }

    if (isSessionLoading || hasBlockedRef.current) return;

    hasBlockedRef.current = true;
    onBlocked();
  }, [allow, isSessionLoading, onBlocked]);

  if (isSessionLoading) return null;
  if (!allow) return <Navigate to={fallbackPath} replace />;
  return <>{children}</>;
};

// 胜利弹窗延迟进场：先让血条归零 / 最后一手落定，胜利才有那"一拍"
const WINNER_REVEAL_DELAY_MS = 550;

const App: React.FC = () => {
  const { language, setLanguage, t } = useI18n();
  // --- User & View State ---
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  // 启动界面的真实加载进度（0~1），由实际初始化步骤推进
  const [splashProgress, setSplashProgress] = useState(0);
  const splashProgressRef = useRef(0);
  // 启动动画退场过渡：加载完成后覆盖层淡出再卸载
  const [isSplashExiting, setIsSplashExiting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [homeModeLayout, setHomeModeLayout] = useState<HomeModeId[]>(DEFAULT_HOME_MODE_LAYOUT);
  const [isHomeModeLayoutEditing, setIsHomeModeLayoutEditing] = useState(false);
  const [draggingHomeModeId, setDraggingHomeModeId] = useState<HomeModeId | null>(null);
  const [dragHoverHomeModeId, setDragHoverHomeModeId] = useState<HomeModeId | null>(null);
  const [draggingHomeModeOffset, setDraggingHomeModeOffset] = useState({ x: 0, y: 0 });
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [onlineRoomActive, setOnlineRoomActive] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logoutInFlightRef = useRef(false);
  const pendingRoomInviteRef = useRef<RoomInvite | null>(null);
  const [isUserCenterMenuOpen, setIsUserCenterMenuOpen] = useState(false);
  const [isUserCenterMenuVisible, setIsUserCenterMenuVisible] = useState(false);
  const [isDeckSelectionOpen, setIsDeckSelectionOpen] = useState(false);
  const [isHomeAnnouncementModalOpen, setIsHomeAnnouncementModalOpen] = useState(false);
  const [homeAnnouncementConfig, setHomeAnnouncementConfig] = useState<HomeAnnouncementConfig>(() => sanitizeHomeAnnouncementConfig(null));
  const [isHomeAnnouncementLoaded, setIsHomeAnnouncementLoaded] = useState(false);
  const [onlineAccessConfig, setOnlineAccessConfig] = useState<OnlineAccessConfig>(() => sanitizeOnlineAccessConfig(null));
  // 联机模式是否仅限管理员：由管理员在管理界面里的开关控制
  const isCurrentUserAdmin = !!currentUser
    && !!(currentUser.isAdmin || currentUser.roles?.includes('administrator') || currentUser.roles?.includes('author'));
  const currentUserId = currentUser?.uid ?? '';
  const onlineAdminOnlyEnabled = !!onlineAccessConfig.adminOnly;
  const isOnlineAccessBlocked = onlineAdminOnlyEnabled && !!currentUser && !isCurrentUserAdmin;
  const homeAnnouncementAutoCheckDoneRef = useRef(false);
  const [isWeChatModalOpen, setIsWeChatModalOpen] = useState(false);
  const [isDailyCheckInOpen, setIsDailyCheckInOpen] = useState(false);
  const [isCardIntroOpen, setIsCardIntroOpen] = useState(false);
  const [introRevealed, setIntroRevealed] = useState(false);
  const [isMakeupCalendarModalOpen, setIsMakeupCalendarModalOpen] = useState(false);
  // 待确认的补签日期，非空时弹出补签确认提示
  const [pendingMakeupDate, setPendingMakeupDate] = useState<string | null>(null);
  const [isCardZoomOpen, setIsCardZoomOpen] = useState(false);
  const [dailyCheckIn, setDailyCheckIn] = useState<DailyCheckIn>(EMPTY_DAILY_CHECK_IN);
  const checkInLoadedUidRef = useRef<string | null>(null);
  // 镜像当前签到数据，供同步 effect 判断“后端没返回该字段”时是否需要保留已有记录
  const dailyCheckInRef = useRef<DailyCheckIn>(EMPTY_DAILY_CHECK_IN);
  // 记录最近一次“补写回服务器”的签到数据指纹，避免后端不认该字段时反复提交
  const checkInPushSignatureRef = useRef<string>('');
  const [weChatFloatPosition, setWeChatFloatPosition] = useState({ x: FLOATING_ICON_PADDING, y: FLOATING_ICON_TOP_OFFSET });
  const [isWeChatFloatReady, setIsWeChatFloatReady] = useState(false);
  const [isWeChatFloatPressed, setIsWeChatFloatPressed] = useState(false);
  const [officialWeChatLiveCodeConfig, setOfficialWeChatLiveCodeConfig] = useState<OfficialWeChatLiveCodeConfig>(() => sanitizeOfficialWeChatLiveCodeConfig(null));
  const generateRadarBlips = () => {
    const blips: { top: number; left: number; delay: number }[] = [];
    for (let i = 0; i < 3; i++) {
      const left = 8 + Math.random() * 80;
      const top = 8 + Math.random() * 80;
      const dx = left - 50;
      const dyUp = 50 - top;
      const angle = (Math.atan2(dx, dyUp) * 180 / Math.PI + 360) % 360;
      const delay = ((angle + 2) / 360 * 4) % 4;
      blips.push({ top, left, delay });
    }
    return blips;
  };
  const [radarBlipPositions, setRadarBlipPositions] = useState(generateRadarBlips);
  useEffect(() => {
    const interval = setInterval(() => {
      setRadarBlipPositions(generateRadarBlips());
    }, 4000);
    return () => clearInterval(interval);
  }, []);
  const canManageWarmup = canAccessWarmupAdmin(currentUser);
  const syncManagedUserDataPromiseRef = useRef<Promise<void> | null>(null);
  const homeModeDragStateRef = useRef<{
    pointerId: number;
    modeId: HomeModeId | null;
    startX: number;
    startY: number;
    hasMoved: boolean;
  }>({
    pointerId: -1,
    modeId: null,
    startX: 0,
    startY: 0,
    hasMoved: false,
  });
  const homeModeLongPressTimerRef = useRef<number | null>(null);
  const homeModeLongPressReadyRef = useRef(false);
  const featuredActivitySwipeRef = useRef({
    pointerId: -1,
    startX: 0,
    startY: 0,
    deltaX: 0,
    deltaY: 0,
    captured: false,
  });
  const featuredActivitySuppressClickRef = useRef(false);
  const weChatFloatButtonRef = useRef<HTMLButtonElement | null>(null);
  const weChatFloatPositionRef = useRef({ x: FLOATING_ICON_PADDING, y: FLOATING_ICON_TOP_OFFSET });
  const weChatFloatFrameRef = useRef<number | null>(null);
  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);
  const wakeLockWarnedRef = useRef(false);
  const weChatDragStateRef = useRef({
    pointerId: -1,
    startX: 0,
    startY: 0,
    originX: FLOATING_ICON_PADDING,
    originY: FLOATING_ICON_TOP_OFFSET,
    dragged: false,
  });
  const suppressWeChatClickRef = useRef(false);
  const loginPromptGuardRef = useRef<{ key: string; ts: number } | null>(null);
  const userCenterMenuCloseTimerRef = useRef<number | null>(null);
  const userCenterMenuOpenFrameRef = useRef<number | null>(null);
  // 页面外壳节点，边缘滑返回时直接改它的 transform 来做跟手与滑出动画。
  const appShellRef = useRef<HTMLDivElement | null>(null);
  
  // Helper to get view from path
  const getViewFromPath = () => {
    const path = location.pathname;
    if (path === '/game') return 'GAME';
    if (path === '/battle-mode') return 'BATTLE_MODE';
    if (path === '/editor') return 'EDITOR';
    if (path === '/tournament') return 'TOURNAMENT_MENU';
    if (path === '/tournament/game') return 'TOURNAMENT_GAME';
    if (path === '/tournament/editor') return 'TOURNAMENT_EDITOR';
    if (path === '/tournament/history') return 'TOURNAMENT_HISTORY';
    if (path === '/tournament/final-submit') return 'TOURNAMENT_FINAL_SUBMIT';
    if (path === '/freestyle') return 'FREESTYLE_LOBBY';
    if (path.startsWith('/freestyle/room')) return 'FREESTYLE_ROOM';
    if (path === '/online') return 'ONLINE_LOBBY';
    if (path === '/admin') return 'ADMIN';
    if (path === '/profile') return 'PROFILE';
    if (path === '/kendama-map') return 'KENDAMA_MAP';
    if (path === '/wireless-progress' || path.startsWith('/wireless-progress/')) return 'WIRELESS_PROGRESS';
    return 'MENU';
  };
  const view = getViewFromPath();
  const profileTabFromQuery = useMemo<UserProfileTab | undefined>(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab === 'profile' || tab === 'info' || tab === 'edit' || tab === 'password' || tab === 'feedback' || tab === 'settings') {
      if (tab === 'info' || tab === 'edit') return 'profile';
      return tab as UserProfileTab;
    }
    return undefined;
  }, [location.search]);

  const messagesWithFromQuery = useMemo<string | undefined>(() => {
    const withUid = new URLSearchParams(location.search).get('with');
    return withUid && withUid.trim() ? withUid.trim() : undefined;
  }, [location.search]);
  
  const setView = (v: string) => {
    switch(v) {
      case 'GAME': navigate('/game'); break;
      case 'BATTLE_MODE': navigate('/battle-mode'); break;
      case 'EDITOR': navigate('/editor'); break;
      case 'TOURNAMENT_MENU': navigate('/tournament'); break;
      case 'TOURNAMENT_GAME': navigate('/tournament/game'); break;
      case 'TOURNAMENT_EDITOR': navigate('/tournament/editor'); break;
      case 'TOURNAMENT_HISTORY': navigate('/tournament/history'); break;
      case 'TOURNAMENT_FINAL_SUBMIT': navigate('/tournament/final-submit'); break;
      case 'FREESTYLE_LOBBY': navigate('/freestyle'); break;
      case 'ONLINE_LOBBY': navigate('/online'); break;
      case 'ADMIN': navigate('/admin'); break;
      case 'PROFILE': navigate('/profile'); break;
      case 'KENDAMA_MAP': navigate('/kendama-map'); break;
      case 'WIRELESS_PROGRESS': navigate('/wireless-progress'); break;
      default: navigate('/');
    }
  };

  // 左侧边缘右滑返回上一级（iOS 风格手势）。仅在非首页生效，避免误退出。
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.pathname === '/') return;

    const shell = appShellRef.current;
    if (!shell) return;

    const EDGE_ZONE_PX = 24; // 触发区：距屏幕左边缘 24px 内
    const COMMIT_DISTANCE_PX = 12; // 确认是水平右滑后才开始跟手
    const TRIGGER_DISTANCE_PX = 60; // 松手时滑过该距离才真正返回
    const MAX_VERTICAL_DRIFT_PX = 80; // 垂直漂移过大视为页面滚动，取消手势
    const SLIDE_DURATION_MS = 240; // 滑出屏幕的时长
    const SLIDE_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)'; // 先快后缓，贴近系统手感

    let tracking = false;
    let committed = false;
    let startX = 0;
    let startY = 0;
    let offset = 0;
    let slideTimer: number | null = null;

    // 跟手阶段每帧都要改位移，直接写 DOM，避免走 React 状态更新造成掉帧。
    const applyTransform = (value: string, animated: boolean) => {
      shell.style.transition = animated ? `transform ${SLIDE_DURATION_MS}ms ${SLIDE_EASING}` : 'none';
      // 静止时必须清空 transform：外壳一旦有 transform 就会成为 fixed 子元素的包含块，弹层会因此偏移。
      shell.style.transform = value;
    };

    const clearSlideTimer = () => {
      if (slideTimer !== null) {
        window.clearTimeout(slideTimer);
        slideTimer = null;
      }
    };

    // 未达阈值或手势被打断：带过渡滑回原位。
    const settleBack = () => {
      tracking = false;
      committed = false;
      offset = 0;
      shell.style.willChange = '';
      applyTransform('', true);
    };

    const goBack = () => {
      const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (historyIndex > 0) {
        navigate(-1);
      } else {
        navigate('/');
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      // 滑出动画进行中不接受新手势，免得把已经确定要发生的返回打断成半路弹回。
      if (slideTimer !== null) return;
      if (event.touches.length !== 1) {
        settleBack();
        return;
      }
      const touch = event.touches[0];
      if (touch.clientX > EDGE_ZONE_PX) {
        settleBack();
        return;
      }
      tracking = true;
      committed = false;
      startX = touch.clientX;
      startY = touch.clientY;
      offset = 0;
      shell.style.willChange = 'transform';
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!tracking) return;
      const touch = event.touches[0];
      if (!touch) return;

      const dx = touch.clientX - startX;
      const dy = Math.abs(touch.clientY - startY);

      // 纵向意图明显时让页面正常滚动，整个手势作废。
      if (dy > MAX_VERTICAL_DRIFT_PX && dy > Math.abs(dx)) {
        settleBack();
        return;
      }
      if (!committed) {
        // 方向锁：确认是向右的水平滑动之后，页面才开始跟手平移。
        if (dx < COMMIT_DISTANCE_PX || dx < dy * 1.5) return;
        committed = true;
      }
      offset = Math.max(0, dx);
      applyTransform(`translateX(${offset}px)`, false);
    };

    const handleTouchEnd = () => {
      if (!tracking) return;
      const shouldGoBack = committed && offset >= TRIGGER_DISTANCE_PX;
      tracking = false;
      committed = false;

      if (!shouldGoBack) {
        offset = 0;
        shell.style.willChange = '';
        applyTransform('', true);
        return;
      }

      // 先把页面整体滑出屏幕，动画落点后再切路由，避免新页面被一起平移出画面。
      shell.style.willChange = '';
      applyTransform('translateX(100%)', true);
      slideTimer = window.setTimeout(() => {
        slideTimer = null;
        goBack();
        applyTransform('', false);
      }, SLIDE_DURATION_MS);
    };

    const handleTouchCancel = () => settleBack();

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
      clearSlideTimer();
      // 切页或卸载时擦掉内联样式，避免位移残留到下一个页面。
      shell.style.transition = '';
      shell.style.transform = '';
      shell.style.willChange = '';
    };
  }, [location.pathname, navigate]);

  const shouldEnableWakeLock = useCallback(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  }, []);

  const releaseWakeLock = useCallback(async () => {
    const activeWakeLock = wakeLockRef.current;
    if (!activeWakeLock || activeWakeLock.released) {
      wakeLockRef.current = null;
      return;
    }

    try {
      await activeWakeLock.release();
    } catch {
      // Ignore release errors and allow the browser to manage the lock state.
    } finally {
      if (wakeLockRef.current === activeWakeLock) {
        wakeLockRef.current = null;
      }
    }
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || typeof navigator === 'undefined') return;
    if (!shouldEnableWakeLock()) return;
    if (document.visibilityState !== 'visible') return;

    const navigatorWithWakeLock = navigator as NavigatorWithWakeLock;
    if (!navigatorWithWakeLock.wakeLock?.request) return;

    if (wakeLockRef.current && !wakeLockRef.current.released) return;

    try {
      const wakeLock = await navigatorWithWakeLock.wakeLock.request('screen');
      wakeLockRef.current = wakeLock;
      wakeLockWarnedRef.current = false;
      wakeLock.addEventListener?.('release', () => {
        if (wakeLockRef.current === wakeLock) {
          wakeLockRef.current = null;
        }
      });
    } catch (error) {
      if (!wakeLockWarnedRef.current) {
        console.warn('Wake Lock unavailable:', error);
        wakeLockWarnedRef.current = true;
      }
    }
  }, [shouldEnableWakeLock]);

  const clampWeChatFloatPosition = useCallback((x: number, y: number) => {
    const size = getFloatingIconSize();
    const maxX = Math.max(FLOATING_ICON_PADDING, window.innerWidth - size - FLOATING_ICON_PADDING);
    const maxY = Math.max(FLOATING_ICON_TOP_OFFSET, window.innerHeight - size - FLOATING_ICON_PADDING);

    return {
      x: Math.min(Math.max(FLOATING_ICON_PADDING, x), maxX),
      y: Math.min(Math.max(FLOATING_ICON_TOP_OFFSET, y), maxY),
    };
  }, []);

  const snapWeChatFloatPosition = useCallback((x: number, y: number) => {
    const clamped = clampWeChatFloatPosition(x, y);
    const size = getFloatingIconSize();
    const maxX = Math.max(FLOATING_ICON_PADDING, window.innerWidth - size - FLOATING_ICON_PADDING);
    const shouldSnapLeft = clamped.x + size / 2 < window.innerWidth / 2;

    return {
      x: shouldSnapLeft ? FLOATING_ICON_PADDING : maxX,
      y: clamped.y,
    };
  }, [clampWeChatFloatPosition]);

  const applyWeChatFloatPosition = useCallback((position: { x: number; y: number }) => {
    weChatFloatPositionRef.current = position;
    if (weChatFloatButtonRef.current) {
      weChatFloatButtonRef.current.style.left = `${position.x}px`;
      weChatFloatButtonRef.current.style.top = `${position.y}px`;
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const savedPosition = window.localStorage.getItem(OFFICIAL_WECHAT_FLOAT_POSITION_KEY);
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition) as { x?: number; y?: number };
        const initialPosition = snapWeChatFloatPosition(parsed.x ?? window.innerWidth - getFloatingIconSize() - FLOATING_ICON_PADDING, parsed.y ?? FLOATING_ICON_TOP_OFFSET);
        weChatFloatPositionRef.current = initialPosition;
        setWeChatFloatPosition(initialPosition);
      } catch {
        const fallbackPosition = snapWeChatFloatPosition(window.innerWidth - getFloatingIconSize() - FLOATING_ICON_PADDING, 140);
        weChatFloatPositionRef.current = fallbackPosition;
        setWeChatFloatPosition(fallbackPosition);
      }
    } else {
      const defaultPosition = snapWeChatFloatPosition(window.innerWidth - getFloatingIconSize() - FLOATING_ICON_PADDING, 140);
      weChatFloatPositionRef.current = defaultPosition;
      setWeChatFloatPosition(defaultPosition);
    }
    setIsWeChatFloatReady(true);
  }, [snapWeChatFloatPosition]);

  useEffect(() => {
    if (!isWeChatFloatReady || typeof window === 'undefined') return;

    window.localStorage.setItem(OFFICIAL_WECHAT_FLOAT_POSITION_KEY, JSON.stringify(weChatFloatPosition));
  }, [isWeChatFloatReady, weChatFloatPosition]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let mounted = true;

    const loadHomeAnnouncement = async () => {
      const config = await fetchHomeAnnouncementConfig();
      if (!mounted) return;
      setHomeAnnouncementConfig(sanitizeHomeAnnouncementConfig(config));
      setIsHomeAnnouncementLoaded(true);
    };

    void loadHomeAnnouncement();

    const handleHomeAnnouncementFocus = () => {
      void loadHomeAnnouncement();
    };

    const handleHomeAnnouncementStorage = (event: StorageEvent) => {
      if (event.key === HOME_ANNOUNCEMENT_VERSION_KEY) {
        void loadHomeAnnouncement();
      }
    };

    const handleHomeAnnouncementUpdated = () => {
      void loadHomeAnnouncement();
    };

    window.addEventListener('focus', handleHomeAnnouncementFocus);
    window.addEventListener('storage', handleHomeAnnouncementStorage);
    window.addEventListener(HOME_ANNOUNCEMENT_UPDATED_EVENT, handleHomeAnnouncementUpdated);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleHomeAnnouncementFocus);
      window.removeEventListener('storage', handleHomeAnnouncementStorage);
      window.removeEventListener(HOME_ANNOUNCEMENT_UPDATED_EVENT, handleHomeAnnouncementUpdated);
    };
  }, [location.pathname]);

  // 联机模式访问开关（管理员在管理界面里切换，这里读回来做入口拦截）
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let mounted = true;

    const loadOnlineAccess = async () => {
      const config = await fetchOnlineAccessConfig();
      if (!mounted) return;
      setOnlineAccessConfig(sanitizeOnlineAccessConfig(config));
    };

    void loadOnlineAccess();

    const handleFocus = () => {
      void loadOnlineAccess();
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'kendama-local-online-access-config') {
        void loadOnlineAccess();
      }
    };
    const handleUpdated = () => {
      void loadOnlineAccess();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorage);
    window.addEventListener(ONLINE_ACCESS_UPDATED_EVENT, handleUpdated);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(ONLINE_ACCESS_UPDATED_EVENT, handleUpdated);
    };
    // 登录/退出后重新读取开关，避免用旧值判断入口权限。
  }, [location.pathname, currentUserId]);

  useEffect(() => {
    if (typeof window === 'undefined' || location.pathname !== '/' || !isHomeAnnouncementLoaded) return;
    if (homeAnnouncementAutoCheckDoneRef.current) return;

    homeAnnouncementAutoCheckDoneRef.current = true;

    const trimmedTitle = homeAnnouncementConfig.title.trim();
    const trimmedContent = homeAnnouncementConfig.content.trim();
    if (!trimmedTitle && !trimmedContent) return;
    if (homeAnnouncementConfig.pushedAt <= 0) return;

    const currentVersion = String(homeAnnouncementConfig.pushedAt);
    const lastSeenVersion = window.localStorage.getItem(HOME_ANNOUNCEMENT_LAST_SEEN_KEY);
    if (lastSeenVersion === currentVersion) return;

    window.localStorage.setItem(HOME_ANNOUNCEMENT_LAST_SEEN_KEY, currentVersion);
    setIsHomeAnnouncementModalOpen(true);
  }, [homeAnnouncementConfig.content, homeAnnouncementConfig.pushedAt, homeAnnouncementConfig.title, isHomeAnnouncementLoaded, location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let mounted = true;

    const loadOfficialWeChatLiveCode = async () => {
      const config = await fetchOfficialWeChatLiveCodeConfig();
      if (!mounted) return;
      setOfficialWeChatLiveCodeConfig(sanitizeOfficialWeChatLiveCodeConfig(config));
    };

    void loadOfficialWeChatLiveCode();

    const handleFocus = () => {
      void loadOfficialWeChatLiveCode();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === OFFICIAL_WECHAT_LIVE_CODE_VERSION_KEY) {
        void loadOfficialWeChatLiveCode();
      }
    };

    const handleLiveCodeUpdated = () => {
      void loadOfficialWeChatLiveCode();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorage);
    window.addEventListener(OFFICIAL_WECHAT_LIVE_CODE_UPDATED_EVENT, handleLiveCodeUpdated);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(OFFICIAL_WECHAT_LIVE_CODE_UPDATED_EVENT, handleLiveCodeUpdated);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    void requestWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void requestWakeLock();
      } else {
        void releaseWakeLock();
      }
    };

    const handleWindowFocus = () => {
      void requestWakeLock();
    };

    const handleWindowBlur = () => {
      void releaseWakeLock();
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      void releaseWakeLock();
    };
  }, [releaseWakeLock, requestWakeLock]);

  const activeOfficialWeChatQrInfo = useMemo(
    () => getActiveOfficialWeChatQrCode(officialWeChatLiveCodeConfig),
    [officialWeChatLiveCodeConfig]
  );

  const activeOfficialWeChatQrImage = activeOfficialWeChatQrInfo.entry?.imageUrl || officialWeChatQrModal;
  const activeOfficialWeChatQrSwitchTimeLabel = activeOfficialWeChatQrInfo.totalEntries > 0
    ? new Date(activeOfficialWeChatQrInfo.endsAt).toLocaleString()
    : '';
  const hasHomeAnnouncement = homeAnnouncementConfig.title.trim().length > 0 || homeAnnouncementConfig.content.trim().length > 0;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const savedLayout = window.localStorage.getItem(HOME_MODE_LAYOUT_STORAGE_KEY);
    if (!savedLayout) return;

    try {
      const parsed = JSON.parse(savedLayout) as unknown;
      if (isValidHomeModeLayout(parsed)) {
        setHomeModeLayout(parsed);
      }
    } catch {
      window.localStorage.removeItem(HOME_MODE_LAYOUT_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(HOME_MODE_LAYOUT_STORAGE_KEY, JSON.stringify(homeModeLayout));
  }, [homeModeLayout]);

  useEffect(() => {
    if (!isHomeModeLayoutEditing) {
      if (homeModeLongPressTimerRef.current !== null) {
        window.clearTimeout(homeModeLongPressTimerRef.current);
        homeModeLongPressTimerRef.current = null;
      }
      homeModeLongPressReadyRef.current = false;
      homeModeDragStateRef.current = {
        pointerId: -1,
        modeId: null,
        startX: 0,
        startY: 0,
        hasMoved: false,
      };
      setDraggingHomeModeId(null);
      setDragHoverHomeModeId(null);
      setDraggingHomeModeOffset({ x: 0, y: 0 });
    }
  }, [isHomeModeLayoutEditing]);

  useEffect(() => {
    applyWeChatFloatPosition(weChatFloatPosition);
  }, [applyWeChatFloatPosition, weChatFloatPosition]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      const nextPosition = snapWeChatFloatPosition(weChatFloatPositionRef.current.x, weChatFloatPositionRef.current.y);
      applyWeChatFloatPosition(nextPosition);
      setWeChatFloatPosition(nextPosition);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [applyWeChatFloatPosition, snapWeChatFloatPosition]);

  useEffect(() => {
    return () => {
      if (weChatFloatFrameRef.current !== null) {
        window.cancelAnimationFrame(weChatFloatFrameRef.current);
      }
    };
  }, []);

  const handleWeChatFloatPointerDown = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    setIsWeChatFloatPressed(true);
    weChatDragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: weChatFloatPosition.x,
      originY: weChatFloatPosition.y,
      dragged: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [weChatFloatPosition]);

  const handleWeChatFloatPointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (weChatDragStateRef.current.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - weChatDragStateRef.current.startX;
    const deltaY = event.clientY - weChatDragStateRef.current.startY;
    if (!weChatDragStateRef.current.dragged && Math.abs(deltaX) + Math.abs(deltaY) > 6) {
      weChatDragStateRef.current.dragged = true;
    }

    if (!weChatDragStateRef.current.dragged) return;

    const nextPosition = clampWeChatFloatPosition(
      weChatDragStateRef.current.originX + deltaX,
      weChatDragStateRef.current.originY + deltaY,
    );
    weChatFloatPositionRef.current = nextPosition;

    if (weChatFloatFrameRef.current !== null) {
      window.cancelAnimationFrame(weChatFloatFrameRef.current);
    }

    weChatFloatFrameRef.current = window.requestAnimationFrame(() => {
      applyWeChatFloatPosition(nextPosition);
      weChatFloatFrameRef.current = null;
    });
  }, [applyWeChatFloatPosition, clampWeChatFloatPosition]);

  const handleWeChatFloatPointerUp = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (weChatDragStateRef.current.pointerId !== event.pointerId) return;
    setIsWeChatFloatPressed(false);

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (weChatDragStateRef.current.dragged) {
      suppressWeChatClickRef.current = true;
      const snappedPosition = snapWeChatFloatPosition(weChatFloatPositionRef.current.x, weChatFloatPositionRef.current.y);
      applyWeChatFloatPosition(snappedPosition);
      setWeChatFloatPosition(snappedPosition);
      window.setTimeout(() => {
        suppressWeChatClickRef.current = false;
      }, 0);
    }

    weChatDragStateRef.current.pointerId = -1;
  }, [snapWeChatFloatPosition]);

  const handleWeChatFloatClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (suppressWeChatClickRef.current) {
      event.preventDefault();
      return;
    }
    setIsWeChatModalOpen(true);
  }, []);

  const handleWeChatFloatPointerLeave = useCallback(() => {
    if (weChatDragStateRef.current.pointerId === -1) {
      setIsWeChatFloatPressed(false);
    }
  }, []);
  
  // --- Global Volume State ---
  const [volume, setVolume] = useState<number>(0.8);

  // 真实加载步骤推进启动进度；进度只增不减，避免回调顺序不同导致进度条回退
  const reportSplashProgress = useCallback((value: number) => {
    if (value <= splashProgressRef.current) return;
    splashProgressRef.current = value;
    setSplashProgress(value);
  }, []);

  // --- Auth Session Check (REST API) ---
  useEffect(() => {
    const isLocal = isLocalBackend();

    const applyUser = async (data: any) => {
      reportSplashProgress(0.5);
      const managedMasterLists = await fetchManagedTournamentMasterLists();
      reportSplashProgress(0.62);
      const managedDrawPresetDecks = await fetchManagedDrawPresetDecks();
      reportSplashProgress(0.74);
      setCurrentUser(migrateUserData(data.user, managedMasterLists, managedDrawPresetDecks));
      reportSplashProgress(0.86);
    };

    const tryAutoLogin = async () => {
      let cred: { email?: string; password?: string } | null = null;
      try {
        const raw = window.localStorage.getItem(AUTH_AUTO_LOGIN_KEY);
        if (raw) cred = JSON.parse(raw);
      } catch (e) {
        cred = null;
      }
      if (!cred || !cred.email || !cred.password) return false;

      try {
        reportSplashProgress(0.46);
        const response = await fetch(isLocal ? '/api/login' : phpApiUrl('login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: cred.email, password: cred.password, username: cred.email }),
        });
        if (!response.ok) return false;
        reportSplashProgress(0.6);
        const data = await response.json();
        if (!data.success || !data.user) return false;
        if (data.token) setAuthToken(data.token);
        await applyUser(data);
        return true;
      } catch (error) {
        console.error("Auto login failed", error);
        return false;
      }
    };

    const checkSession = async () => {
      reportSplashProgress(0.08);
      try {
        // 远程接口走 token 头（原生 App 跨域不能依赖 Cookie）；本地开发走 /api + Cookie
        const meOptions = isLocal ? {} : { headers: phpAuthHeaders() };
        const response = await fetch(isLocal ? '/api/me' : phpApiUrl('me'), meOptions);
        reportSplashProgress(0.38);
        if (response.ok) {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await response.json();
            if (data.token) setAuthToken(data.token);
            await applyUser(data);
            return;
          }
        }
      } catch (error) {
        console.error("Session check failed", error);
      }

      // 未登录：尝试使用本地保存的凭据自动登录
      await tryAutoLogin();
      reportSplashProgress(0.9);
    };

    // 会话校验完成且启动动画至少完整播放一次后，进度补满再淡出到首页
    void Promise.all([
      checkSession(),
      new Promise((resolve) => window.setTimeout(resolve, MATRIX_SPLASH_MIN_DURATION_MS)),
    ]).finally(() => {
      reportSplashProgress(1);
      // 停留一小段让进度跑满被看见，再挂载首页并让覆盖层淡出
      window.setTimeout(() => {
        setIsSessionLoading(false);
        setIsSplashExiting(true);
        // 多留 48ms：CSS 动画起播通常晚于这次 setTimeout 起点一帧左右，
        // 卡在动画跑完之前摘掉启动层会看到一下闪。
        window.setTimeout(() => setIsSplashExiting(false), MATRIX_SPLASH_EXIT_DURATION_MS + 48);
      }, MATRIX_SPLASH_PROGRESS_SETTLE_MS);
    });
  }, [reportSplashProgress]);

  // --- Game State (Battle Mode) ---
  const [gameState, setGameState] = useState<GameState>({
    phase: 'START',
    deck: [],
    activeCards: [],
    discardPile: [],
    currentPlayerIndex: 0,
    players: [
      { id: 0, name: 'Player 1', health: MAX_ATTEMPTS, attemptsLeft: MAX_ATTEMPTS, color: 'red' },
      { id: 1, name: 'Player 2', health: MAX_ATTEMPTS, attemptsLeft: MAX_ATTEMPTS, color: 'blue' },
    ],
    winnerId: null,
    roundMessage: '',
    roundStarterIndex: 0,
    subRoundIndex: 1,
    firstHandResult: null,
    customHealth: MAX_ATTEMPTS,
  });

  // --- Undo history for battle judgment (防误判) ---
  const [gameHistory, setGameHistory] = useState<GameState[]>([]);

  // --- 胜利弹窗：延迟进场，让最后一手先落定（"赢的那一拍"） ---
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  // 退场动画期间 gameState 可能已经被 returnToMenu 重置，所以这里存一份赢家快照
  const winnerSnapshotRef = useRef<{
    winner: GameState['players'][number];
    opponent?: GameState['players'][number];
  } | null>(null);

  const snapshotGame = (gs: GameState): GameState => ({
    ...gs,
    deck: gs.deck.map(c => ({ ...c })),
    activeCards: gs.activeCards.map(c => ({ ...c })),
    discardPile: gs.discardPile.map(c => ({ ...c })),
    players: gs.players.map(p => ({ ...p })),
  });

  // --- Reset Game State on Leave ---
  useEffect(() => {
    if (location.pathname !== '/game') {
      setGameHistory([]);
      setGameState(prev => {
        if (prev.phase !== 'START') {
          return {
            ...prev,
            phase: 'START',
            winnerId: null,
            roundMessage: '',
            players: prev.players.map(p => ({ ...p, health: prev.customHealth || WINNING_SCORE, attemptsLeft: MAX_ATTEMPTS })),
          };
        }
        return prev;
      });
    }
  }, [location.pathname]);

  // --- Helpers ---
  const shuffleDeck = (cards: CardData[]) => {
    return [...cards].sort(() => Math.random() - 0.5);
  };

  // --- Auth Handlers ---
  const handleLoginSuccess = (user: User) => {
      setCurrentUser(user);
      setIsAuthOpen(false);
  };

  const showToast = useCallback((message: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 3000);
  }, []);

  const openHomeAnnouncementModal = useCallback(() => {
    setIsHomeAnnouncementModalOpen(true);
  }, []);

  const closeHomeAnnouncementModal = useCallback(() => {
    setIsHomeAnnouncementModalOpen(false);
  }, []);

  const getLocalDateString = (date: Date = new Date()) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // 依据已签到日期列表计算当前连续签到天数（今天未签则从昨天起算）
  const computeStreak = (dates: string[]): number => {
    const set = new Set(dates);
    const cursor = new Date();
    if (!set.has(getLocalDateString(cursor))) {
      cursor.setDate(cursor.getDate() - 1);
    }
    let streak = 0;
    while (set.has(getLocalDateString(cursor))) {
      streak += 1;
      cursor.setDate(cursor.getDate() - 1);
    }
    return streak;
  };

  const hasCheckedInToday = dailyCheckIn.checkedInDates.includes(getLocalDateString());
  // 用来决定签到卡片右上角徽标：从未签过 → NEW；今天签过 → 已签到；其余不显示
  const hasEverCheckedIn = dailyCheckIn.checkedInDates.length > 0;

  // 登录用户切换时加载对应账号的签到数据。
  // 数据来源有两条：数据库（服务器）+ 设备本地备份，两者取并集后再回写，
  // 这样「更新后签到被清空」这类问题不会再出现——任何一边少了都能互相补回来。
  useEffect(() => {
    if (isSessionLoading) return;
    const uid = currentUser?.uid;
    if (!uid) {
      checkInLoadedUidRef.current = null;
      setDailyCheckIn(EMPTY_DAILY_CHECK_IN);
      return;
    }

    const dbData = sanitizeDailyCheckIn(currentUser.dailyCheckIn);
    const hasDbData = !!dbData;
    const localBackup = readLocalDailyCheckIn(uid);

    let next: DailyCheckIn = dbData ?? EMPTY_DAILY_CHECK_IN;
    if (localBackup && localBackup.checkedInDates.length > 0) {
      next = mergeDailyCheckInData(next, localBackup);
    }

    // 部分接口（如登录）返回的用户对象不带 dailyCheckIn 字段，这不代表“没有签到数据”。
    // 此时保留本次已加载的记录，避免把签到数据清空后再覆盖服务器。
    if (!hasDbData && checkInLoadedUidRef.current === uid && dailyCheckInRef.current.checkedInDates.length > 0) {
      next = mergeDailyCheckInData(next, dailyCheckInRef.current);
    }

    const hadDataBefore = checkInLoadedUidRef.current === uid;
    const isFirstLoad = !hadDataBefore;
    checkInLoadedUidRef.current = uid;
    dailyCheckInRef.current = next;
    setDailyCheckIn(next);

    // 每个账号首次加载签到数据后，若当天未签到就弹出开牌入场动画（已签到则不再弹出）
    if (isFirstLoad && !next.checkedInDates.includes(getLocalDateString())) {
      setIsCardIntroOpen(true);
    }

    if (next.checkedInDates.length === 0) return;

    // 设备本地始终留一份备份：即使服务器记录丢了，下次也能自动恢复
    writeLocalDailyCheckIn(uid, next);

    const dbDates = dbData?.checkedInDates ?? [];
    const missingOnServer = !hasDbData || next.checkedInDates.some((d) => !dbDates.includes(d));
    if (!missingOnServer) {
      // 服务器已经有了完整记录，旧版全局 key 可以清掉了（按用户 key 保留作备份）
      try {
        window.localStorage.removeItem(CHECK_IN_LEGACY_KEY);
      } catch (error) {
        // 忽略清理失败
      }
      return;
    }

    // 服务器缺数据（升级后用旧版本没上传过、或后端曾经没存成功）：这里自动补写回去，
    // 不需要等用户再签一次；同一个数据指纹只提交一次，避免后端不认该字段时反复请求。
    const signature = JSON.stringify(next);
    if (checkInPushSignatureRef.current === signature) return;
    checkInPushSignatureRef.current = signature;

    const updatedUser: User = { ...currentUser, dailyCheckIn: next };
    setCurrentUser(updatedUser);
    void storage.save(updatedUser).then((ok) => {
      if (!ok) return;
      try {
        window.localStorage.removeItem(CHECK_IN_LEGACY_KEY);
      } catch (error) {
        // 忽略清理失败
      }
    });
  }, [isSessionLoading, currentUser]);

  // 启动时：未登录则打开登录窗口
  useEffect(() => {
    if (isSessionLoading) return;
    if (!currentUser) {
      setIsAuthOpen(true);
    }
  }, [isSessionLoading, currentUser]);

  const openDailyCheckInModal = useCallback(() => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    setIsDailyCheckInOpen(true);
  }, [currentUser]);

  const closeDailyCheckInModal = useCallback(() => {
    setIsDailyCheckInOpen(false);
    setIsMakeupCalendarModalOpen(false);
  }, []);

  // 开牌即签到：翻面动画完成后关闭入场弹窗，并跳转到签到界面
  const proceedFromCardIntro = useCallback(() => {
    setIsCardIntroOpen(false);
    setIsDailyCheckInOpen(true);
  }, []);

  // 点击「开牌」按钮后签到并翻转卡牌展示正面，翻面完成后关闭入场弹窗
  useEffect(() => {
    if (!introRevealed || !isCardIntroOpen) return;
    const timer = window.setTimeout(proceedFromCardIntro, 1500);
    return () => window.clearTimeout(timer);
  }, [introRevealed, isCardIntroOpen, proceedFromCardIntro]);

  const promptLogin = useCallback((message: string) => {
    const promptKey = `${location.pathname}::${message}`;
    const now = Date.now();
    const lastPrompt = loginPromptGuardRef.current;
    if (lastPrompt && lastPrompt.key === promptKey && now - lastPrompt.ts < 1200) {
      setIsAuthOpen(true);
      return;
    }

    loginPromptGuardRef.current = { key: promptKey, ts: now };
    showToast(message);
    setIsAuthOpen(true);
  }, [location.pathname, showToast]);

  const joinRoomViaInvite = useCallback((invite: RoomInvite) => {
    if (currentUser && isOnlineAccessBlocked) {
      showToast('Online Mode is restricted to admins. / 联机模式仅限管理员进入。');
      return;
    }
    // 冷启动打开邀请链接时会话还没加载完，此时 currentUser 仍是 null：
    // 先记住邀请，等会话加载完成后再自动进房，不要弹多余的登录框。
    if (isSessionLoading || !currentUser) {
      pendingRoomInviteRef.current = invite;
      if (isSessionLoading) return;
      promptLogin('Please Login to join the battle room. / 请登录后加入对战房间。');
      return;
    }
    const params = new URLSearchParams();
    params.set('roomId', invite.roomId);
    if (invite.password) params.set('password', invite.password);
    navigate(`/online?${params.toString()}`);
  }, [currentUser, isOnlineAccessBlocked, isSessionLoading, navigate, promptLogin, showToast]);

  const handleAppUrlOpen = useCallback((url: string) => {
    const invite = parseRoomInvite(url);
    if (!invite) return;
    joinRoomViaInvite(invite);
  }, [joinRoomViaInvite]);

  const handleAppUrlOpenRef = useRef(handleAppUrlOpen);
  useEffect(() => {
    handleAppUrlOpenRef.current = handleAppUrlOpen;
  }, [handleAppUrlOpen]);

  // 监听深链（kendamabox://room?roomId=...&password=...），打开 App 后自动进入对应房间
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;

    void CapacitorApp.getLaunchUrl()
      .then((result) => {
        if (!cancelled && result?.url) handleAppUrlOpenRef.current(result.url);
      })
      .catch(() => {});

    const listenerPromise = CapacitorApp.addListener('appUrlOpen', (data) => {
      handleAppUrlOpenRef.current(data.url);
    });

    return () => {
      cancelled = true;
      listenerPromise.then((handle) => handle.remove()).catch(() => {});
    };
  }, []);

  // 安卓物理返回键：非首页时回到首页，在首页时再按一次才退出 App
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let cancelled = false;
    let handle: { remove: () => void } | null = null;

    void CapacitorApp
      .addListener('backButton', () => {
        const hashPath = window.location.hash.replace(/^#/, '');
        const currentPath = hashPath || location.pathname || '/';
        if (currentPath !== '/' && currentPath !== '') {
          navigate('/');
          return;
        }
        void CapacitorApp.exitApp();
      })
      .then((listenerHandle) => {
        if (cancelled) {
          listenerHandle.remove();
          return;
        }
        handle = listenerHandle;
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [location.pathname, navigate]);

  // 冷启动时未登录，登录完成后继续处理待加入的邀请
  useEffect(() => {
    if (isSessionLoading) return;
    if (!currentUser) return;
    const pending = pendingRoomInviteRef.current;
    if (!pending) return;
    pendingRoomInviteRef.current = null;
    joinRoomViaInvite(pending);
  }, [isSessionLoading, currentUser, joinRoomViaInvite]);

  const handleProtectedNavigation = useCallback((path: string, message: string) => {
    if (currentUser) {
      navigate(path);
      return;
    }

    promptLogin(message);
  }, [currentUser, navigate, promptLogin]);

  const handleOnlineNavigation = useCallback(() => {
    if (!currentUser) {
      promptLogin("Please Login to access Online Mode. / 请登录以访问线上模式。");
      return;
    }

    if (onlineAdminOnlyEnabled && !isCurrentUserAdmin) {
      showToast('Online Mode is restricted to admins. / 联机模式仅限管理员进入。');
      return;
    }

    navigate('/online');
  }, [currentUser, isCurrentUserAdmin, navigate, onlineAdminOnlyEnabled, promptLogin, showToast]);

  const handleKendamaMapNavigation = useCallback(() => {
    navigate('/kendama-map');
  }, [navigate]);

  const cycleLanguage = useCallback(() => {
    const order: AppLanguage[] = ['zh', 'en', 'ja'];
    const currentIndex = order.indexOf(language as AppLanguage);
    const nextLanguage = order[(currentIndex + 1 + order.length) % order.length];
    setLanguage(nextLanguage);
  }, [language, setLanguage]);

  const handleProfileNavigation = useCallback(() => {
    if (currentUser) {
      navigate('/profile');
      return;
    }
    promptLogin('Please Login to access User Center. / 请登录后访问个人中心。');
  }, [currentUser, navigate, promptLogin]);

  const handleEncyclopediaNavigation = useCallback(() => {
    navigate('/wiki');
  }, [navigate]);

  const handleMessagesNavigation = useCallback(() => {
    if (currentUser) {
      navigate('/messages');
      return;
    }
    promptLogin('Please Login to access Messages. / 请登录后查看消息。');
  }, [currentUser, navigate, promptLogin]);

  const redirectToLogin = useCallback((message: string) => {
    promptLogin(message);
  }, [promptLogin]);

  const redirectToAccessDenied = useCallback((message: string) => {
    showToast(message);
  }, [showToast]);

  const clearHomeModeLongPress = useCallback(() => {
    if (homeModeLongPressTimerRef.current !== null) {
      window.clearTimeout(homeModeLongPressTimerRef.current);
      homeModeLongPressTimerRef.current = null;
    }
    homeModeLongPressReadyRef.current = false;
  }, []);

  const beginHomeModeDragging = useCallback((modeId: HomeModeId, pointerId: number, startX: number, startY: number) => {
    homeModeLongPressReadyRef.current = true;
    setIsHomeModeLayoutEditing(true);
    homeModeDragStateRef.current = {
      pointerId,
      modeId,
      startX,
      startY,
      hasMoved: false,
    };
    setDraggingHomeModeId(modeId);
    setDragHoverHomeModeId(modeId);
    setDraggingHomeModeOffset({ x: 0, y: 0 });
  }, []);

  const handleResetHomeModeLayout = useCallback(() => {
    setHomeModeLayout(DEFAULT_HOME_MODE_LAYOUT);
    clearHomeModeLongPress();
    homeModeDragStateRef.current = {
      pointerId: -1,
      modeId: null,
      startX: 0,
      startY: 0,
      hasMoved: false,
    };
    setDraggingHomeModeId(null);
    setDragHoverHomeModeId(null);
    setDraggingHomeModeOffset({ x: 0, y: 0 });
  }, [clearHomeModeLongPress]);

  const getHomeModeIdFromElement = useCallback((element: Element | null): HomeModeId | null => {
    const cardElement = element?.closest?.('[data-home-mode-id]');
    const rawModeId = cardElement?.getAttribute('data-home-mode-id');
    if (rawModeId && DEFAULT_HOME_MODE_LAYOUT.includes(rawModeId as HomeModeId)) {
      return rawModeId as HomeModeId;
    }
    return null;
  }, []);

  const swapHomeModePositions = useCallback((sourceId: HomeModeId, targetId: HomeModeId) => {
    if (sourceId === targetId) return;

    setHomeModeLayout(previous => {
      const next = [...previous];
      const sourceIndex = next.indexOf(sourceId);
      const targetIndex = next.indexOf(targetId);
      if (sourceIndex === -1 || targetIndex === -1) return previous;
      [next[sourceIndex], next[targetIndex]] = [next[targetIndex], next[sourceIndex]];
      return next;
    });
  }, []);

  const clearHomeModeDragState = useCallback(() => {
    clearHomeModeLongPress();
    homeModeDragStateRef.current = {
      pointerId: -1,
      modeId: null,
      startX: 0,
      startY: 0,
      hasMoved: false,
    };
    setDraggingHomeModeId(null);
    setDragHoverHomeModeId(null);
    setDraggingHomeModeOffset({ x: 0, y: 0 });
  }, [clearHomeModeLongPress]);

  const handleFinishHomeModeLayout = useCallback(() => {
    setIsHomeModeLayoutEditing(false);
    clearHomeModeDragState();
  }, [clearHomeModeDragState]);

  const handleHomeModeCardClick = useCallback((action?: () => void, disabled?: boolean) => {
    if (isHomeModeLayoutEditing || disabled || !action) return;
    try {
      action();
    } catch (err) {
      showToast(typeof err === 'object' && err && 'message' in err ? (err as Error).message : '发生错误，请稍后再试。');
    }
  }, [isHomeModeLayoutEditing, showToast]);

  const handleHomeModePointerDown = useCallback((modeId: HomeModeId, event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;

    clearHomeModeLongPress();

    if (isHomeModeLayoutEditing) {
      event.preventDefault();
      beginHomeModeDragging(modeId, event.pointerId, event.clientX, event.clientY);
      return;
    }

    homeModeDragStateRef.current = {
      pointerId: event.pointerId,
      modeId,
      startX: event.clientX,
      startY: event.clientY,
      hasMoved: false,
    };
    homeModeLongPressTimerRef.current = window.setTimeout(() => {
      beginHomeModeDragging(modeId, event.pointerId, event.clientX, event.clientY);
      homeModeLongPressTimerRef.current = null;
    }, 420);
  }, [beginHomeModeDragging, clearHomeModeLongPress, isHomeModeLayoutEditing]);

  const handleHomeModePointerMove = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (isHomeModeLayoutEditing || homeModeLongPressTimerRef.current === null) return;

    const dragState = homeModeDragStateRef.current;
    if (dragState.pointerId !== event.pointerId || !dragState.modeId) return;

    const deltaX = event.clientX - dragState.startX;
    const deltaY = event.clientY - dragState.startY;
    if (Math.abs(deltaX) + Math.abs(deltaY) >= 8) {
      clearHomeModeLongPress();
      homeModeDragStateRef.current = {
        pointerId: -1,
        modeId: null,
        startX: 0,
        startY: 0,
        hasMoved: false,
      };
    }
  }, [clearHomeModeLongPress, isHomeModeLayoutEditing]);

  const handleHomeModePointerCancel = useCallback((event: React.PointerEvent<HTMLButtonElement>) => {
    if (homeModeLongPressTimerRef.current === null) return;
    if (homeModeDragStateRef.current.pointerId !== event.pointerId) return;
    clearHomeModeLongPress();
    homeModeDragStateRef.current = {
      pointerId: -1,
      modeId: null,
      startX: 0,
      startY: 0,
      hasMoved: false,
    };
  }, [clearHomeModeLongPress]);

  const handleHomeModeCanvasPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isHomeModeLayoutEditing) return;

    const target = event.target as Element | null;
    if (
      target?.closest?.('[data-home-mode-id]')
      || target?.closest?.('[data-home-layout-reset]')
      || target?.closest?.('[data-home-layout-done]')
    ) {
      return;
    }

    setIsHomeModeLayoutEditing(false);
    clearHomeModeDragState();
  }, [clearHomeModeDragState, isHomeModeLayoutEditing]);

  useEffect(() => {
    if (!isHomeModeLayoutEditing) return;

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = homeModeDragStateRef.current;
      if (dragState.pointerId !== event.pointerId || !dragState.modeId) return;

      if (!dragState.hasMoved) {
        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;
        if (Math.abs(deltaX) + Math.abs(deltaY) < 8) return;
        dragState.hasMoved = true;
      }

      const offsetX = event.clientX - dragState.startX;
      const offsetY = event.clientY - dragState.startY;
      setDraggingHomeModeOffset({ x: offsetX, y: offsetY });

      const targetModeId = getHomeModeIdFromElement(document.elementFromPoint(event.clientX, event.clientY));
      setDragHoverHomeModeId(targetModeId ?? null);
    };

    const handlePointerEnd = (event: PointerEvent) => {
      const dragState = homeModeDragStateRef.current;
      if (dragState.pointerId !== event.pointerId || !dragState.modeId) return;

      const sourceModeId = dragState.modeId;
      const targetModeId = getHomeModeIdFromElement(document.elementFromPoint(event.clientX, event.clientY));

      if (dragState.hasMoved && targetModeId && targetModeId !== sourceModeId) {
        swapHomeModePositions(sourceModeId, targetModeId);
      }

      clearHomeModeDragState();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerEnd);
    window.addEventListener('pointercancel', handlePointerEnd);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerEnd);
      window.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [clearHomeModeDragState, getHomeModeIdFromElement, isHomeModeLayoutEditing, swapHomeModePositions]);

  const handleKwc2026Navigation = useCallback(() => {
    navigate('/kwc2026');
  }, [navigate]);

  const handleKwc2026PointNavigation = useCallback(() => {
    navigate('/kwc2026-point');
  }, [navigate]);

  const homeModeUiText = {
    zh: {
      resetButton: '重置',
      doneButton: '完成',
      testingBadge: '测试中',
      comingSoon: '敬请期待',
    },
    en: {
      resetButton: 'Reset',
      doneButton: 'Done',
      testingBadge: 'In Testing',
      comingSoon: 'Coming Soon',
    },
    ja: {
      resetButton: 'リセット',
      doneButton: '完了',
      testingBadge: 'テスト中',
      comingSoon: '準備中',
    },
  }[language as AppLanguage];

  const mobileHomeUiText = {
    zh: {
      featuredLabel: '活动入口',
      featuredAction: '点击进入',
      quickBattle: '抽招赛',
      quickPoint: '积分赛',
      quickFreestyle: '自由式',
      quickOnline: '联机',
      tabCompetition: '首页',
      tabMap: '地图',
      tabMessages: '消息',
      tabWiki: '百科',
      tabProfile: '个人',
      announcementButton: '公告',
      languageButton: '语言',
      archiveHall: '万剑阁',
      archiveSub: '剑玉图鉴',
      archiveBadge: '收录中',
    },
    en: {
      featuredLabel: 'Featured',
      featuredAction: 'Open',
      quickBattle: 'Draw',
      quickPoint: 'Point',
      quickFreestyle: 'Free',
      quickOnline: 'Online',
      tabCompetition: 'Home',
      tabMap: 'Map',
      tabMessages: 'Messages',
      tabWiki: 'Wiki',
      tabProfile: 'Profile',
      announcementButton: 'News',
      languageButton: 'Lang',
      archiveHall: 'Sword Hall',
      archiveSub: 'Archive',
      archiveBadge: 'In Progress',
    },
    ja: {
      featuredLabel: '注目入口',
      featuredAction: '開く',
      quickBattle: 'ドロー',
      quickPoint: 'ポイント',
      quickFreestyle: 'フリー',
      quickOnline: 'オンライン',
      tabCompetition: '試合',
      tabMap: '地図',
      tabMessages: 'メッセージ',
      tabWiki: '百科',
      tabProfile: '個人',
      announcementButton: '告知',
      languageButton: '言語',
      archiveHall: '万剣閣',
      archiveSub: 'けん玉図鑑',
      archiveBadge: '収録中',
    },
  }[language as AppLanguage];

  const homeAnnouncementUiText = {
    zh: {
      button: '公告',
      title: '首页公告',
      subtitle: 'Announcement',
      empty: '暂无公告'
    },
    en: {
      button: 'News',
      title: 'Announcement',
      subtitle: 'Home Notice',
      empty: 'No announcements yet.'
    },
    ja: {
      button: '告知',
      title: 'お知らせ',
      subtitle: 'Home Notice',
      empty: '現在お知らせはありません。'
    },
  }[language as AppLanguage];

  const dailyCheckInUiText = {
    zh: {
      cardLabel: '每日签到',
      cardCheckedIn: '已签到',
      revealCardButton: '开牌',
      streakPrefix: '连续签到',
      streakSuffix: '天',
      notCheckedIn: '今日未签到',
      modalPrompt: '今日份的剑玉练习完成了吗?',
      confirmButton: 'YES !',
      checkedInSuccess: '签到成功',
      alreadyCheckedIn: '今日已签到',
      history: '历史签到',
      makeupChances: '补签机会',
      chances: '机会',
      makeupButton: '补签',
      makeup: '补',
      weekdays: ['日', '一', '二', '三', '四', '五', '六'],
      makeupSuccess: '补签成功',
      noMakeupChances: '补签机会不足',
      cannotMakeup: '仅能补签过去的日期',
      makeupBeforeFirst: '仅能补签首次签到之后的日期',
      makeupConfirmTitle: '补签确认',
      makeupConfirmMessage: '补签将消耗一次补签机会，确定要补签该日期吗？',
      makeupConfirmOk: '确认补签',
      cancel: '取消',
      makeupRule: '每连续签到3天获得一次补签机会',
      syncFailed: '签到已存在本机，但上传服务器失败，联网后会自动补传',
    },
    en: {
      cardLabel: 'Daily Check-in',
      cardCheckedIn: 'Checked in',
      revealCardButton: 'Reveal',
      streakPrefix: 'Streak',
      streakSuffix: 'days',
      notCheckedIn: 'Not checked in',
      modalPrompt: 'Have you finished today\'s kendama practice?',
      confirmButton: 'YES !',
      checkedInSuccess: 'Checked in',
      alreadyCheckedIn: 'Already checked in',
      history: 'History',
      makeupChances: 'Make-up',
      chances: 'chances',
      makeupButton: 'Make-up',
      makeup: 'M',
      weekdays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      makeupSuccess: 'Made up',
      noMakeupChances: 'No make-up chances',
      cannotMakeup: 'Only past dates can be made up',
      makeupBeforeFirst: 'Only dates after your first check-in can be made up',
      makeupConfirmTitle: 'Confirm Make-up',
      makeupConfirmMessage: 'This will use one make-up chance. Make up this date?',
      makeupConfirmOk: 'Confirm',
      cancel: 'Cancel',
      makeupRule: 'Earn one make-up chance for every 3 consecutive check-ins',
      syncFailed: 'Saved on this device, but syncing to the server failed. It will retry automatically.',
    },
    ja: {
      cardLabel: 'デイリーチェックイン',
      cardCheckedIn: 'チェック済',
      revealCardButton: '開く',
      streakPrefix: '連続',
      streakSuffix: '日',
      notCheckedIn: '未チェックイン',
      modalPrompt: '今日のけん玉練習は終わりましたか?',
      confirmButton: 'YES !',
      checkedInSuccess: 'チェックイン完了',
      alreadyCheckedIn: '本日チェックイン済み',
      history: '履歴',
      makeupChances: '補欠チャンス',
      chances: 'チャンス',
      makeupButton: '補欠',
      makeup: '補',
      weekdays: ['日', '月', '火', '水', '木', '金', '土'],
      makeupSuccess: '補欠完了',
      noMakeupChances: '補欠チャンスなし',
      cannotMakeup: '過去の日付のみ補欠可能',
      makeupBeforeFirst: '初回チェックイン以降の日付のみ補欠できます',
      makeupConfirmTitle: '補欠の確認',
      makeupConfirmMessage: '補欠チャンスを1回消費します。この日付を補欠しますか?',
      makeupConfirmOk: '補欠する',
      cancel: 'キャンセル',
      makeupRule: '3日連続チェックインごとに補欠チャンスを1回獲得',
      syncFailed: '端末には保存しましたが、サーバーへの同期に失敗しました。オンライン時に自動で再送します。',
    },
  }[language as AppLanguage];

  // 标记今日已签到（返回是否真正完成了一次签到）
  const checkInToday = useCallback((): boolean => {
    if (!currentUser) {
      return false;
    }
    const today = getLocalDateString();
    if (dailyCheckIn.checkedInDates.includes(today)) {
      return false;
    }

    const nextDates = Array.from(new Set([...dailyCheckIn.checkedInDates, today])).sort();
    const nextStreak = computeStreak(nextDates);
    // 连续签到每满 3 天获得一次补签机会
    const nextMakeupChances = dailyCheckIn.makeupChances + (nextStreak > 0 && nextStreak % 3 === 0 ? 1 : 0);

    const next: DailyCheckIn = { checkedInDates: nextDates, streak: nextStreak, makeupChances: nextMakeupChances };
    dailyCheckInRef.current = next;
    setDailyCheckIn(next);
    // 先落一份设备本地备份，再写入数据库（随用户对象保存），防止更新/换设备时记录丢失
    writeLocalDailyCheckIn(currentUser.uid, next);
    const updatedUser: User = { ...currentUser, dailyCheckIn: next };
    setCurrentUser(updatedUser);
    void storage.save(updatedUser).then((ok) => {
      // 上传失败时不必中断签到（本地已有备份），但要提示用户，避免出现“签到后消失”
      if (!ok) showToast(dailyCheckInUiText.syncFailed);
    });
    return true;
  }, [currentUser, dailyCheckIn.checkedInDates, dailyCheckIn.makeupChances, dailyCheckInUiText.syncFailed, showToast]);

  const handleDailyCheckIn = useCallback(() => {
    if (!currentUser) {
      setIsDailyCheckInOpen(false);
      setIsAuthOpen(true);
      return;
    }
    const today = getLocalDateString();
    if (dailyCheckIn.checkedInDates.includes(today)) {
      setIsDailyCheckInOpen(false);
      showToast(dailyCheckInUiText.alreadyCheckedIn);
      return;
    }

    checkInToday();
    setIsDailyCheckInOpen(false);
    showToast(dailyCheckInUiText.checkedInSuccess);
  }, [currentUser, dailyCheckIn.checkedInDates, checkInToday, dailyCheckInUiText, showToast]);

  const handleMakeupCheckIn = useCallback((dateStr: string) => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    const today = getLocalDateString();

    if (dailyCheckIn.makeupChances <= 0) {
      showToast(dailyCheckInUiText.noMakeupChances);
      return;
    }
    if (dailyCheckIn.checkedInDates.includes(dateStr)) {
      showToast(dailyCheckInUiText.alreadyCheckedIn);
      return;
    }
    if (dateStr >= today) {
      showToast(dailyCheckInUiText.cannotMakeup);
      return;
    }
    // 仅允许补签首次签到之后的日期
    const firstCheckInDate = dailyCheckIn.checkedInDates.length > 0
      ? [...dailyCheckIn.checkedInDates].sort()[0]
      : null;
    if (firstCheckInDate === null || dateStr <= firstCheckInDate) {
      showToast(dailyCheckInUiText.makeupBeforeFirst);
      return;
    }

    const nextDates = Array.from(new Set([...dailyCheckIn.checkedInDates, dateStr])).sort();
    const nextStreak = computeStreak(nextDates);
    const next: DailyCheckIn = {
      checkedInDates: nextDates,
      streak: nextStreak,
      makeupChances: dailyCheckIn.makeupChances - 1,
    };
    dailyCheckInRef.current = next;
    setDailyCheckIn(next);
    // 先落设备本地备份，再写入数据库，防止更新/换设备时记录丢失
    writeLocalDailyCheckIn(currentUser.uid, next);
    const updatedUser: User = { ...currentUser, dailyCheckIn: next };
    setCurrentUser(updatedUser);
    void storage.save(updatedUser).then((ok) => {
      if (!ok) showToast(dailyCheckInUiText.syncFailed);
    });
    showToast(dailyCheckInUiText.makeupSuccess);
  }, [currentUser, dailyCheckIn.checkedInDates, dailyCheckIn.makeupChances, dailyCheckInUiText, showToast]);

  // 点击补签日期：先弹出确认提示，确认后再执行补签
  const handleMakeupDateSelect = useCallback((dateStr: string) => {
    setPendingMakeupDate(dateStr);
  }, []);

  const handleMakeupConfirm = useCallback(() => {
    if (!pendingMakeupDate) {
      return;
    }
    const dateStr = pendingMakeupDate;
    setPendingMakeupDate(null);
    handleMakeupCheckIn(dateStr);
  }, [pendingMakeupDate, handleMakeupCheckIn]);

  const closeMakeupCalendar = useCallback(() => {
    setIsMakeupCalendarModalOpen(false);
    setPendingMakeupDate(null);
  }, []);

  // 联机模式被限制为管理员时，首页卡片上给个明确提示
  const onlineModeBadge = isOnlineAccessBlocked
    ? ({ zh: '仅管理员', en: 'ADMINS', ja: '管理者限定' } as Record<AppLanguage, string>)[language as AppLanguage]
    : homeModeUiText.testingBadge;
  const onlineModeBadgeClassName = isOnlineAccessBlocked
    ? 'border-amber-400/50 bg-amber-500/20 text-amber-100 shadow-amber-500/10'
    : undefined;

  const homeModes: Record<HomeModeId, HomeModeConfig & { action?: () => void }> = {
    battle: {
      id: 'battle',
      icon: Swords,
      variant: 'small',
      action: () => navigate('/battle-mode'),
      copy: {
        zh: { titleLines: ['对战'], description: 'Classic 1v1 Card Duel' },
        en: { titleLines: ['BATTLE'], description: 'Classic 1v1 Card Duel' },
        ja: { titleLines: ['対戦'], description: 'Classic 1v1 Card Duel' },
      },
      accentClassName: 'hover:border-red-500 hover:bg-red-900/10',
      activeAccentClassName: 'border-red-500/70 bg-red-500/10 shadow-[0_0_28px_rgba(239,68,68,0.2)]',
      iconClassName: 'text-zinc-700 group-hover:text-red-500',
      activeIconClassName: 'text-red-400',
      descriptionActiveClassName: 'group-hover:text-red-200/70',
    },
    point: {
      id: 'point',
      icon: Trophy,
      variant: 'small',
      action: () => handleProtectedNavigation('/tournament', "Please Login to access Tournament Mode. / 请登录以访问锦标赛模式。"),
      copy: {
        zh: { titleLines: ['积分'], description: 'Score Attack Mode' },
        en: { titleLines: ['POINT'], description: 'Score Attack Mode' },
        ja: { titleLines: ['ポイント'], description: 'Score Attack Mode' },
      },
      accentClassName: 'hover:border-yellow-500 hover:bg-yellow-900/10',
      activeAccentClassName: 'border-yellow-500/70 bg-yellow-500/10 shadow-[0_0_28px_rgba(234,179,8,0.18)]',
      iconClassName: 'text-zinc-700 group-hover:text-yellow-500',
      activeIconClassName: 'text-yellow-400',
      descriptionActiveClassName: 'group-hover:text-yellow-200/70',
    },
    freestyle: {
      id: 'freestyle',
      icon: Lightbulb,
      variant: 'small',
      action: () => handleProtectedNavigation('/freestyle', "Please Login to access Freestyle Mode. / 请登录以访问自由式模式。"),
      copy: {
        zh: { titleLines: ['自由式'], description: 'Judge Panel Mode' },
        en: { titleLines: ['FREESTYLE'], description: 'Judge Panel Mode' },
        ja: { titleLines: ['フリースタイル'], description: 'Judge Panel Mode' },
      },
      accentClassName: 'hover:border-purple-500 hover:bg-purple-900/10',
      activeAccentClassName: 'border-purple-500/70 bg-purple-500/10 shadow-[0_0_28px_rgba(168,85,247,0.18)]',
      iconClassName: 'text-zinc-700 group-hover:text-purple-500',
      activeIconClassName: 'text-purple-400',
      descriptionActiveClassName: 'group-hover:text-purple-200/70',
    },
    online: {
      id: 'online',
      icon: Gamepad2,
      variant: 'small',
      action: handleOnlineNavigation,
      copy: {
        zh: { titleLines: ['联机'], description: 'Multiplayer Lobby', badge: onlineModeBadge, badgeClassName: onlineModeBadgeClassName },
        en: { titleLines: ['ONLINE'], description: 'Multiplayer Lobby', badge: onlineModeBadge, badgeClassName: onlineModeBadgeClassName },
        ja: { titleLines: ['オンライン'], description: 'Multiplayer Lobby', badge: onlineModeBadge, badgeClassName: onlineModeBadgeClassName },
      },
      accentClassName: 'hover:border-blue-500 hover:bg-blue-900/10',
      activeAccentClassName: 'border-blue-500/70 bg-blue-500/10 shadow-[0_0_28px_rgba(59,130,246,0.18)]',
      iconClassName: 'text-zinc-700 group-hover:text-blue-500',
      activeIconClassName: 'text-blue-400',
      descriptionActiveClassName: 'group-hover:text-blue-200/70',
    },
    'kendama-map': {
      id: 'kendama-map',
      icon: MapPinned,
      variant: 'tall',
      action: handleKendamaMapNavigation,
      copy: {
        zh: { titleLines: ['剑玉地图'], description: 'Map Point Mode' },
        en: { titleLines: ['KENDAMA', 'MAP'], description: 'Map Point Mode' },
        ja: { titleLines: ['けん玉マップ'], description: 'Map Point Mode' },
      },
      accentClassName: 'hover:border-cyan-400 hover:bg-cyan-900/10',
      activeAccentClassName: 'border-cyan-400/70 bg-cyan-500/10 shadow-[0_0_28px_rgba(34,211,238,0.18)]',
      iconClassName: 'text-zinc-700 group-hover:text-cyan-400',
      activeIconClassName: 'text-cyan-300',
      descriptionActiveClassName: 'group-hover:text-cyan-100/75',
    },
    'kwc2026': {
      id: 'kwc2026',
      icon: Trophy,
      variant: 'tall',
      action: handleKwc2026Navigation,
      copy: {
        zh: { titleLines: ['KWC', '2026', '活动专栏'], description: '练招 · 找队友 · 规则 · 出行', badge: '活动入口', badgeClassName: 'border-sky-400/50 bg-sky-500/20 text-sky-100 shadow-sky-500/10' },
        en: { titleLines: ['KWC', '2026', 'FEATURE'], description: 'Practice · Team-up · Rules · Travel', badge: 'Enter', badgeClassName: 'border-sky-400/50 bg-sky-500/20 text-sky-100 shadow-sky-500/10' },
        ja: { titleLines: ['KWC', '2026', '特集'], description: '練習 · 仲間募集 · ルール · 渡航準備', badge: '入口', badgeClassName: 'border-sky-400/50 bg-sky-500/20 text-sky-100 shadow-sky-500/10' },
      },
      accentClassName: 'hover:border-sky-400 hover:bg-sky-900/10',
      activeAccentClassName: 'border-sky-400/70 bg-sky-500/10 shadow-[0_0_28px_rgba(56,189,248,0.18)]',
      iconClassName: 'text-zinc-700 group-hover:text-sky-400',
      activeIconClassName: 'text-sky-300',
      descriptionActiveClassName: 'group-hover:text-sky-100/75',
    },
  };

  const mobileQuickActions = useMemo(() => ([
    { key: 'battle', modeId: 'battle' as HomeModeId, label: mobileHomeUiText.quickBattle, action: () => navigate('/battle-mode') },
    { key: 'point', modeId: 'point' as HomeModeId, label: mobileHomeUiText.quickPoint, action: () => handleProtectedNavigation('/tournament', 'Please Login to access Tournament Mode. / 请登录以访问锦标赛模式。') },
    { key: 'freestyle', modeId: 'freestyle' as HomeModeId, label: mobileHomeUiText.quickFreestyle, action: () => handleProtectedNavigation('/freestyle', 'Please Login to access Freestyle Mode. / 请登录以访问自由式模式。') },
    { key: 'online', modeId: 'online' as HomeModeId, label: mobileHomeUiText.quickOnline, action: handleOnlineNavigation },
  ]), [handleOnlineNavigation, handleProtectedNavigation, mobileHomeUiText.quickBattle, mobileHomeUiText.quickFreestyle, mobileHomeUiText.quickOnline, mobileHomeUiText.quickPoint, navigate]);

  const mobileFeaturedCards = useMemo<FeaturedActivityCardConfig[]>(() => {
    const cards: FeaturedActivityCardConfig[] = [
      {
      id: 'kwc-teamup',
      action: handleKwc2026Navigation,
      copy: {
        zh: {
          titleLines: ['2026', 'KENDAMA', 'WORLE CUP', '活动专栏', '报名时间', '2026 年 8 月 6 日-2026 年 10 月 12 日', '比赛时间', '11月21日', '11月22日'],
          titleLineClassNames: ['', '', '', '', 'mt-3 text-[clamp(12px,2.8vw,15px)] tracking-[0.08em] text-sky-100/80', 'text-[clamp(11px,2.5vw,13px)] tracking-[0.02em] text-sky-50/90', 'mt-3 text-[clamp(12px,2.8vw,15px)] tracking-[0.08em] text-sky-100/80', 'text-[clamp(14px,3vw,17px)] tracking-[0.04em]', 'text-[clamp(14px,3vw,17px)] tracking-[0.04em]'],
          description: '这里把练招、找队友、规则和出行准备都集中到一起，方便你在比赛前一站式查看。',
          badge: '活动入口',
          badgeClassName: 'border-sky-400/50 bg-sky-500/20 text-sky-100 shadow-sky-500/10'
        },
        en: {
          titleLines: ['2026', 'KENDAMA', 'WORLE CUP', 'FEATURE', 'Entry Period', 'Aug 6 - Oct 12, 2026', 'Event Date', 'Nov 21', 'Nov 22'],
          titleLineClassNames: ['', '', '', '', 'mt-3 text-[clamp(12px,2.8vw,15px)] tracking-[0.08em] text-sky-100/80', 'text-[clamp(11px,2.5vw,13px)] tracking-[0.02em] text-sky-50/90', 'mt-3 text-[clamp(12px,2.8vw,15px)] tracking-[0.08em] text-sky-100/80', 'text-[clamp(14px,3vw,17px)] tracking-[0.04em]', 'text-[clamp(14px,3vw,17px)] tracking-[0.04em]'],
          description: 'Practice, team-up, rules, and travel prep are all gathered here for one-stop access before the event.',
          badge: 'Enter',
          badgeClassName: 'border-sky-400/50 bg-sky-500/20 text-sky-100 shadow-sky-500/10'
        },
        ja: {
          titleLines: ['2026', 'KENDAMA', 'WORLE CUP', '特集', 'エントリー期間', '2026年8月6日-2026年10月12日', '大会日程', '11月21日', '11月22日'],
          titleLineClassNames: ['', '', '', '', 'mt-3 text-[clamp(12px,2.8vw,15px)] tracking-[0.08em] text-sky-100/80', 'text-[clamp(11px,2.5vw,13px)] tracking-[0.02em] text-sky-50/90', 'mt-3 text-[clamp(12px,2.8vw,15px)] tracking-[0.08em] text-sky-100/80', 'text-[clamp(14px,3vw,17px)] tracking-[0.04em]', 'text-[clamp(14px,3vw,17px)] tracking-[0.04em]'],
          description: '練習、仲間募集、ルール、渡航準備をここにまとめて、事前にまとめて確認できます。',
          badge: '入口',
          badgeClassName: 'border-sky-400/50 bg-sky-500/20 text-sky-100 shadow-sky-500/10'
        },
      },
      cardClassName: 'border-sky-400/35 bg-[linear-gradient(145deg,rgba(8,18,30,0.82),rgba(5,10,24,0.76),rgba(6,22,30,0.82))] shadow-[0_0_32px_rgba(56,189,248,0.14)]',
      labelClassName: 'text-sky-200/80',
      descriptionClassName: 'text-sky-100/78',
      actionClassName: 'text-sky-200/85',
      backgroundImageUrl: kwcLogoBackground,
      backgroundOverlayClassName: 'bg-[linear-gradient(160deg,rgba(6,18,34,0.28),rgba(5,18,38,0.7),rgba(4,12,24,0.84))]',
      },
    ];

    // KWC模拟决赛目前还在试运行，活动卡片只对管理员显示。
    if (canAccessWarmupAdmin(currentUser)) {
      cards.unshift({
        id: 'kwc2026-point',
        action: handleKwc2026PointNavigation,
        copy: {
          zh: {
            titleLines: ['KWC', '模拟决赛'],
            description: '3 分钟打完所选招式组 · 投稿视频 · 排行榜',
            badge: '管理员',
            badgeClassName: 'border-amber-400/50 bg-amber-500/20 text-amber-100 shadow-amber-500/10',
          },
          en: {
            titleLines: ['KWC', 'SIM FINAL'],
            description: '3-minute run · video submission · leaderboard',
            badge: 'Admin',
            badgeClassName: 'border-amber-400/50 bg-amber-500/20 text-amber-100 shadow-amber-500/10',
          },
          ja: {
            titleLines: ['KWC', '模擬決勝'],
            description: '3分間の演技 · 動画投稿 · ランキング',
            badge: '管理者',
            badgeClassName: 'border-amber-400/50 bg-amber-500/20 text-amber-100 shadow-amber-500/10',
          },
        },
        cardClassName: 'border-amber-400/35 bg-[linear-gradient(145deg,rgba(30,18,8,0.82),rgba(24,12,6,0.78),rgba(30,22,8,0.82))] shadow-[0_0_32px_rgba(245,158,11,0.14)]',
        labelClassName: 'text-amber-200/80',
        descriptionClassName: 'text-amber-100/78',
        actionClassName: 'text-amber-200/85',
        backgroundImageUrl: kwcLogoBackground,
        backgroundOverlayClassName: 'bg-[linear-gradient(160deg,rgba(30,20,6,0.3),rgba(32,20,6,0.72),rgba(20,12,4,0.86))]',
      });
    }

    return cards;
  }, [currentUser, handleKwc2026Navigation, handleKwc2026PointNavigation]);
  const [activeFeaturedCardIndex, setActiveFeaturedCardIndex] = useState(0);
  const featuredCardCount = mobileFeaturedCards.length;
  const shouldReduceWarmupEffects = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const hostname = window.location.hostname;
    // 原生 App 的页面也来自 Capacitor 内置 localhost，不能当成「本地预览」，
    // 否则真机 iPhone 上的重动效降级永远不会生效。
    const isPreviewHost = !isNativeApp() && (hostname === 'localhost' || hostname === '127.0.0.1');
    if (isPreviewHost) return false;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const userAgent = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isSafari = /Safari/i.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(userAgent);
    return prefersReducedMotion || (isIOS && isSafari);
  }, []);

  const shouldShowMobileBottomNav = useMemo(() => {
    if (location.pathname === '/online' && onlineRoomActive) return false;
    // 百科也保留底部栏：它本身就是主导航的一站，把栏藏掉会让人只能靠返回键出去
    const mainPages = ['/', '/kendama-map', '/messages', '/wiki', '/profile', '/online'];
    return mainPages.includes(location.pathname);
  }, [location.pathname, onlineRoomActive]);
  const isKwcRoute = location.pathname.startsWith('/kwc2026');

  const isNativeIos = useMemo(
    () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios',
    []
  );

  // 真正的 Liquid Glass 底栏需要 iOS 26+；旧系统上原生 UITabBar 会以普通外观渲染，
  // 与浮层布局不匹配导致底部栏显示异常，因此旧系统直接回退到应用内底部栏。
  // null 代表仍在探测中。
  const [liquidGlassSupported, setLiquidGlassSupported] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isNativeIos) {
      setLiquidGlassSupported(false);
      return;
    }
    let cancelled = false;
    detectLiquidGlassSupport().then((supported) => {
      if (!cancelled) setLiquidGlassSupported(supported);
    });
    return () => {
      cancelled = true;
    };
  }, [isNativeIos]);

  // 原生底栏是否真正启用：仅当 iOS 26+ 且 showTabBar 调用成功时为 true
  const [nativeTabBarActive, setNativeTabBarActive] = useState(false);

  useEffect(() => {
    if (!isNativeIos) return;

    // 探测完成前（null）不启用原生底栏，避免在旧系统上闪出原生 UITabBar
    // 初始化（会话加载）期间同样不显示，避免用户误操作
    const canUseNativeTabBar = liquidGlassSupported === true && !isSessionLoading;
    if (!canUseNativeTabBar || !shouldShowMobileBottomNav) {
      setNativeTabBarActive(false);
      LiquidGlass.hideTabBar().catch(() => {});
      return;
    }

    const tabs = [
      { id: '/', label: mobileHomeUiText.tabCompetition, sfSymbol: 'house.fill' },
      { id: '/kendama-map', label: mobileHomeUiText.tabMap, sfSymbol: 'map.fill', badge: '•' },
      { id: '/messages', label: mobileHomeUiText.tabMessages, sfSymbol: 'message.fill' },
      { id: 'wiki', label: mobileHomeUiText.tabWiki, sfSymbol: 'book.fill' },
      { id: '/profile', label: mobileHomeUiText.tabProfile, sfSymbol: 'person.fill' },
    ];
    const selectedIndex = Math.max(0, tabs.findIndex((item) => item.id === location.pathname));

    let cancelled = false;
    // 先乐观隐藏应用内底部栏，原生底栏成功后再保持隐藏；失败则回退显示
    setNativeTabBarActive(true);
    try {
      LiquidGlass.showTabBar({
        items: tabs,
        selectedIndex,
        tintColor: '#22D3EE',
        tabBarStyle: 'liquidGlass',
      })
        .then(() => LiquidGlass.setSelectedTab({ index: selectedIndex }))
        .catch(() => {
          if (!cancelled) setNativeTabBarActive(false);
        });
    } catch {
      setNativeTabBarActive(false);
    }

    return () => {
      cancelled = true;
    };
  }, [isNativeIos, liquidGlassSupported, shouldShowMobileBottomNav, language, location.pathname, isSessionLoading]);

  // 保存最新的 tab 导航处理函数，供原生 tab bar 监听器读取，避免监听器闭包捕获到旧的登录状态
  const tabNavHandlersRef = useRef({
    navigate,
    handleKendamaMapNavigation,
    handleMessagesNavigation,
    handleProfileNavigation,
    handleEncyclopediaNavigation,
  });

  useEffect(() => {
    tabNavHandlersRef.current = {
      navigate,
      handleKendamaMapNavigation,
      handleMessagesNavigation,
      handleProfileNavigation,
      handleEncyclopediaNavigation,
    };
  });

  useEffect(() => {
    if (!isNativeIos || !nativeTabBarActive) return;

    let cancelled = false;
    let handle: { remove: () => Promise<void> } | undefined;

    LiquidGlass.addListener('tabSelected', ({ id, index }) => {
      if (cancelled) return;
      const handlers = tabNavHandlersRef.current;
      // 原生回传的 id 可能带或不带前导斜杠，先统一规整；
      // 万一某个 id 与预期不符（历史上就出现过单个 tab 静默失效），
      // 再用 index 兜底——顺序与上面 tabs 的定义保持一致。
      const key = (id ?? '').replace(/^\/+/, '').replace(/\/+$/, '');
      const byKey: Record<string, () => void> = {
        '': () => handlers.navigate('/'),
        'kendama-map': handlers.handleKendamaMapNavigation,
        messages: handlers.handleMessagesNavigation,
        wiki: handlers.handleEncyclopediaNavigation,
        profile: handlers.handleProfileNavigation,
      };
      const byIndex = [
        () => handlers.navigate('/'),
        handlers.handleKendamaMapNavigation,
        handlers.handleMessagesNavigation,
        handlers.handleEncyclopediaNavigation,
        handlers.handleProfileNavigation,
      ];
      const handler = byKey[key] ?? (typeof index === 'number' ? byIndex[index] : undefined);
      handler?.();
    })
      .then((registeredHandle) => {
        if (cancelled) {
          registeredHandle.remove().catch(() => {});
          return;
        }
        handle = registeredHandle;
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (handle) {
        handle.remove().catch(() => {});
      }
    };
  }, [isNativeIos, nativeTabBarActive]);

  const shiftFeaturedCard = useCallback((direction: 1 | -1) => {
    if (featuredCardCount <= 1) return;
    setActiveFeaturedCardIndex((prev) => (prev + direction + featuredCardCount) % featuredCardCount);
  }, [featuredCardCount]);

  useEffect(() => {
    if (featuredCardCount <= 1) return;
    const timer = window.setTimeout(() => {
      shiftFeaturedCard(1);
    }, FEATURED_CARD_AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [activeFeaturedCardIndex, featuredCardCount, shiftFeaturedCard]);

  const handleFeaturedActivityPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    featuredActivitySwipeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      deltaX: 0,
      deltaY: 0,
      captured: false,
    };
    // 注意：这里不能立刻 setPointerCapture。
    // 指针捕获会把后续的 click 事件也改派到容器上，卡片按钮的 onClick 就永远不会触发，
    // 表现就是「活动卡片点不进去」。改成真的开始拖动时再接管指针。
  }, []);

  const handleFeaturedActivityPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const swipe = featuredActivitySwipeRef.current;
    if (swipe.pointerId !== event.pointerId) return;
    swipe.deltaX = event.clientX - swipe.startX;
    swipe.deltaY = event.clientY - swipe.startY;
    if (!swipe.captured && Math.abs(swipe.deltaX) + Math.abs(swipe.deltaY) > 6) {
      swipe.captured = true;
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // 少数浏览器不支持指针捕获，拖动逻辑本身不依赖它
      }
    }
  }, []);

  const handleFeaturedActivityPointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (featuredActivitySwipeRef.current.pointerId !== event.pointerId) return;
    const { deltaX, deltaY } = featuredActivitySwipeRef.current;
    if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
      featuredActivitySuppressClickRef.current = true;
      shiftFeaturedCard(deltaX < 0 ? 1 : -1);
    }
    featuredActivitySwipeRef.current = {
      pointerId: -1,
      startX: 0,
      startY: 0,
      deltaX: 0,
      deltaY: 0,
      captured: false,
    };
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, [shiftFeaturedCard]);

  const handleFeaturedActivityCardClick = useCallback((card: FeaturedActivityCardConfig) => {
    if (featuredActivitySuppressClickRef.current) {
      featuredActivitySuppressClickRef.current = false;
      return;
    }
    if (card.disabled || !card.action) return;
    card.action();
  }, []);

  const renderFeaturedActivityCard = (card: FeaturedActivityCardConfig, options?: { className?: string; inert?: boolean; keySuffix?: string }) => {
    const localizedCopy = card.copy[language as AppLanguage];
    const isWarmupCard = card.showWarmupEffects;
    const animationClassName = options?.className || '';
    const keySuffix = options?.keySuffix || 'base';

    return (
      <button
        key={`${card.id}-${keySuffix}`}
        type="button"
        onClick={() => handleFeaturedActivityCardClick(card)}
        disabled={card.disabled || options?.inert}
        aria-hidden={options?.inert}
        tabIndex={options?.inert ? -1 : undefined}
        className={`${isWarmupCard && !shouldReduceWarmupEffects ? 'warmup-glitch' : ''} ${isWarmupCard && shouldReduceWarmupEffects ? 'warmup-glitch-lite' : ''} ${animationClassName} relative flex h-full w-full flex-col justify-between overflow-hidden rounded-[26px] border px-[clamp(16px,3.6vw,22px)] py-[clamp(12px,2.4vw,18px)] text-left transition-transform active:scale-[0.985] ${card.cardClassName} ${card.disabled ? 'cursor-default' : ''}`}
        aria-label={localizedCopy.titleLines.join(' ')}
      >
        {card.backgroundImageUrl && (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-50"
              style={{ backgroundImage: `url(${card.backgroundImageUrl})` }}
              aria-hidden="true"
            />
            <div
              className={`absolute inset-0 ${card.backgroundOverlayClassName || 'bg-[linear-gradient(160deg,rgba(10,8,16,0.3),rgba(12,7,24,0.72),rgba(6,6,14,0.82))]'}`}
              aria-hidden="true"
            />
          </>
        )}
        {isWarmupCard && (
          <div className={shouldReduceWarmupEffects ? 'warmup-radar-bg warmup-radar-bg-lite' : 'warmup-radar-bg'}>
            {radarBlipPositions.map((blip, i) => (
              <div
                key={i}
                className="warmup-radar-blip"
                style={{
                  top: `${blip.top}%`,
                  left: `${blip.left}%`,
                  animationDelay: `${blip.delay}s`,
                }}
              />
            ))}
          </div>
        )}
        {localizedCopy.badge && (
          <div className={`absolute right-4 top-4 z-10 rounded-full border px-3 py-1 font-orbitron text-[10px] uppercase tracking-[0.14em] shadow-lg ${localizedCopy.badgeClassName || 'border-cyan-400/40 bg-cyan-500/15 text-cyan-200 shadow-cyan-500/10'}`}>
            {localizedCopy.badge}
          </div>
        )}
        <div className="relative z-10 min-w-0">
          <div className="flex w-full min-w-0 flex-col font-orbitron text-[clamp(20px,4.6vw,30px)] font-bold leading-[1.05] tracking-[0.05em] text-white">
            {localizedCopy.titleLines.map((line, index) => (
              <span
                key={`${card.id}-${keySuffix}-${index}`}
                className={`${index > 0 ? 'mt-1' : ''} w-full break-words leading-tight ${localizedCopy.titleLineClassNames?.[index] || ''}`.trim()}
              >
                {line}
              </span>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center justify-between gap-3">
          <div className={`text-[12px] uppercase tracking-[0.2em] ${card.actionClassName}`}>
            {card.disabled ? homeModeUiText.comingSoon : mobileHomeUiText.featuredAction}
          </div>
        </div>
        {isWarmupCard && !shouldReduceWarmupEffects && <div className="warmup-glitch-slice" />}
        {isWarmupCard && !shouldReduceWarmupEffects && <div className="warmup-glitch-slice" />}
        {isWarmupCard && !shouldReduceWarmupEffects && <div className="warmup-glitch-slice" />}
      </button>
    );
  };

  const handleLogout = async () => {
    // 退出过程中重复点击（用户中心菜单 + 个人中心各有一处入口）会重复发请求，
    // 这里用 ref 兜住：状态更新是异步的，光靠 isLoggingOut 拦不住连续两次点击。
    if (logoutInFlightRef.current) return;
    logoutInFlightRef.current = true;
    const startedAt = Date.now();
    setIsLoggingOut(true);

    try {
        if (currentUser && !currentUser.uid.startsWith('guest-')) {
             const logoutOptions = isLocalBackend() ? {} : { headers: phpAuthHeaders() };
             await fetch(isLocalBackend() ? '/api/logout' : phpApiUrl('logout'), { method: 'POST', ...logoutOptions });
        }
    } catch (error) {
        console.error("Logout failed", error);
    } finally {
        // 保证提示层至少停留一小段时间，再切到未登录状态。
        const remainingMs = LOGOUT_OVERLAY_MIN_DURATION_MS - (Date.now() - startedAt);
        if (remainingMs > 0) {
          await new Promise<void>(resolve => window.setTimeout(resolve, remainingMs));
        }
        setIsUserCenterMenuVisible(false);
        setIsUserCenterMenuOpen(false);
        setCurrentUser(null);
        setAuthToken(null);
        // 退出登录时清除自动登录凭据，避免下次启动被自动登录
        try {
          window.localStorage.removeItem(AUTH_AUTO_LOGIN_KEY);
        } catch (e) {
          // 忽略本地存储异常
        }
        setView('MENU');
        if (gameState.phase !== 'START') returnToMenu();
        setIsLoggingOut(false);
        logoutInFlightRef.current = false;
    }
  };

  const handleUpdateUser = useCallback((updatedUser: User) => {
    setCurrentUser(updatedUser);
  }, []);

  const openUserCenterMenu = useCallback(() => {
    if (userCenterMenuCloseTimerRef.current !== null) {
      window.clearTimeout(userCenterMenuCloseTimerRef.current);
      userCenterMenuCloseTimerRef.current = null;
    }
    if (userCenterMenuOpenFrameRef.current !== null) {
      window.cancelAnimationFrame(userCenterMenuOpenFrameRef.current);
      userCenterMenuOpenFrameRef.current = null;
    }

    setIsUserCenterMenuOpen(true);
    setIsUserCenterMenuVisible(false);
    userCenterMenuOpenFrameRef.current = window.requestAnimationFrame(() => {
      setIsUserCenterMenuVisible(true);
      userCenterMenuOpenFrameRef.current = null;
    });
  }, []);

  const closeUserCenterMenu = useCallback((immediate = false) => {
    if (userCenterMenuOpenFrameRef.current !== null) {
      window.cancelAnimationFrame(userCenterMenuOpenFrameRef.current);
      userCenterMenuOpenFrameRef.current = null;
    }
    if (userCenterMenuCloseTimerRef.current !== null) {
      window.clearTimeout(userCenterMenuCloseTimerRef.current);
      userCenterMenuCloseTimerRef.current = null;
    }

    setIsUserCenterMenuVisible(false);

    if (immediate) {
      setIsUserCenterMenuOpen(false);
      return;
    }

    userCenterMenuCloseTimerRef.current = window.setTimeout(() => {
      setIsUserCenterMenuOpen(false);
      userCenterMenuCloseTimerRef.current = null;
    }, 260);
  }, []);

  const handleOpenUserCenterTab = useCallback((tab: UserProfileTab) => {
    closeUserCenterMenu(true);
    navigate(`/profile?tab=${tab}`);
  }, [closeUserCenterMenu, navigate]);

  const userCenterMenuItems = useMemo<UserCenterMenuItem[]>(() => {
    const items: UserCenterMenuItem[] = [
      { key: 'profile', label: '个人资料', description: 'View / Edit Profile' },
      { key: 'password', label: '修改密码', description: 'Change Password' },
      { key: 'settings', label: '设置', description: 'Audio Settings' },
      { key: 'feedback', label: '问题反馈', description: 'Send Feedback' },
    ];

    if (currentUser?.isAdmin) {
      items.push({ key: 'admin', label: '管理后台', description: 'Open Admin Panel' });
    }

    items.push({ key: 'logout', label: '退出登录', description: 'Sign Out' });
    return items;
  }, [currentUser?.isAdmin]);

  const currentUserAvatarFallback = useMemo(() => {
    const base = currentUser?.displayName?.trim() || currentUser?.email || 'U';
    return base.charAt(0).toUpperCase();
  }, [currentUser?.displayName, currentUser?.email]);

  const syncManagedUserData = useCallback(async () => {
    if (!currentUser) return;

    if (syncManagedUserDataPromiseRef.current) {
      return syncManagedUserDataPromiseRef.current;
    }

    const syncPromise = (async () => {
      const [managedMasterLists, managedDrawPresetDecks] = await Promise.all([
        fetchManagedTournamentMasterLists(),
        fetchManagedDrawPresetDecks()
      ]);

      setCurrentUser(previousUser => {
        if (!previousUser) {
          return previousUser;
        }

        const updatedUser = migrateUserData(previousUser, managedMasterLists, managedDrawPresetDecks);
        const previousSnapshot = JSON.stringify({
          customDecks: previousUser.customDecks ?? [],
          customFolders: previousUser.customFolders ?? [],
          tournamentData: previousUser.tournamentData ?? null
        });
        const updatedSnapshot = JSON.stringify({
          customDecks: updatedUser.customDecks ?? [],
          customFolders: updatedUser.customFolders ?? [],
          tournamentData: updatedUser.tournamentData ?? null
        });

        return previousSnapshot === updatedSnapshot ? previousUser : updatedUser;
      });
    })().finally(() => {
      if (syncManagedUserDataPromiseRef.current === syncPromise) {
        syncManagedUserDataPromiseRef.current = null;
      }
    });

    syncManagedUserDataPromiseRef.current = syncPromise;
    return syncPromise;
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser) return;

    let mounted = true;

    const syncIfMounted = async () => {
      if (!mounted) return;
      await syncManagedUserData();
    };

    const handleWindowFocus = () => {
      syncIfMounted();
    };

    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === MANAGED_MASTER_LISTS_VERSION_KEY ||
        event.key === MANAGED_DRAW_PRESET_DECKS_VERSION_KEY
      ) {
        syncIfMounted();
      }
    };

    syncIfMounted();
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('storage', handleStorage);

    return () => {
      mounted = false;
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('storage', handleStorage);
    };
  }, [currentUser?.uid, location.pathname, syncManagedUserData]);

  useEffect(() => {
    closeUserCenterMenu(true);
  }, [closeUserCenterMenu, location.pathname, location.search]);

  useEffect(() => {
    return () => {
      if (userCenterMenuOpenFrameRef.current !== null) {
        window.cancelAnimationFrame(userCenterMenuOpenFrameRef.current);
      }
      if (userCenterMenuCloseTimerRef.current !== null) {
        window.clearTimeout(userCenterMenuCloseTimerRef.current);
      }
    };
  }, []);

  // --- Game Actions ---
  const handleCustomClick = () => {
      setIsDeckSelectionOpen(true);
  };

  const startCustomGame = (deck: CustomDeck) => {
      setIsDeckSelectionOpen(false);
      if (deck.tricks.length === 0) {
          alert("This deck is empty! / 该招式表为空！");
          return;
      }
      initializeGame('CUSTOM', deck.tricks, 'DRAW', gameState.customHealth);
      navigate('/game');
  };

  const startGame = (difficulty: Difficulty, mode: 'DRAW' | 'FREE' = 'DRAW', health: number = MAX_ATTEMPTS) => {
    initializeGame(difficulty, [], mode, health);
  };

  const initializeGame = (
    difficulty: Difficulty, 
    customTricks: string[], 
    mode: 'DRAW' | 'FREE' = 'DRAW',
    health: number = WINNING_SCORE
  ) => {
    const deck = mode === 'DRAW' ? getDeck(difficulty, customTricks) : [];
    const shuffled = mode === 'DRAW' ? shuffleDeck(deck) : [];
    setGameHistory([]);
    setGameState(prev => ({
      ...prev,
      phase: mode === 'FREE' ? 'PLAYING' : 'DRAWING',
      battleMode: mode,
      deck: shuffled,
      activeCards: mode === 'FREE' ? [{ id: 'free', name: 'Free Trick', difficulty: 'CUSTOM', isCompleted: false }] : [],
      discardPile: [],
      currentPlayerIndex: 0, 
      roundStarterIndex: 0,
      subRoundIndex: 1,
      firstHandResult: null,
      customHealth: health,
      players: prev.players.map(p => ({ ...p, health, attemptsLeft: MAX_ATTEMPTS })),
      winnerId: null,
      roundMessage: mode === 'FREE' ? 'Free Battle Started' : 'Draw Phase',
      difficulty: difficulty
    }));
  };

  const returnToMenu = () => {
    setGameHistory([]);
    setGameState(prev => {
      return {
        ...prev,
        phase: 'START',
        winnerId: null,
        roundMessage: '',
        players: prev.players.map(p => ({ ...p, health: prev.customHealth || WINNING_SCORE, attemptsLeft: MAX_ATTEMPTS })),
      };
    });
  };

  // 判定出赢家后先让棋盘静一拍，再让胜利弹窗进场；离开 /game 或重开时立刻收起。
  useEffect(() => {
    const winnerId = gameState.winnerId;
    if (winnerId === null || location.pathname !== '/game') {
      setShowWinnerModal(false);
      return;
    }

    const winner = gameState.players.find(p => p.id === winnerId);
    const opponent = gameState.players.find(p => p.id !== winnerId);
    if (winner) {
      winnerSnapshotRef.current = { winner, opponent };
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const timer = window.setTimeout(
      () => setShowWinnerModal(true),
      prefersReducedMotion ? 0 : WINNER_REVEAL_DELAY_MS
    );
    return () => window.clearTimeout(timer);
    // 只跟赢家与路由联动：players 每次 setGameState 都会换新数组，
    // 放进依赖会让这 550ms 被后续更新不断重置。
  }, [gameState.winnerId, location.pathname]);

  const handleBack = () => {
    if (view === 'TOURNAMENT_GAME' || view === 'TOURNAMENT_EDITOR' || view === 'TOURNAMENT_HISTORY') {
      setView('TOURNAMENT_MENU');
      return;
    }
    if (view === 'FREESTYLE_ROOM') {
      setView('FREESTYLE_LOBBY');
      return;
    }
    if (view === 'GAME') {
      returnToMenu();
      setView('BATTLE_MODE');
      return;
    }
    if (view === 'TOURNAMENT_MENU' || view === 'FREESTYLE_LOBBY' || view === 'BATTLE_MODE' || view === 'ADMIN' || view === 'PROFILE' || view === 'KENDAMA_MAP') {
      setView('MENU');
      return;
    }
    if (gameState.phase !== 'START') {
        returnToMenu();
    } else {
        setView('MENU');
    }
  };

  const drawCards = useCallback(() => {
    if (gameState.deck.length < 1) {
      let newDeck = [...gameState.deck];
      if (newDeck.length < 1 && gameState.discardPile.length > 0) {
        newDeck = [...newDeck, ...gameState.discardPile];
        newDeck = shuffleDeck(newDeck);
         setGameState(prev => ({ ...prev, discardPile: [], deck: newDeck })); 
      } else if (newDeck.length < 1 && gameState.discardPile.length === 0) {
         setGameState(prev => ({...prev, phase: 'GAME_OVER', roundMessage: "No more cards! Draw game."}));
         return;
      }
    }

    setGameState(prev => {
      let currentDeck = [...prev.deck];
      let discards = [...prev.discardPile];
      
      if (currentDeck.length < 1) {
        currentDeck = [...currentDeck, ...discards];
        discards = [];
        currentDeck.sort(() => Math.random() - 0.5);
      }

      const drawn = currentDeck.splice(0, 1);
      const currentPlayerName = prev.players[prev.roundStarterIndex].name;

      return {
        ...prev,
        deck: currentDeck,
        activeCards: drawn,
        discardPile: discards,
        phase: 'PLAYING',
        roundMessage: `${currentPlayerName} starts Round 1`,
        players: prev.players.map(p => ({ ...p, attemptsLeft: MAX_ATTEMPTS })),
        currentPlayerIndex: prev.roundStarterIndex, 
        subRoundIndex: 1,
        firstHandResult: null,
      };
    });
  }, [gameState.deck, gameState.discardPile]);

  useEffect(() => {
    if (gameState.phase === 'DRAWING') {
      const timer = setTimeout(() => {
        drawCards();
      }, 2000); 
      return () => clearTimeout(timer);
    }
  }, [gameState.phase, drawCards]);


  const handleAttempt = (currentSuccess: boolean) => {
    if (gameState.phase !== 'PLAYING') return;
    setGameHistory(prev => [...prev.slice(-11), snapshotGame(gameState)]);

    setGameState(prev => {
      const nextPlayers = prev.players.map(p => ({ ...p }));
      const isFirstHand = prev.currentPlayerIndex === prev.roundStarterIndex;
      const starterPlayer = nextPlayers[prev.roundStarterIndex];
      const followerPlayer = nextPlayers[(prev.roundStarterIndex + 1) % 2];
      const currentPlayer = nextPlayers[prev.currentPlayerIndex];
      const otherPlayerIndex = (prev.currentPlayerIndex + 1) % 2;

      let nextPhase = prev.phase;
      let nextWinner = prev.winnerId;
      let nextRoundMessage = prev.roundMessage;
      let nextPlayerIndex = otherPlayerIndex; 
      let nextSubRoundIndex = prev.subRoundIndex;
      let nextFirstHandResult = prev.firstHandResult;
      let shouldDrawNewCard = false;
      let nextDiscard = prev.discardPile;
      let nextActiveCards = prev.activeCards;

      if (currentPlayer.attemptsLeft > 0) {
        currentPlayer.attemptsLeft -= 1;
      }

      if (isFirstHand) {
          nextFirstHandResult = currentSuccess ? 'success' : 'fail';
          nextRoundMessage = `Round ${prev.subRoundIndex}/3: ${currentPlayer.name} ${currentSuccess ? 'Success' : 'Fail'}. ${followerPlayer.name}'s turn.`;
          nextPlayerIndex = (prev.roundStarterIndex + 1) % 2; 
      } else {
          const p1Result = prev.firstHandResult;
          const p2Result = currentSuccess ? 'success' : 'fail';
          
          if (p1Result !== p2Result) {
              shouldDrawNewCard = true;
              if (p1Result === 'success' && p2Result === 'fail') {
                  followerPlayer.health = Math.max(0, followerPlayer.health - 1);
                  nextRoundMessage = `Round ${prev.subRoundIndex}/3: ${starterPlayer.name} Wins! (Success vs Fail)`;
              } else {
                  starterPlayer.health = Math.max(0, starterPlayer.health - 1);
                  nextRoundMessage = `Round ${prev.subRoundIndex}/3: ${followerPlayer.name} Wins! (Fail vs Success)`;
              }
          } else {
              const drawType = p1Result === 'success' ? "Both Success" : "Both Fail";
              if (prev.subRoundIndex < 3) {
                  nextSubRoundIndex = prev.subRoundIndex + 1;
                  nextFirstHandResult = null; 
                  nextPlayerIndex = prev.roundStarterIndex; 
                  nextRoundMessage = `Round ${prev.subRoundIndex} Draw (${drawType}). Entering Round ${nextSubRoundIndex}...`;
              } else {
                  shouldDrawNewCard = true;
                  nextRoundMessage = `Round 3 Draw (${drawType}). No damage dealt.`;
              }
          }
      }

      const defeatedPlayer = nextPlayers.find(p => p.health <= 0);
      if (defeatedPlayer) {
          nextPhase = 'GAME_OVER';
          const winner = nextPlayers.find(p => p.id !== defeatedPlayer.id)!;
          nextWinner = winner.id;
          nextRoundMessage = `Game Over! ${winner.name} wins!`;
          shouldDrawNewCard = false; 
      }

      if (shouldDrawNewCard && nextPhase !== 'GAME_OVER') {
          nextPhase = 'ROUND_END';
          nextDiscard = [...nextDiscard, ...prev.activeCards];
          nextActiveCards = [];
          nextFirstHandResult = null; 
      }

      if (nextWinner !== null) {
        return {
          ...prev,
          phase: 'GAME_OVER',
          roundMessage: `Game Over! ${prev.players[nextWinner].name} wins!`,
          players: nextPlayers,
          winnerId: nextWinner,
          activeCards: nextActiveCards,
          discardPile: nextDiscard,
          currentPlayerIndex: nextPlayerIndex,
          subRoundIndex: nextSubRoundIndex,
          firstHandResult: nextFirstHandResult,
        };
      }

      if (nextPhase === 'ROUND_END') {
        const nextStarter = (prev.roundStarterIndex + 1) % 2;
        const isFree = prev.battleMode === 'FREE';
        
        if (isFree) {
          return {
            ...prev,
            phase: 'PLAYING',
            roundMessage: `${nextPlayers[nextStarter].name} starts next round`,
            players: nextPlayers.map(p => ({ ...p, attemptsLeft: MAX_ATTEMPTS })),
            winnerId: nextWinner,
            activeCards: [{ id: `free_${Date.now()}`, name: 'Free Trick', difficulty: 'CUSTOM', isCompleted: false }],
            discardPile: nextDiscard,
            currentPlayerIndex: nextStarter,
            roundStarterIndex: nextStarter,
            subRoundIndex: 1,
            firstHandResult: null,
          };
        }
      }

      return {
        ...prev,
        players: nextPlayers,
        phase: nextPhase as GameState['phase'],
        winnerId: nextWinner,
        activeCards: nextActiveCards,
        discardPile: nextDiscard,
        roundMessage: nextRoundMessage,
        currentPlayerIndex: nextPlayerIndex,
        subRoundIndex: nextSubRoundIndex,
        firstHandResult: nextFirstHandResult,
      };
    });
  };

  const handleUndoAttempt = () => {
    if (gameHistory.length === 0) return;
    const previous = gameHistory[gameHistory.length - 1];
    setGameState(previous);
    setGameHistory(gameHistory.slice(0, -1));
  };

  useEffect(() => {
    if (gameState.phase === 'ROUND_END') {
      const timer = setTimeout(() => {
        setGameState(prev => {
            const nextStarter = (prev.roundStarterIndex + 1) % 2;
            return {
              ...prev,
              phase: 'DRAWING',
              activeCards: [],
              currentPlayerIndex: nextStarter,
              roundStarterIndex: nextStarter,
              subRoundIndex: 1,
              firstHandResult: null,
            };
        });
      }, 2500); 
      return () => clearTimeout(timer);
    }
  }, [gameState.phase]);


  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const isGameRunning = gameState.phase !== 'START' && gameState.phase !== 'GAME_OVER';
  
  const getTurnLabel = () => {
      if (!isGameRunning) return '';
      const isStarter = gameState.currentPlayerIndex === gameState.roundStarterIndex;
      if (isStarter) return '(Setting)';
      if (gameState.firstHandResult === 'success') return '(Matching)';
      if (gameState.firstHandResult === 'fail') return '(Stealing)';
      return '';
  };
  
  const getSubRoundIndicator = () => {
      if (!isGameRunning) return '';
      return `Round ${gameState.subRoundIndex}/3`;
  };

  // --- Main Render ---

  return (
    <>
    {/* 启动初始化界面覆盖层：加载与退场共用同一实例，进入首页时不重复播放初始化动画 */}
    {(isSessionLoading || isSplashExiting) && (
      <MatrixRainLoader progress={splashProgress} exiting={isSplashExiting} />
    )}

    {!isSessionLoading && (
    <div ref={appShellRef} className={`h-[100dvh] w-full ${isKwcRoute ? 'bg-transparent' : 'bg-[#0a0a0a]'} text-white flex flex-col items-center relative overflow-hidden pt-[var(--safe-top)] pb-[var(--safe-bottom)] ${isSplashExiting ? 'app-enter' : ''}`}>
      <style>{`
        @keyframes home-mode-edit-jiggle {
          0%, 100% { transform: rotate(-1.2deg); }
          50% { transform: rotate(1.2deg); }
        }
      `}</style>
      
      {isKwcRoute ? (
        <div
          aria-hidden="true"
          className="fixed inset-0 pointer-events-none -z-10 bg-[radial-gradient(circle_at_top,rgba(76,29,149,0.28),transparent_32%),linear-gradient(180deg,#05070d_0%,#090b12_45%,#05070d_100%)]"
        />
      ) : (
        <>
          <div className="fixed inset-0 bg-black opacity-20 pointer-events-none -z-10"></div>
          <div className="fixed inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] opacity-20 pointer-events-none -z-10"></div>
        </>
      )}

      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => setIsAuthOpen(false)} 
        onLogin={handleLoginSuccess}
      />

      {toastMessage && (
        <div className="fixed z-[200] top-[calc(1rem_+_var(--safe-top))] left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="animate-in slide-in-from-top-2 fade-in duration-300 rounded-2xl border border-amber-400/30 bg-amber-500/10 backdrop-blur-md px-4 py-2.5 text-center text-sm text-amber-100 shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      {/* 退出登录提示层：盖住整屏，既给用户「正在退出」的反馈，也顺手挡住重复点击。
          文案走 t()，所以这里用 data-i18n-skip 让全局 DOM 翻译器不要重复处理。 */}
      <AnimatePresence>
        {isLoggingOut && (
          <motion.div
            key="logout-overlay"
            data-i18n-skip="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/65 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 420, damping: 34 }}
              role="status"
              aria-live="polite"
              className="mx-6 flex items-center gap-3 rounded-2xl border border-cyan-400/30 bg-[#050b14]/95 px-6 py-4 shadow-[0_0_40px_rgba(34,211,238,0.22)]"
            >
              <Loader2 size={20} strokeWidth={2} className="animate-spin text-cyan-400" />
              <div className="flex flex-col">
                <span className="font-orbitron text-sm uppercase tracking-[0.16em] text-cyan-100">{t('loggingOut')}</span>
                <span className="mt-0.5 font-pingfang text-[11px] text-white/60">{t('loggingOutHint')}</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DeckSelectionModal
        isOpen={isDeckSelectionOpen}
        onClose={() => setIsDeckSelectionOpen(false)}
        decks={currentUser?.customDecks || []}
        onSelectDeck={startCustomGame}
        onManageDecks={() => { 
          if (!currentUser) {
            setIsDeckSelectionOpen(false);
            setIsAuthOpen(true);
          } else {
            setIsDeckSelectionOpen(false); 
            navigate('/editor'); 
          }
        }}
        user={currentUser}
      />

      {/* Header */}
      <header className="hidden w-full shrink-0 items-center justify-between gap-2 border-b border-white/10 bg-black/50 px-2 py-2 backdrop-blur-md md:flex md:p-4 z-50">
        <div className="flex flex-1 items-center gap-1.5 md:gap-4 min-w-0">
            {(location.pathname !== '/') && (
              <button 
                onClick={handleBack}
                className="p-1.5 md:p-2 mr-0.5 md:mr-1 text-white/50 hover:text-white transition-colors border border-transparent hover:border-white/20 rounded-lg group shrink-0"
                aria-label="Back"
              >
                <ArrowLeft size={18} className="group-hover:-translate-x-0.5 transition-transform md:w-5 md:h-5" />
              </button>
            )}
            <img
              src={ktbLogo}
              alt="KTB Logo"
              className="h-7 md:h-10 w-auto object-contain"
            />
            {location.pathname === '/' && (
              <div className="hidden [@media(min-width:1024px)_and_(max-height:860px)]:flex items-center gap-2 rounded-xl border border-cyan-400/20 bg-black/35 px-2.5 py-1.5 shadow-[0_0_18px_rgba(34,211,238,0.08)] backdrop-blur-sm shrink-0">
                <div className="text-[8px] leading-tight font-orbitron uppercase tracking-[0.14em] text-cyan-300/80">
                  <div>Official Partner</div>
                  <div>官方合作伙伴</div>
                </div>
                <img
                  src={ckoPartnerLogo}
                  alt="CKO official partner logo"
                  className="h-6 w-auto object-contain drop-shadow-[0_0_12px_rgba(255,255,255,0.18)]"
                />
                <img
                  src={csckPartnerLogo}
                  alt="CSCK official partner logo"
                  className="h-6 w-auto object-contain drop-shadow-[0_0_12px_rgba(255,255,255,0.18)]"
                />
              </div>
            )}
        </div>
        
        <div className="flex shrink-0 items-center gap-1 md:gap-4">
          <div className="text-neutral-400 font-rajdhani text-xs md:text-base hidden lg:block">
              {location.pathname === '/' 
                ? 'Select Mode' 
                : (gameState.phase === 'START' ? 'Ready to Duel' : `Status: ${gameState.roundMessage}`)
              }
          </div>

          {location.pathname === '/' && (
            <button
              type="button"
              onClick={openHomeAnnouncementModal}
              className="flex items-center gap-1 rounded-md md:rounded-lg border border-amber-400/20 bg-amber-500/10 px-1.5 md:px-2 py-1 text-[10px] md:text-xs text-amber-100 min-w-0 transition-colors hover:border-amber-300/40 hover:bg-amber-400/15"
              aria-label={homeAnnouncementUiText.title}
              title={homeAnnouncementUiText.title}
            >
              <BookOpen size={13} className="shrink-0 md:w-[14px] md:h-[14px]" />
              <span className="hidden md:inline font-orbitron tracking-widest uppercase">{homeAnnouncementUiText.button}</span>
            </button>
          )}

          <label className="flex items-center gap-1 rounded-md md:rounded-lg border border-white/10 bg-white/5 px-1.5 md:px-2 py-1 text-[10px] md:text-xs text-white/80 min-w-0">
            <Globe size={13} className="text-cyan-400 shrink-0 md:w-[14px] md:h-[14px]" />
            <span className="hidden md:inline font-orbitron tracking-widest uppercase">{t('language')}</span>
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value as 'zh' | 'en' | 'ja')}
              className="w-[42px] min-[380px]:w-[48px] md:w-auto bg-transparent text-white outline-none font-rajdhani text-[10px] md:text-xs"
              aria-label={t('language')}
              title={t('language')}
            >
              <option value="zh" className="bg-black text-white">中文</option>
              <option value="en" className="bg-black text-white">English</option>
              <option value="ja" className="bg-black text-white">日本語</option>
            </select>
          </label>

          {/* User Controls */}
          {gameState.phase === 'START' && (
            <div className="flex items-center border-l border-white/10 pl-1 md:pl-4 ml-1 md:ml-2">
              {currentUser ? (
                <div className="flex items-center gap-1 md:gap-3">
                   <button
                      type="button"
                      className="flex h-8 w-8 min-[380px]:h-9 min-[380px]:w-9 md:h-10 md:w-10 items-center justify-center overflow-hidden rounded-full border border-cyan-400/25 bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-zinc-900 shadow-[0_0_18px_rgba(34,211,238,0.12)] transition-transform hover:scale-105"
                      onClick={() => navigate('/profile')}
                      title="User Profile / 用户中心"
                      aria-label="打开用户中心"
                   >
                      {currentUser.avatarUrl ? (
                        <img src={currentUser.avatarUrl} alt="用户头像" className="h-full w-full object-cover" />
                      ) : (
                        <span className="font-orbitron text-xs md:text-sm text-white">{currentUserAvatarFallback}</span>
                      )}
                   </button>
                   <button
                     onClick={openUserCenterMenu}
                     className="text-cyan-300 hover:text-cyan-200 ml-0.5 md:ml-1 shrink-0 transition-colors"
                     title="User Menu / 用户菜单"
                     aria-label="打开用户菜单"
                   >
                     <Menu size={15} className="md:w-4 md:h-4" />
                   </button>
                </div>
              ) : (
                <button 
                  onClick={() => setIsAuthOpen(true)}
                  className="flex items-center gap-1 md:gap-2 text-white hover:text-cyan-400 transition-colors font-orbitron text-[9px] min-[380px]:text-[10px] md:text-sm tracking-[0.12em] md:tracking-widest uppercase border border-white/30 hover:border-cyan-400 px-1.5 min-[380px]:px-2 py-1 md:px-3 md:py-1 rounded-md"
                >
                  <UserIcon size={14} className="md:w-4 md:h-4" />
                  Login
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      {location.pathname === '/' && (
        <div className="absolute left-1/2 top-[66px] z-20 hidden w-fit max-w-[calc(100%-1.5rem)] -translate-x-1/2 md:block md:top-[74px] lg:top-[78px] [@media(min-width:1024px)_and_(max-height:860px)]:hidden">
          <div className="mx-auto flex w-fit max-w-full items-center gap-2 md:gap-3 rounded-2xl border border-cyan-400/20 bg-black/35 px-3 py-2 md:px-4 md:py-2.5 shadow-[0_0_24px_rgba(34,211,238,0.08)] backdrop-blur-sm">
          <div className="text-[8px] sm:text-[9px] md:text-[10px] leading-tight font-orbitron uppercase tracking-[0.14em] md:tracking-[0.18em] text-cyan-300/80">
            <div>Official Partner</div>
            <div>官方合作伙伴</div>
          </div>
          <img
            src={ckoPartnerLogo}
            alt="CKO official partner logo"
            className="h-6 w-auto sm:h-7 md:h-8 object-contain drop-shadow-[0_0_12px_rgba(255,255,255,0.18)]"
          />
          <img
            src={csckPartnerLogo}
            alt="CSCK official partner logo"
            className="h-6 w-auto sm:h-7 md:h-8 object-contain drop-shadow-[0_0_12px_rgba(255,255,255,0.18)]"
          />
        </div>
        </div>
      )}

      {location.pathname === '/' && isWeChatFloatReady && !isSessionLoading && (
        <button
          ref={weChatFloatButtonRef}
          onClick={handleWeChatFloatClick}
          onPointerDown={handleWeChatFloatPointerDown}
          onPointerMove={handleWeChatFloatPointerMove}
          onPointerUp={handleWeChatFloatPointerUp}
          onPointerCancel={handleWeChatFloatPointerUp}
          onPointerLeave={handleWeChatFloatPointerLeave}
          className={`fixed z-[60] touch-none select-none cursor-grab active:cursor-grabbing hover:scale-105 ${isWeChatFloatPressed ? 'opacity-60 scale-95 transition-none' : 'opacity-100 scale-100 transition-all duration-150'}`}
          style={{
            left: `${weChatFloatPosition.x}px`,
            top: `${weChatFloatPosition.y}px`,
            width: `${getFloatingIconSize()}px`,
            height: `${getFloatingIconSize()}px`,
          }}
          aria-label="打开官方微信群"
          title="官方微信群"
        >
          <img
            src={officialWeChatIcon}
            alt="官方微信群入口图标"
            className="h-full w-full object-contain pointer-events-none"
            draggable={false}
          />
        </button>
      )}

      {location.pathname === '/' && isHomeAnnouncementModalOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
          onClick={closeHomeAnnouncementModal}
        >
          <div
            className="relative w-full max-w-xl rounded-3xl border border-amber-400/25 bg-zinc-950/95 p-4 shadow-[0_0_30px_rgba(245,158,11,0.12)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={closeHomeAnnouncementModal}
              className="absolute right-3 top-3 rounded-full border border-white/10 bg-white/5 p-1.5 text-zinc-400 transition-colors hover:border-white/20 hover:text-white"
              aria-label="关闭首页公告"
              title="关闭"
            >
              <XCircle size={18} />
            </button>
            <div className="mb-4 text-center">
              <div className="font-orbitron text-sm uppercase tracking-[0.18em] text-amber-300">{homeAnnouncementUiText.subtitle}</div>
              <div className="mt-1 text-xl font-orbitron text-white">
                {homeAnnouncementConfig.title.trim() || homeAnnouncementUiText.title}
              </div>
              <div className="mt-2 text-xs text-zinc-500 font-rajdhani">
                {hasHomeAnnouncement && homeAnnouncementConfig.updatedAt > 0
                  ? new Date(homeAnnouncementConfig.updatedAt).toLocaleString()
                  : homeAnnouncementUiText.empty}
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto rounded-2xl border border-white/10 bg-black/30 px-4 py-4 text-sm leading-7 text-zinc-200 custom-scrollbar">
              <div className="whitespace-pre-wrap">
                {homeAnnouncementConfig.content.trim() || homeAnnouncementUiText.empty}
              </div>
            </div>
          </div>
        </div>
      )}

      {location.pathname === '/' && isCardIntroOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
          onClick={() => setIsCardIntroOpen(false)}
        >
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setIsCardIntroOpen(false);
            }}
            className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/5 p-2 text-zinc-300 transition-colors hover:border-white/25 hover:text-white"
            aria-label="关闭"
            title="关闭"
          >
            <XCircle size={20} />
          </button>
          <div
            className="flex flex-col items-center"
            onClick={(event) => event.stopPropagation()}
          >
            <FloatingPokerCard scale={1.5} initialY="45vh" revealed={introRevealed} entranceDelay={0.6} />
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 12 }}
              animate={introRevealed ? { opacity: 0, y: 12 } : { opacity: 1, y: 0 }}
              transition={{ delay: introRevealed ? 0 : 0.2, duration: 0.5, ease: 'easeOut' }}
              disabled={introRevealed}
              onClick={() => {
                if (introRevealed) return;
                setIntroRevealed(true);
                checkInToday();
                showToast(dailyCheckInUiText.checkedInSuccess);
              }}
              className={`mt-8 rounded-full bg-cyan-500/90 px-8 py-3 font-pingfang text-base font-bold text-black active:scale-95 ${introRevealed ? 'pointer-events-none' : ''}`}
            >
              {dailyCheckInUiText.revealCardButton}
            </motion.button>
          </div>
        </div>
      )}

      {location.pathname === '/' && isDailyCheckInOpen && (
        <div
          className="fixed inset-0 z-40 flex flex-col items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
          onClick={closeDailyCheckInModal}
        >
          <div
            className="relative w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-3xl border border-cyan-400/25 bg-zinc-950/95 p-6 text-center shadow-[0_0_30px_rgba(34,211,238,0.12)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={closeDailyCheckInModal}
              className="absolute right-3 top-3 rounded-full border border-white/10 bg-white/5 p-1.5 text-zinc-400 transition-colors hover:border-white/20 hover:text-white"
              aria-label="关闭签到"
              title="关闭"
            >
              <XCircle size={18} />
            </button>
            <div className="flex justify-center py-3">
              <button
                type="button"
                onClick={() => setIsCardZoomOpen(true)}
                className="cursor-zoom-in outline-none"
                aria-label="查看卡牌"
              >
                <FloatingPokerCard glow={false} />
              </button>
            </div>
            <p className="font-pingfang text-base leading-7 text-white">{dailyCheckInUiText.modalPrompt}</p>
            {hasCheckedInToday && (
              <p className="mt-3 font-pingfang text-sm font-semibold text-emerald-300">
                {dailyCheckInUiText.alreadyCheckedIn}
              </p>
            )}
            <button
              type="button"
              onClick={handleDailyCheckIn}
              className="mt-6 rounded-full bg-cyan-500/90 px-8 py-3 font-pingfang text-base font-bold text-black active:scale-95"
            >
              {dailyCheckInUiText.confirmButton}
            </button>

            <button
              type="button"
              onClick={() => setIsMakeupCalendarModalOpen(true)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-8 py-2.5 font-pingfang text-sm font-semibold text-amber-200 transition-colors hover:bg-amber-500/20"
            >
              {dailyCheckInUiText.makeupButton}
              <span className="text-amber-300/80">
                {dailyCheckInUiText.chances} × {dailyCheckIn.makeupChances}
              </span>
            </button>
          </div>

          <p
            className="mt-4 font-pingfang text-xs tracking-wide text-zinc-500"
            onClick={(event) => event.stopPropagation()}
          >
            扑克牌由星城剑客的地呱提供设计
          </p>
        </div>
      )}

      {location.pathname === '/' && isCardZoomOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
          onClick={() => setIsCardZoomOpen(false)}
        >
          <div onClick={(event) => event.stopPropagation()}>
            <FloatingPokerCard scale={2} />
          </div>
        </div>
      )}

      {location.pathname === '/' && isMakeupCalendarModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 backdrop-blur-sm"
          onClick={closeMakeupCalendar}
        >
          <div
            className="relative w-full max-w-sm max-h-[90vh] overflow-y-auto rounded-3xl border border-amber-400/25 bg-zinc-950/95 p-6 shadow-[0_0_30px_rgba(251,191,36,0.15)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeMakeupCalendar}
              className="absolute right-4 top-4 text-zinc-500 transition-colors hover:text-white"
              aria-label="close"
            >
              <XCircle size={18} />
            </button>
            <h3 className="mb-4 font-pingfang text-base font-semibold text-amber-200">
              {dailyCheckInUiText.makeupButton}
              <span className="ml-2 text-sm font-normal text-amber-300/80">
                {dailyCheckInUiText.chances} × {dailyCheckIn.makeupChances}
              </span>
            </h3>
            <p className="mb-4 -mt-2 font-pingfang text-xs leading-5 text-zinc-500">
              {dailyCheckInUiText.makeupRule}
            </p>
            {pendingMakeupDate ? (
              <div className="rounded-2xl border border-amber-400/25 bg-amber-500/5 p-4 text-center">
                <p className="font-pingfang text-sm font-semibold text-amber-200">
                  {dailyCheckInUiText.makeupConfirmTitle}
                </p>
                <p className="mt-2 font-orbitron text-lg font-bold text-white">
                  {pendingMakeupDate.replace(/-/g, '.')}
                </p>
                <p className="mt-2 font-pingfang text-xs leading-5 text-zinc-400">
                  {dailyCheckInUiText.makeupConfirmMessage}
                </p>
                <div className="mt-4 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setPendingMakeupDate(null)}
                    className="flex-1 rounded-full border border-white/15 bg-white/5 px-4 py-2 font-pingfang text-sm text-zinc-300 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    {dailyCheckInUiText.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={handleMakeupConfirm}
                    className="flex-1 rounded-full bg-amber-400/90 px-4 py-2 font-pingfang text-sm font-bold text-black transition-colors hover:bg-amber-300"
                  >
                    {dailyCheckInUiText.makeupConfirmOk}
                  </button>
                </div>
              </div>
            ) : (
              <CheckInCalendar
                checkedInDates={dailyCheckIn.checkedInDates}
                makeupChances={dailyCheckIn.makeupChances}
                today={getLocalDateString()}
                onMakeup={handleMakeupDateSelect}
                labels={{
                  weekdays: dailyCheckInUiText.weekdays,
                  makeup: dailyCheckInUiText.makeup,
                }}
              />
            )}
          </div>
        </div>
      )}

      {isWeChatModalOpen && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
          onClick={() => setIsWeChatModalOpen(false)}
        >
          <div
            className="relative w-full max-w-sm rounded-3xl border border-cyan-400/25 bg-zinc-950/95 p-4 shadow-[0_0_30px_rgba(34,211,238,0.12)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              onClick={() => setIsWeChatModalOpen(false)}
              className="absolute right-3 top-3 rounded-full border border-white/10 bg-white/5 p-1.5 text-zinc-400 transition-colors hover:border-white/20 hover:text-white"
              aria-label="关闭官方群二维码"
              title="关闭"
            >
              <XCircle size={18} />
            </button>
            <div className="mb-4 text-center">
              <div className="font-orbitron text-sm uppercase tracking-[0.18em] text-cyan-300">Official WeChat Group</div>
              <div className="mt-1 text-xs text-zinc-400 font-rajdhani">
                {activeOfficialWeChatQrInfo.totalEntries > 0
                  ? `当前显示第 ${activeOfficialWeChatQrInfo.activeIndex + 1} 张活码，${activeOfficialWeChatQrSwitchTimeLabel} 自动切换下一张。`
                  : '扫码查看官方群信息。'}
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white p-2">
              <img
                src={activeOfficialWeChatQrImage}
                alt="官方微信群二维码"
                className="w-full rounded-xl object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {currentUser && isUserCenterMenuOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="关闭用户菜单"
            className="absolute inset-0 h-full w-full bg-black/70 backdrop-blur-sm transition-opacity duration-200"
            style={{ opacity: isUserCenterMenuVisible ? 1 : 0 }}
            onClick={() => closeUserCenterMenu()}
          />
          <aside
            className="absolute bottom-[calc(0.75rem_+_var(--safe-bottom))] right-3 top-[calc(0.75rem_+_var(--safe-top))] flex w-[min(300px,82vw)] flex-col overflow-hidden rounded-3xl border border-cyan-400/20 bg-gradient-to-b from-zinc-950 via-zinc-950/95 to-zinc-950/72 backdrop-blur-md shadow-[-18px_0_40px_rgba(0,0,0,0.45)] transition-[transform,opacity] duration-300 ease-out"
            style={{
              transform: isUserCenterMenuVisible ? 'translateX(0)' : 'translateX(100%)',
              opacity: isUserCenterMenuVisible ? 1 : 0.98,
            }}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-3 py-3.5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-cyan-400/25 bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-zinc-900 text-white shadow-[0_0_18px_rgba(34,211,238,0.12)]">
                  {currentUser.avatarUrl ? (
                    <img src={currentUser.avatarUrl} alt="用户头像" className="h-full w-full object-cover" />
                  ) : (
                    <span className="font-orbitron text-sm">{currentUserAvatarFallback}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-orbitron text-sm uppercase tracking-[0.16em] text-cyan-300">User Center</div>
                  <div className="mt-1 truncate text-sm text-white">{currentUser.displayName}</div>
                  <div className="truncate text-[11px] text-zinc-500">UID {currentUser.uid}</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => closeUserCenterMenu()}
                className="rounded-xl border border-white/10 p-2 text-zinc-300 transition-colors hover:border-cyan-300/40 hover:text-white"
                aria-label="关闭用户菜单"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto bg-gradient-to-b from-transparent to-zinc-950/20 px-2.5 py-3">
              {userCenterMenuItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    if (item.key === 'admin') {
                      closeUserCenterMenu(true);
                      navigate('/admin');
                      return;
                    }

                    if (item.key === 'logout') {
                      void handleLogout();
                      return;
                    }

                    handleOpenUserCenterTab(item.key);
                  }}
                  className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
                    item.key === 'logout'
                      ? 'border-red-500/60 bg-red-950 text-red-100 hover:border-red-300/60 hover:bg-red-900'
                      : item.key === 'admin'
                        ? 'border-amber-500/60 bg-amber-950 text-amber-100 hover:border-amber-300/60 hover:bg-amber-900'
                        : 'border-transparent bg-transparent text-zinc-400 hover:border-zinc-700 hover:bg-zinc-800/80 hover:text-white'
                  }`}
                >
                  <div className="font-orbitron text-sm uppercase tracking-[0.14em]">{item.label}</div>
                  <div data-i18n-skip="true" className="mt-1 font-rajdhani text-[11px] uppercase tracking-[0.12em] text-zinc-400">{item.description}</div>
                </button>
              ))}
            </div>
          </aside>
        </div>
      )}

      <main className={`flex-grow w-full max-w-7xl px-2 py-2 md:px-4 md:py-4 flex flex-col items-center justify-between z-10 min-h-0 relative ${shouldShowMobileBottomNav ? 'pb-[72px]' : ''}`}>
        <Routes>
          <Route path="/" element={
            <>
              <div key={`home-menu-mobile-${language}`} className="relative flex w-full flex-1 flex-col overflow-hidden text-white md:hidden min-h-0">
                <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.12),_transparent_28%),linear-gradient(180deg,_rgba(12,14,20,0.98),_rgba(5,6,10,1))]" />
                <style>{`
                      @keyframes warmupGlitchBar1 {
                        0%, 89%, 100% { opacity: 0; transform: translateX(-100%); }
                        90% { opacity: 1; transform: translateX(0); }
                        92% { opacity: 0; transform: translateX(100%); }
                      }
                      @keyframes warmupGlitchBar2 {
                        0%, 92%, 100% { opacity: 0; transform: translateX(100%); }
                        93% { opacity: 1; transform: translateX(0); }
                        95% { opacity: 0; transform: translateX(-100%); }
                      }
                      @keyframes warmupGlitchBar3 {
                        0%, 94%, 100% { opacity: 0; transform: translateY(-100%); }
                        95% { opacity: 1; transform: translateY(0); }
                        96.5% { opacity: 0; transform: translateY(100%); }
                      }
                      @keyframes warmupGlitchFlash {
                        0%, 89.5%, 100% { opacity: 0; }
                        90% { opacity: 0.35; }
                        90.4% { opacity: 0; }
                        92.8% { opacity: 0.25; }
                        93.1% { opacity: 0; }
                        95% { opacity: 0.3; }
                        95.3% { opacity: 0; }
                      }
                      @keyframes warmupLiteSweep {
                        0% { opacity: 0.08; transform: translate3d(-125%, 0, 0) skewX(-16deg); }
                        20% { opacity: 0.3; }
                        48% { opacity: 0.46; }
                        76% { opacity: 0.2; }
                        100% { opacity: 0.08; transform: translate3d(125%, 0, 0) skewX(-16deg); }
                      }
                      .warmup-glitch,
                      .warmup-glitch-lite {
                        position: relative;
                        transform: translateZ(0);
                        will-change: transform, opacity;
                      }
                      .warmup-glitch::before,
                      .warmup-glitch-lite::before,
                      .warmup-glitch::after,
                      .warmup-glitch-lite::after {
                        content: '';
                        position: absolute;
                        inset: 0;
                        border-radius: 28px;
                        pointer-events: none;
                        z-index: 10;
                        opacity: 0;
                      }
                      .warmup-glitch::before {
                        background: linear-gradient(90deg, transparent 0%, rgba(255,0,255,0.25) 20%, rgba(0,255,255,0.25) 50%, transparent 80%);
                        animation: warmupGlitchFlash 6s ease-in-out infinite;
                      }
                      .warmup-glitch-lite::before {
                        inset: -12% -38%;
                        background: linear-gradient(108deg, transparent 18%, rgba(34,211,238,0.08) 36%, rgba(34,211,238,0.28) 47%, rgba(16,185,129,0.14) 58%, transparent 76%);
                        animation: warmupLiteSweep 4.8s cubic-bezier(0.22, 1, 0.36, 1) infinite;
                        opacity: 0.4;
                      }
                      .warmup-glitch-lite::after {
                        inset: 1px;
                        border: 1px solid rgba(52, 211, 153, 0.22);
                        box-shadow: inset 0 0 24px rgba(16, 185, 129, 0.1), 0 0 18px rgba(34, 211, 238, 0.08);
                        animation: radarPulse 3.6s ease-in-out infinite;
                        opacity: 0.42;
                      }
                      .warmup-glitch .warmup-glitch-slice {
                        position: absolute;
                        pointer-events: none;
                        z-index: 11;
                        border-radius: 4px;
                        opacity: 0;
                      }
                      .warmup-glitch .warmup-glitch-slice:nth-child(1) {
                        top: 20%; left: -4px; right: -4px; height: 6px;
                        background: rgba(0,255,255,0.5);
                        animation: warmupGlitchBar1 6s ease-in-out infinite;
                      }
                      .warmup-glitch .warmup-glitch-slice:nth-child(2) {
                        top: 55%; left: -4px; right: -4px; height: 4px;
                        background: rgba(255,0,128,0.45);
                        animation: warmupGlitchBar2 6s ease-in-out infinite;
                      }
                      .warmup-glitch .warmup-glitch-slice:nth-child(3) {
                        top: 75%; left: -4px; width: 8px; height: 50%;
                        background: rgba(0,255,255,0.35);
                        animation: warmupGlitchBar3 6s ease-in-out infinite;
                      }
                      @keyframes radarSpin {
                        0% { transform: translate(-50%, -50%) rotate(0deg); }
                        100% { transform: translate(-50%, -50%) rotate(360deg); }
                      }
                    @keyframes radarPulse {
                      0%, 100% { opacity: 0.16; }
                      50% { opacity: 0.34; }
                    }
                    @keyframes warmupLiteDrift {
                      0%, 100% { transform: translate(-50%, -50%) scale(0.94); }
                      50% { transform: translate(-50%, -50%) scale(1.02); }
                    }
                      @keyframes radarBlipSweep {
                        0% { opacity: 0; transform: scale(0.3); }
                        4% { opacity: 1; transform: scale(1); }
                        22% { opacity: 0.5; transform: scale(1); }
                        45% { opacity: 0; transform: scale(2); }
                        100% { opacity: 0; transform: scale(3); }
                      }
                      .warmup-radar-blip {
                        position: absolute;
                        width: 5px;
                        height: 5px;
                        border-radius: 50%;
                        background: rgba(0, 255, 100, 0.9);
                        box-shadow: 0 0 6px 2px rgba(0, 255, 100, 0.5), 0 0 12px 4px rgba(0, 255, 100, 0.2);
                        pointer-events: none;
                        opacity: 0;
                        animation: radarBlipSweep 4s ease-out infinite;
                      }
                      .warmup-radar-bg {
                        position: absolute;
                        inset: 0;
                        overflow: hidden;
                        border-radius: 28px;
                        pointer-events: none;
                        z-index: 0;
                        opacity: 0.85;
                      }
                      .warmup-radar-bg-lite {
                        opacity: 0.82;
                      }
                      .warmup-radar-bg::before {
                        content: '';
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        width: 200%;
                        padding-bottom: 200%;
                        background: conic-gradient(
                          from 0deg,
                          transparent 0deg,
                          transparent 328deg,
                          rgba(0, 255, 100, 0.04) 340deg,
                          rgba(0, 255, 100, 0.12) 352deg,
                          rgba(0, 255, 100, 0.22) 358deg,
                          transparent 360deg
                        );
                        animation: radarSpin 4s linear infinite;
                        pointer-events: none;
                      }
                      .warmup-radar-bg-lite::before {
                        width: 132%;
                        padding-bottom: 132%;
                        background: radial-gradient(circle at center, rgba(16,185,129,0.24) 0%, rgba(16,185,129,0.12) 24%, rgba(34,211,238,0.09) 46%, transparent 72%);
                        animation: radarPulse 3.1s ease-in-out infinite, warmupLiteDrift 6.2s ease-in-out infinite;
                        transform: translate(-50%, -50%) scale(0.94);
                        opacity: 0.95;
                      }
                      .warmup-radar-bg::after {
                        content: '';
                        position: absolute;
                        inset: 0;
                        background:
                          radial-gradient(circle at center, transparent 12%, rgba(0,255,100,0.22) 12%, rgba(0,255,100,0.22) 12.5%, transparent 12.5%),
                          radial-gradient(circle at center, transparent 25%, rgba(0,255,100,0.18) 25%, rgba(0,255,100,0.18) 25.5%, transparent 25.5%),
                          radial-gradient(circle at center, transparent 38%, rgba(0,255,100,0.16) 38%, rgba(0,255,100,0.16) 38.5%, transparent 38.5%),
                          radial-gradient(circle at center, transparent 50%, rgba(0,255,100,0.25) 50%, rgba(0,255,100,0.25) 50.5%, transparent 50.5%),
                          radial-gradient(circle at center, transparent 63%, rgba(0,255,100,0.16) 63%, rgba(0,255,100,0.16) 63.5%, transparent 63.5%),
                          radial-gradient(circle at center, transparent 75%, rgba(0,255,100,0.18) 75%, rgba(0,255,100,0.18) 75.5%, transparent 75.5%),
                          radial-gradient(circle at center, transparent 88%, rgba(0,255,100,0.22) 88%, rgba(0,255,100,0.22) 88.5%, transparent 88.5%),
                          repeating-conic-gradient(from 0deg, transparent 0deg, transparent 0.25deg, rgba(0,255,100,0.14) 0.25deg, rgba(0,255,100,0.14) 0.6deg, transparent 0.6deg, transparent 90deg),
                          repeating-conic-gradient(from 0deg, transparent 0deg, transparent 0.15deg, rgba(0,255,100,0.06) 0.15deg, rgba(0,255,100,0.06) 0.3deg, transparent 0.3deg, transparent 30deg);
                        pointer-events: none;
                      }
                      .warmup-radar-bg-lite::after {
                        background:
                          radial-gradient(circle at center, transparent 22%, rgba(16,185,129,0.22) 22.6%, transparent 23.8%),
                          radial-gradient(circle at center, transparent 46%, rgba(34,211,238,0.16) 46.7%, transparent 47.9%),
                          radial-gradient(circle at center, transparent 70%, rgba(16,185,129,0.12) 70.7%, transparent 71.9%);
                        opacity: 0.95;
                        animation: radarPulse 4.2s ease-in-out infinite;
                      }
                      .mobile-launch-shell {
                        min-height: 100%;
                        padding-top: 16px;
                        padding-bottom: 16px;
                        overflow-y: auto;
                        overscroll-behavior-y: contain;
                        -webkit-overflow-scrolling: touch;
                      }
                      @keyframes featuredCardEnterFromRight {
                        0% { opacity: 0; transform: translate3d(18%, 0, 0) scale(0.95); }
                        100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
                      }
                      @keyframes featuredCardEnterFromLeft {
                        0% { opacity: 0; transform: translate3d(-18%, 0, 0) scale(0.95); }
                        100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
                      }
                      @keyframes featuredCardExitToLeft {
                        0% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
                        100% { opacity: 0; transform: translate3d(-14%, 0, 0) scale(0.96); }
                      }
                      @keyframes featuredCardExitToRight {
                        0% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
                        100% { opacity: 0; transform: translate3d(14%, 0, 0) scale(0.96); }
                      }
                      .featured-card-enter-next {
                        animation: featuredCardEnterFromRight 280ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
                      }
                      .featured-card-enter-prev {
                        animation: featuredCardEnterFromLeft 280ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
                      }
                      .featured-card-exit-next {
                        animation: featuredCardExitToLeft 280ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                      }
                      .featured-card-exit-prev {
                        animation: featuredCardExitToRight 280ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
                      }
                    `}</style>

                <div className="mobile-launch-shell flex h-full flex-col px-4">
                  {/* 入场第 1 行：顶部 logo / 公告 / 语言 */}
                  <div className="home-enter-row-1 flex shrink-0 items-center justify-between gap-4">
                    <img
                      src={ktbLogo}
                      alt="KTB Logo"
                      className="h-[clamp(24px,4.8vw,32px)] w-auto object-contain drop-shadow-[0_0_18px_rgba(34,211,238,0.2)]"
                    />
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={openHomeAnnouncementModal}
                        className="flex h-[clamp(42px,7.2vw,48px)] w-[clamp(42px,7.2vw,48px)] shrink-0 flex-col items-center justify-center rounded-full border border-amber-400/20 bg-amber-500/10 text-[10px] font-medium text-amber-100 shadow-[0_0_18px_rgba(245,158,11,0.08)] transition-transform active:scale-95"
                        aria-label={homeAnnouncementUiText.title}
                        title={homeAnnouncementUiText.title}
                      >
                        <span className="text-[11px] leading-none">{mobileHomeUiText.announcementButton}</span>
                        <span className="mt-0.5 text-[9px] uppercase tracking-[0.14em] text-amber-200/75">i</span>
                      </button>
                      <button
                        type="button"
                        onClick={cycleLanguage}
                        className="flex h-[clamp(42px,7.2vw,48px)] w-[clamp(42px,7.2vw,48px)] shrink-0 flex-col items-center justify-center rounded-full border border-cyan-400/20 bg-white/5 text-[10px] font-medium text-cyan-100 shadow-[0_0_18px_rgba(34,211,238,0.08)] transition-transform active:scale-95"
                        aria-label={t('language')}
                        title={t('language')}
                      >
                        <span className="text-[11px] leading-none">{mobileHomeUiText.languageButton}</span>
                        <span className="mt-0.5 text-[9px] uppercase tracking-[0.14em] text-cyan-300/75">{language}</span>
                      </button>
                    </div>
                  </div>

                  {/* 入场第 2 行：合作方 logo */}
                  <div className="home-enter-row-2 mt-[clamp(12px,2.4vh,20px)] flex shrink-0 items-center justify-center gap-[clamp(18px,5vw,28px)]">
                    <img
                      src={ckoPartnerLogo}
                      alt="CKO partner logo"
                      className="h-[clamp(28px,5vh,40px)] w-auto object-contain opacity-95"
                    />
                    <img
                      src={csckPartnerLogo}
                      alt="CSCK partner logo"
                      className="h-[clamp(28px,5vh,40px)] w-auto object-contain opacity-95"
                    />
                  </div>

                  {/* 入场第 3 行：活动卡片 */}
                  <div className="home-enter-row-3 mt-[clamp(12px,2.4vh,24px)] flex min-h-[clamp(200px,32vh,320px)] flex-1">
                    <div
                      className="relative h-full w-full overflow-hidden rounded-[26px]"
                      onPointerDown={featuredCardCount > 1 ? handleFeaturedActivityPointerDown : undefined}
                      onPointerMove={featuredCardCount > 1 ? handleFeaturedActivityPointerMove : undefined}
                      onPointerUp={featuredCardCount > 1 ? handleFeaturedActivityPointerEnd : undefined}
                      onPointerCancel={featuredCardCount > 1 ? handleFeaturedActivityPointerEnd : undefined}
                      style={{ touchAction: featuredCardCount > 1 ? 'pan-y' : 'auto' }}
                    >
                      <div className="relative z-10 h-full px-[14px] py-3">
                        <div className="relative h-full w-full overflow-hidden rounded-[26px]">
                          <div
                            className="flex h-full w-full"
                            style={{
                              transform: `translate3d(-${activeFeaturedCardIndex * 100}%, 0, 0)`,
                              transition: `transform ${FEATURED_CARD_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                              willChange: 'transform',
                            }}
                          >
                            {mobileFeaturedCards.map((card, index) => (
                              <div key={`${card.id}-slide`} className="h-full w-full shrink-0">
                                {renderFeaturedActivityCard(card, {
                                  className: 'h-full w-full',
                                  inert: index !== activeFeaturedCardIndex,
                                  keySuffix: `slide-${index}`
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="pointer-events-none absolute bottom-8 right-8 z-20 flex items-center gap-1.5">
                        {mobileFeaturedCards.map((card, index) => (
                          <button
                            key={`${card.id}-indicator`}
                            type="button"
                            aria-label={`Go to featured card ${index + 1}`}
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (index === activeFeaturedCardIndex) return;
                              featuredActivitySuppressClickRef.current = false;
                              setActiveFeaturedCardIndex(index);
                            }}
                            className={`pointer-events-auto h-1.5 rounded-full transition-all ${index === activeFeaturedCardIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/35'}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 入场第 4 行：万剑阁 + 每日签到 */}
                  <div className="home-enter-row-4 mt-[clamp(14px,2.8vh,24px)] grid shrink-0 grid-cols-2 gap-5 px-[14px]">
                    {/* 万剑阁卡片 */}
                    <button
                      type="button"
                      onClick={() => {
                        if (!currentUser) {
                          promptLogin('请登录后进入万剑阁。');
                          return;
                        }
                        navigate('/kendama-archive');
                      }}
                      className="relative flex h-[clamp(84px,13vh,112px)] flex-col items-center justify-center gap-2 rounded-2xl border border-cyan-400/25 bg-cyan-500/10 px-2 text-center transition-[transform,border-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] active:border-cyan-300/60"
                    >
                      {/* 徽标跨在卡片上边框上，必须有不透明底色，否则 1px 边框会从字后面穿过去 */}
                      <span aria-hidden="true" className="pointer-events-none absolute -right-2 -top-2 z-10 rounded-md border border-amber-400/50 bg-amber-950 px-1.5 py-0.5 font-pingfang text-[9px] font-bold leading-none text-amber-200">
                        {mobileHomeUiText.archiveBadge}
                      </span>
                      <KendamaArchiveIcon
                        strokeWidth={1.4}
                        className="h-[clamp(20px,4vh,28px)] w-[clamp(20px,4vh,28px)] text-cyan-300"
                      />
                      <span className="w-full truncate font-pingfang text-[clamp(12px,3vw,15px)] font-bold leading-tight tracking-[0.04em] text-white">
                        {mobileHomeUiText.archiveHall}
                      </span>
                      <span className="w-full truncate font-pingfang text-[11px] leading-tight text-cyan-100/70">
                        {mobileHomeUiText.archiveSub}
                      </span>
                    </button>

                    {/* 右侧每日签到卡片 */}
                    <button
                      type="button"
                      onClick={openDailyCheckInModal}
                      className={`relative flex h-[clamp(84px,13vh,112px)] flex-col items-center justify-center gap-2 rounded-2xl border px-2 text-center transition-[transform,border-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] ${
                        hasCheckedInToday
                          ? 'border-cyan-400/25 bg-cyan-500/10 active:border-cyan-300/70'
                          : 'border-amber-400/25 bg-amber-500/10 active:border-amber-300/60'
                      }`}
                    >
                      {/* 徽标只在"从未签到"时喊 NEW，之后换成今日状态；其余时候不显示，避免天天挂着变成噪音 */}
                      {!hasEverCheckedIn ? (
                        <span aria-hidden="true" className="pointer-events-none absolute -right-2 -top-2 z-10 rounded-md bg-rose-500 px-1.5 py-0.5 font-pingfang text-[9px] font-bold leading-none text-white shadow-md">
                          NEW
                        </span>
                      ) : hasCheckedInToday ? (
                        <span aria-hidden="true" className="pointer-events-none absolute -right-2 -top-2 z-10 rounded-md border border-cyan-400/50 bg-cyan-950 px-1.5 py-0.5 font-pingfang text-[9px] font-bold leading-none text-cyan-200">
                          {dailyCheckInUiText.cardCheckedIn}
                        </span>
                      ) : null}
                      {/* 未签到用琥珀色（和签到弹窗的主色一致，是"待办"而不是"禁用"），签完转青色 */}
                      {hasCheckedInToday ? (
                        <CalendarCheck
                          strokeWidth={1.4}
                          className="h-[clamp(20px,4vh,28px)] w-[clamp(20px,4vh,28px)] text-cyan-300"
                        />
                      ) : (
                        <Calendar
                          strokeWidth={1.4}
                          className="h-[clamp(20px,4vh,28px)] w-[clamp(20px,4vh,28px)] text-amber-300"
                        />
                      )}
                      <span className={`w-full truncate font-pingfang text-[clamp(12px,3vw,15px)] font-bold leading-tight tracking-[0.04em] ${
                        hasCheckedInToday ? 'text-white' : 'text-amber-50'
                      }`}>
                        {dailyCheckInUiText.cardLabel}
                      </span>
                      <span className={`w-full truncate font-pingfang text-[11px] leading-tight ${
                        hasCheckedInToday ? 'text-cyan-100/70' : 'text-amber-200/75'
                      }`}>
                        {hasCheckedInToday
                          ? `${dailyCheckInUiText.streakPrefix} ${dailyCheckIn.streak} ${dailyCheckInUiText.streakSuffix}`
                          : dailyCheckInUiText.notCheckedIn}
                      </span>
                    </button>
                  </div>

                  {/* 入场第 5 行：抽招赛 / 积分赛 / 自由式 / 联机 */}
                  <div className="home-enter-row-5 mt-[clamp(14px,2.8vh,24px)] grid shrink-0 grid-cols-4 gap-3">
                    {mobileQuickActions.map((item) => (
                      (() => {
                        const mode = homeModes[item.modeId];
                        const Icon = mode.icon;

                        return (
                          <button
                            key={item.key}
                            type="button"
                            onClick={item.action}
                            className="group relative flex h-[clamp(84px,13vh,112px)] flex-col items-center justify-center gap-2 rounded-xl px-1 py-3 text-center transition-all active:scale-95 active:ring-2 active:ring-white/25"
                          >
                            <Icon
                              strokeWidth={1.4}
                              className={`h-[clamp(20px,4vh,28px)] w-[clamp(20px,4vh,28px)] ${mode.activeIconClassName}`}
                            />
                            <span className="font-pingfang text-[clamp(12px,3vw,15px)] font-bold leading-tight tracking-[0.04em] text-white">
                              {item.label}
                            </span>
                            {item.key === 'online' && (
                              <span className="absolute right-0 top-1 rounded bg-amber-500/25 px-1.5 py-0.5 text-[9px] font-pingfang font-medium text-amber-400 leading-none">
                                测试中
                              </span>
                            )}
                          </button>
                        );
                      })()
                    ))}
                  </div>
                </div>


              </div>

            <div key={`home-menu-${language}`} className="relative hidden min-h-full w-full flex-1 flex-col items-center justify-center gap-3 animate-in fade-in duration-500 overflow-y-auto custom-scrollbar px-3 py-4 md:flex md:gap-4 md:pb-14">
              <div className="relative flex w-full flex-col items-center justify-center max-w-md md:max-w-[980px]">
                {isHomeModeLayoutEditing && (
                  <div className="mb-3 flex justify-end gap-2">
                    <button
                      type="button"
                      data-home-layout-reset="true"
                      onClick={handleResetHomeModeLayout}
                      aria-label={homeModeUiText.resetButton}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-zinc-200 transition-colors hover:border-white/20 hover:bg-white/10"
                    >
                      <RotateCcw size={14} />
                    </button>
                    <button
                      type="button"
                      data-home-layout-done="true"
                      onClick={handleFinishHomeModeLayout}
                      className="inline-flex h-10 items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-500/15 px-4 text-xs font-orbitron uppercase tracking-[0.14em] text-cyan-100 transition-colors hover:border-cyan-300/50 hover:bg-cyan-400/20"
                    >
                      {homeModeUiText.doneButton}
                    </button>
                  </div>
                )}

                <div
                  onPointerDownCapture={handleHomeModeCanvasPointerDown}
                  className="mx-auto grid w-full max-w-[328px] grid-cols-2 gap-3 md:max-w-none md:grid-cols-4 md:grid-rows-2 md:auto-rows-[170px] md:gap-4 lg:auto-rows-[190px] lg:gap-5"
                >
                  <style>{`
                    @keyframes warmupGlitchBar1 {
                      0%, 89%, 100% { opacity: 0; transform: translateX(-100%); }
                      90% { opacity: 1; transform: translateX(0); }
                      92% { opacity: 0; transform: translateX(100%); }
                    }
                    @keyframes warmupGlitchBar2 {
                      0%, 92%, 100% { opacity: 0; transform: translateX(100%); }
                      93% { opacity: 1; transform: translateX(0); }
                      95% { opacity: 0; transform: translateX(-100%); }
                    }
                    @keyframes warmupGlitchBar3 {
                      0%, 94%, 100% { opacity: 0; transform: translateY(-100%); }
                      95% { opacity: 1; transform: translateY(0); }
                      96.5% { opacity: 0; transform: translateY(100%); }
                    }
                    @keyframes warmupGlitchFlash {
                      0%, 89.5%, 100% { opacity: 0; }
                      90% { opacity: 0.35; }
                      90.4% { opacity: 0; }
                      92.8% { opacity: 0.25; }
                      93.1% { opacity: 0; }
                      95% { opacity: 0.3; }
                      95.3% { opacity: 0; }
                    }
                    @keyframes warmupLiteSweep {
                      0% { opacity: 0.08; transform: translate3d(-125%, 0, 0) skewX(-16deg); }
                      20% { opacity: 0.3; }
                      48% { opacity: 0.46; }
                      76% { opacity: 0.2; }
                      100% { opacity: 0.08; transform: translate3d(125%, 0, 0) skewX(-16deg); }
                    }
                    .warmup-glitch,
                    .warmup-glitch-lite {
                      position: relative;
                      transform: translateZ(0);
                      will-change: transform, opacity;
                    }
                    .warmup-glitch::before,
                    .warmup-glitch-lite::before,
                    .warmup-glitch::after,
                    .warmup-glitch-lite::after {
                      content: '';
                      position: absolute;
                      inset: 0;
                      border-radius: 28px;
                      pointer-events: none;
                      z-index: 10;
                      opacity: 0;
                    }
                    .warmup-glitch::before {
                      background: linear-gradient(90deg, transparent 0%, rgba(255,0,255,0.25) 20%, rgba(0,255,255,0.25) 50%, transparent 80%);
                      animation: warmupGlitchFlash 6s ease-in-out infinite;
                    }
                    .warmup-glitch-lite::before {
                      inset: -12% -38%;
                      background: linear-gradient(108deg, transparent 18%, rgba(34,211,238,0.08) 36%, rgba(34,211,238,0.28) 47%, rgba(16,185,129,0.14) 58%, transparent 76%);
                      animation: warmupLiteSweep 4.8s cubic-bezier(0.22, 1, 0.36, 1) infinite;
                      opacity: 0.4;
                    }
                    .warmup-glitch-lite::after {
                      inset: 1px;
                      border: 1px solid rgba(52, 211, 153, 0.22);
                      box-shadow: inset 0 0 24px rgba(16, 185, 129, 0.1), 0 0 18px rgba(34, 211, 238, 0.08);
                      animation: radarPulse 3.6s ease-in-out infinite;
                      opacity: 0.42;
                    }
                    .warmup-glitch .warmup-glitch-slice {
                      position: absolute;
                      pointer-events: none;
                      z-index: 11;
                      border-radius: 4px;
                      opacity: 0;
                    }
                    .warmup-glitch .warmup-glitch-slice:nth-child(1) {
                      top: 20%; left: -4px; right: -4px; height: 6px;
                      background: rgba(0,255,255,0.5);
                      animation: warmupGlitchBar1 6s ease-in-out infinite;
                    }
                    .warmup-glitch .warmup-glitch-slice:nth-child(2) {
                      top: 55%; left: -4px; right: -4px; height: 4px;
                      background: rgba(255,0,128,0.45);
                      animation: warmupGlitchBar2 6s ease-in-out infinite;
                    }
                    .warmup-glitch .warmup-glitch-slice:nth-child(3) {
                      top: 75%; left: -4px; width: 8px; height: 50%;
                      background: rgba(0,255,255,0.35);
                      animation: warmupGlitchBar3 6s ease-in-out infinite;
                    }
                    @keyframes radarSpin {
                      0% { transform: translate(-50%, -50%) rotate(0deg); }
                      100% { transform: translate(-50%, -50%) rotate(360deg); }
                    }
                    @keyframes radarPulse {
                      0%, 100% { opacity: 0.16; }
                      50% { opacity: 0.34; }
                    }
                    @keyframes warmupLiteDrift {
                      0%, 100% { transform: translate(-50%, -50%) scale(0.94); }
                      50% { transform: translate(-50%, -50%) scale(1.02); }
                    }
                    @keyframes radarBlipSweep {
                      0% { opacity: 0; transform: scale(0.3); }
                      4% { opacity: 1; transform: scale(1); }
                      22% { opacity: 0.5; transform: scale(1); }
                      45% { opacity: 0; transform: scale(2); }
                      100% { opacity: 0; transform: scale(3); }
                    }
                    .warmup-radar-blip {
                      position: absolute;
                      width: 5px;
                      height: 5px;
                      border-radius: 50%;
                      background: rgba(0, 255, 100, 0.9);
                      box-shadow: 0 0 6px 2px rgba(0, 255, 100, 0.5), 0 0 12px 4px rgba(0, 255, 100, 0.2);
                      pointer-events: none;
                      opacity: 0;
                      animation: radarBlipSweep 4s ease-out infinite;
                    }
                    .warmup-radar-bg {
                      position: absolute;
                      inset: 0;
                      overflow: hidden;
                      border-radius: 28px;
                      pointer-events: none;
                      z-index: 0;
                      opacity: 0.85;
                    }
                    .warmup-radar-bg-lite {
                      opacity: 0.82;
                    }
                    .warmup-radar-bg::before {
                      content: '';
                      position: absolute;
                      top: 50%;
                      left: 50%;
                      width: 200%;
                      padding-bottom: 200%;
                      background: conic-gradient(
                        from 0deg,
                        transparent 0deg,
                        transparent 328deg,
                        rgba(0, 255, 100, 0.04) 340deg,
                        rgba(0, 255, 100, 0.12) 352deg,
                        rgba(0, 255, 100, 0.22) 358deg,
                        transparent 360deg
                      );
                      animation: radarSpin 4s linear infinite;
                      pointer-events: none;
                    }
                    .warmup-radar-bg-lite::before {
                      width: 132%;
                      padding-bottom: 132%;
                      background: radial-gradient(circle at center, rgba(16,185,129,0.24) 0%, rgba(16,185,129,0.12) 24%, rgba(34,211,238,0.09) 46%, transparent 72%);
                      animation: radarPulse 3.1s ease-in-out infinite, warmupLiteDrift 6.2s ease-in-out infinite;
                      transform: translate(-50%, -50%) scale(0.94);
                      opacity: 0.95;
                    }
                    .warmup-radar-bg::after {
                      content: '';
                      position: absolute;
                      inset: 0;
                      background:
                        radial-gradient(circle at center, transparent 12%, rgba(0,255,100,0.22) 12%, rgba(0,255,100,0.22) 12.5%, transparent 12.5%),
                        radial-gradient(circle at center, transparent 25%, rgba(0,255,100,0.18) 25%, rgba(0,255,100,0.18) 25.5%, transparent 25.5%),
                        radial-gradient(circle at center, transparent 38%, rgba(0,255,100,0.16) 38%, rgba(0,255,100,0.16) 38.5%, transparent 38.5%),
                        radial-gradient(circle at center, transparent 50%, rgba(0,255,100,0.25) 50%, rgba(0,255,100,0.25) 50.5%, transparent 50.5%),
                        radial-gradient(circle at center, transparent 63%, rgba(0,255,100,0.16) 63%, rgba(0,255,100,0.16) 63.5%, transparent 63.5%),
                        radial-gradient(circle at center, transparent 75%, rgba(0,255,100,0.18) 75%, rgba(0,255,100,0.18) 75.5%, transparent 75.5%),
                        radial-gradient(circle at center, transparent 88%, rgba(0,255,100,0.22) 88%, rgba(0,255,100,0.22) 88.5%, transparent 88.5%),
                        repeating-conic-gradient(from 0deg, transparent 0deg, transparent 0.25deg, rgba(0,255,100,0.14) 0.25deg, rgba(0,255,100,0.14) 0.6deg, transparent 0.6deg, transparent 90deg),
                        repeating-conic-gradient(from 0deg, transparent 0deg, transparent 0.15deg, rgba(0,255,100,0.06) 0.15deg, rgba(0,255,100,0.06) 0.3deg, transparent 0.3deg, transparent 30deg);
                      pointer-events: none;
                    }
                    .warmup-radar-bg-lite::after {
                      background:
                        radial-gradient(circle at center, transparent 22%, rgba(16,185,129,0.22) 22.6%, transparent 23.8%),
                        radial-gradient(circle at center, transparent 46%, rgba(34,211,238,0.16) 46.7%, transparent 47.9%),
                        radial-gradient(circle at center, transparent 70%, rgba(16,185,129,0.12) 70.7%, transparent 71.9%);
                      opacity: 0.95;
                      animation: radarPulse 4.2s ease-in-out infinite;
                    }
                  `}</style>
                  {homeModeLayout.map((modeId, slotIndex) => {
                    const mode = homeModes[modeId];
                    const Icon = mode.icon;
                    const isTall = mode.variant === 'tall';
                    const localizedCopy = mode.copy[language as AppLanguage];
                    const isDragging = draggingHomeModeId === mode.id;
                    const isDropTarget = !!draggingHomeModeId && dragHoverHomeModeId === mode.id && draggingHomeModeId !== mode.id;

                    return (
                      <button
                        key={`${slotIndex}-${mode.id}-${language}`}
                        type="button"
                        data-home-mode-id={mode.id}
                        aria-label={localizedCopy.titleLines.join(' ')}
                        title={localizedCopy.titleLines.join(' ')}
                        onClick={() => handleHomeModeCardClick(mode.action, mode.disabled)}
                        onPointerDown={(event) => handleHomeModePointerDown(mode.id, event)}
                        onPointerMove={handleHomeModePointerMove}
                        onPointerUp={handleHomeModePointerCancel}
                        onPointerCancel={handleHomeModePointerCancel}
                        className={`group relative w-full rounded-[28px] border border-zinc-800 bg-black/40 text-white backdrop-blur-sm transition-all duration-300 ${HOME_MODE_MOBILE_SLOT_CLASSES[slotIndex]} ${HOME_MODE_SLOT_CLASSES[slotIndex]} ${
                          'aspect-square md:h-full md:aspect-auto'
                        } ${localizedCopy.badge ? 'overflow-visible' : 'overflow-hidden'} ${
                          isDragging || isDropTarget
                            ? mode.activeAccentClassName
                            : isHomeModeLayoutEditing
                              ? 'hover:border-white/30 hover:bg-white/[0.06]'
                              : mode.disabled
                                ? 'opacity-80'
                                : mode.accentClassName
                        } ${
                          isHomeModeLayoutEditing ? 'cursor-grab active:cursor-grabbing touch-none select-none' : mode.disabled ? 'cursor-default' : 'cursor-pointer'
                        }`}
                        aria-pressed={isHomeModeLayoutEditing && isDragging}
                        style={isDragging ? {
                          transform: `translate3d(${draggingHomeModeOffset.x}px, ${draggingHomeModeOffset.y}px, 0) scale(1.04)`,
                          zIndex: 40,
                          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.48), 0 0 0 1px rgba(255, 255, 255, 0.08)',
                          pointerEvents: 'none',
                          transitionDuration: '0ms',
                        } : isHomeModeLayoutEditing ? {
                          animation: `home-mode-edit-jiggle ${1.9 + (slotIndex % 3) * 0.15}s ease-in-out infinite`,
                        } : undefined}
                      >
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.08),_transparent_55%)] opacity-60"></div>

                        {localizedCopy.badge && !isHomeModeLayoutEditing && (
                          <div className={`absolute -right-2.5 -top-2.5 z-10 rounded-full border px-3 py-1 font-orbitron text-[10px] uppercase tracking-[0.14em] shadow-lg ${localizedCopy.badgeClassName || 'border-cyan-400/40 bg-cyan-500/15 text-cyan-200 shadow-cyan-500/10'}`}>
                            {localizedCopy.badge}
                          </div>
                        )}

                        {mode.disabled && !isHomeModeLayoutEditing && (
                          <div className="absolute right-3 top-3 z-10 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1 font-orbitron text-[9px] uppercase tracking-[0.14em] text-emerald-200">
                            {homeModeUiText.comingSoon}
                          </div>
                        )}

                        <div className="relative z-[1] flex h-full w-full flex-col items-center justify-center p-3 text-center md:p-5 lg:p-6 md:flex-col md:justify-center md:text-center">
                          <Icon
                            strokeWidth={1}
                            className={`shrink-0 transition-all duration-300 group-hover:scale-110 ${
                              `mb-2 h-11 w-11 md:mb-6 ${isTall ? 'md:h-20 md:w-20 lg:h-24 lg:w-24' : 'md:h-16 md:w-16 lg:h-20 lg:w-20'} ${isDragging || isDropTarget ? mode.activeIconClassName : mode.iconClassName}`
                            }`}
                          />

                          <div className="flex flex-col items-center text-center">
                            <h2 className={`flex flex-col font-orbitron font-bold uppercase tracking-[0.14em] text-white ${
                              isTall
                                ? 'items-center text-[1.15rem] leading-tight md:text-[1.7rem] lg:text-[2.1rem]'
                                : 'items-center text-[1.15rem] leading-tight md:text-[2rem] lg:text-[2.3rem]'
                            }`}>
                              {localizedCopy.titleLines.map((line, index) => (
                                <span key={`${mode.id}-${language}-${index}`} className={index > 0 ? 'mt-0.5' : ''}>
                                  {line}
                                </span>
                              ))}
                            </h2>

                            <p data-i18n-skip="true" className={`mt-1 font-rajdhani tracking-wider text-zinc-500 transition-colors ${
                              'text-[11px] leading-snug md:mt-2 md:text-sm'
                            } ${
                              isDragging || isDropTarget ? 'text-white/75' : mode.descriptionActiveClassName
                            }`}>
                              {localizedCopy.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* KWC模拟决赛：试运行阶段的活动卡片，只对管理员显示 */}
                {canAccessWarmupAdmin(currentUser) && !isHomeModeLayoutEditing && (
                  <button
                    type="button"
                    onClick={handleKwc2026PointNavigation}
                    aria-label="KWC 模拟决赛"
                    className="group relative mt-3 flex w-full items-center gap-3 overflow-hidden rounded-[24px] border border-amber-400/35 bg-[linear-gradient(120deg,rgba(38,24,8,0.9),rgba(14,12,20,0.9),rgba(30,20,6,0.9))] px-4 py-3 text-left text-white shadow-[0_0_28px_rgba(245,158,11,0.12)] transition-all duration-300 hover:border-amber-300/60 md:mt-4 md:gap-5 md:px-6 md:py-4"
                  >
                    <div
                      className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-40"
                      style={{ backgroundImage: `url(${kwcLogoBackground})` }}
                      aria-hidden="true"
                    />
                    <div
                      className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(12,10,6,0.86),rgba(10,10,16,0.72),rgba(24,16,4,0.82))]"
                      aria-hidden="true"
                    />
                    <span className="relative z-[1] flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-amber-300/40 bg-amber-500/15 text-amber-200 md:h-14 md:w-14">
                      <Trophy className="h-5 w-5 md:h-7 md:w-7" strokeWidth={1.6} />
                    </span>
                    <span className="relative z-[1] flex min-w-0 flex-1 flex-col">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-orbitron text-base font-bold uppercase tracking-[0.14em] text-amber-50 md:text-xl">KWC 模拟决赛</span>
                        <span className="rounded-full border border-amber-400/40 bg-amber-500/15 px-2 py-0.5 font-pingfang text-[10px] text-amber-100">管理员</span>
                      </span>
                      <span className="mt-1 font-pingfang text-[11px] leading-snug tracking-wide text-amber-100/70 md:text-sm">
                        选招式 · 3 分钟录像投稿 · 按 KWC2026 决赛规则算分 · 排行榜
                      </span>
                    </span>
                    <span className="relative z-[1] hidden shrink-0 font-orbitron text-[11px] uppercase tracking-[0.18em] text-amber-200/85 transition-transform group-hover:translate-x-0.5 md:block">
                      进入
                    </span>
                  </button>
                )}
              </div>
              <div className="pointer-events-none absolute bottom-1 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-center text-xs text-zinc-500">
                <div className="font-pingfang tracking-wide">湘ICP备2026026323号-1</div>
                <div className="flex items-center gap-2 font-pingfang tracking-wide">
                  <img src={beianLogo} alt="公安备案" className="h-4 w-4 object-contain opacity-80" />
                  <span>湘公网安备43010302002338号</span>
                </div>
              </div>
            </div>
            </>
          } />

          <Route path="/online" element={
            <ProtectedRoute
              allow={!!currentUser && !isOnlineAccessBlocked}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {
                if (currentUser && isOnlineAccessBlocked) {
                  redirectToAccessDenied('Online Mode is restricted to admins. / 联机模式仅限管理员进入。');
                  navigate('/');
                  return;
                }
                redirectToLogin('Please Login to access Online Mode. / 请登录以访问线上模式。');
              }}
            >
              <GameLobby 
                user={currentUser!} 
                onLogout={() => {}} 
                onBackToMain={() => navigate('/')} 
                onRoomActiveChange={setOnlineRoomActive}
              />
            </ProtectedRoute>
          } />

          <Route path="/battle-mode" element={
            <BattleModeSelection 
              onSelectMode={(mode, health) => {
                if (mode === 'FREE') {
                  startGame('FREE_BATTLE', 'FREE', health);
                  navigate('/game');
                } else {
                  setGameState(prev => ({ ...prev, customHealth: health }));
                  handleCustomClick();
                }
              }}
              onBack={() => navigate('/')} 
            />
          } />

          <Route path="/game" element={
            <>
              <button
                type="button"
                onClick={handleBack}
                className="self-start mb-1 md:hidden flex h-9 items-center gap-1.5 rounded-full border border-white/15 bg-black/50 px-3 text-white/70 backdrop-blur-sm active:scale-95"
                aria-label="返回"
              >
                <ArrowLeft size={16} />
                <span className="text-xs">返回</span>
              </button>
              <div className="w-full flex flex-row justify-between items-stretch gap-2 shrink-0 mb-1 md:mb-4">
                  <div className="flex-1 flex flex-col items-end justify-start">
                      <div className="flex flex-col items-center w-full sm:w-auto">
                          <PlayerPanel 
                            player={gameState.players[0]} 
                            isActive={isGameRunning && gameState.currentPlayerIndex === 0} 
                            maxHealth={gameState.customHealth || WINNING_SCORE}
                          />
                          {gameState.firstHandResult && isGameRunning && gameState.roundStarterIndex === 0 && (
                          <div className="mt-1 md:mt-2 animate-pulse pointer-events-none z-40">
                              <div className={`
                                  px-2 py-0.5 md:px-4 md:py-1 bg-black/80 backdrop-blur-md text-white font-orbitron font-bold tracking-widest border rounded-full shadow-lg flex items-center gap-2 text-[10px] md:text-sm whitespace-nowrap
                                  ${gameState.firstHandResult === 'success' ? 'border-green-500 text-green-400' : 'border-red-500 text-red-400'}
                              `}>
                                  {gameState.firstHandResult.toUpperCase()}
                              </div>
                          </div>
                          )}
                      </div>
                  </div>
                  
                  <div className="flex flex-col items-center justify-center min-w-[60px] md:mx-32">
                    {isGameRunning && (
                        <div className="hidden md:block text-sm text-yellow-500 font-orbitron tracking-widest mb-1 animate-pulse">
                            {getSubRoundIndicator()}
                        </div>
                    )}
                    <div className="hidden md:block text-sm text-cyan-500 font-orbitron tracking-[0.3em] mb-2 drop-shadow-[0_0_5px_rgba(6,182,212,0.8)]">
                      {gameState.battleMode === 'FREE' ? 'FREE' : 'TRICKS'}
                    </div>
                    <div className="text-2xl md:text-7xl lg:text-8xl font-black font-orbitron text-transparent bg-clip-text bg-gradient-to-b from-white to-neutral-500 drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
                        {gameState.phase === 'START' ? '-' : (gameState.battleMode === 'FREE' ? '∞' : gameState.deck.length)}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col items-start justify-start">
                      <div className="flex flex-col items-center w-full sm:w-auto">
                          <PlayerPanel 
                            player={gameState.players[1]} 
                            isActive={isGameRunning && gameState.currentPlayerIndex === 1} 
                            maxHealth={gameState.customHealth || WINNING_SCORE}
                          />
                          {gameState.firstHandResult && isGameRunning && gameState.roundStarterIndex === 1 && (
                          <div className="mt-1 md:mt-2 animate-pulse pointer-events-none z-40">
                              <div className={`
                                  px-2 py-0.5 md:px-4 md:py-1 bg-black/80 backdrop-blur-md text-white font-orbitron font-bold tracking-widest border rounded-full shadow-lg flex items-center gap-2 text-[10px] md:text-sm whitespace-nowrap
                                  ${gameState.firstHandResult === 'success' ? 'border-green-500 text-green-400' : 'border-red-500 text-red-400'}
                              `}>
                                  {gameState.firstHandResult.toUpperCase()}
                              </div>
                          </div>
                          )}
                      </div>
                  </div>
              </div>

              {isGameRunning && (
                  <div className="md:hidden w-full flex flex-col items-center justify-center mt-4 mb-2 shrink-0 space-y-1">
                      <div className="text-xs text-yellow-500 font-orbitron tracking-widest animate-pulse">
                          {getSubRoundIndicator()}
                      </div>
                      <span className={`text-sm font-orbitron font-bold uppercase tracking-widest ${currentPlayer.id === 0 ? 'text-red-500' : 'text-blue-500'}`}>
                          {currentPlayer.name} {getTurnLabel()}
                      </span>
                  </div>
              )}

              <div className="flex-grow flex flex-col justify-center items-center w-full min-h-0 relative">
                {gameState.phase === 'START' && (
                  <div className="flex-col items-center gap-6 w-full px-6 md:px-0 flex">
                    <h2 className="text-xl md:text-3xl font-orbitron tracking-widest text-white/80 uppercase mb-4">Select Deck</h2>
                    
                      <button onClick={handleCustomClick} className="group flex items-center gap-6 focus:outline-none animate-in fade-in slide-in-from-bottom-4 duration-500">
                          <LayoutGrid className="w-8 h-8 md:w-10 md:h-10 text-purple-400 opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300" />
                          <div className="relative px-8 py-4 bg-transparent border border-purple-500/50 group-hover:border-purple-500 transition-colors rounded-xl overflow-hidden min-w-[200px]">
                              <div className="absolute inset-0 w-0 bg-purple-500/20 transition-all duration-300 ease-out group-hover:w-full"></div>
                              <div className="relative flex flex-col items-center text-center">
                                  <span className="text-xl md:text-2xl font-orbitron font-bold tracking-widest uppercase text-purple-100 leading-none mb-1">自定义</span>
                                  <span className="text-xs md:text-sm font-rajdhani text-purple-400/70 tracking-[0.2em] uppercase">Custom</span>
                              </div>
                          </div>
                      </button>

                    <div className="mt-8 text-[10px] md:text-xs text-neutral-600 font-orbitron tracking-widest opacity-50">powered by @bug kcsc</div>
                  </div>
                )}

                {/* GAME_OVER 也保留这一屏：赢的那一拍里，最后一手和血条要还在场上 */}
                {(gameState.phase === 'DRAWING' || gameState.phase === 'PLAYING' || gameState.phase === 'ROUND_END' || gameState.phase === 'GAME_OVER') && (
                  <div className="flex flex-row items-center justify-center gap-1 md:gap-8 perspective-1000 w-full px-1 h-full">
                    {gameState.battleMode === 'FREE' ? (
                      <div className="flex flex-col items-center justify-center gap-2">
                        <div className="text-4xl md:text-6xl font-black font-orbitron text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-600 drop-shadow-[0_0_15px_rgba(168,85,247,0.5)] uppercase tracking-widest text-center">
                           FREE TRICK
                        </div>
                        <div className="text-xl md:text-2xl text-purple-400/80 font-rajdhani tracking-widest">自由出招</div>
                        {gameState.phase === 'ROUND_END' && <div className="text-lg md:text-2xl font-rajdhani animate-pulse text-neutral-500 mt-4">Round Ended...</div>}
                      </div>
                    ) : (
                      <div className="w-full px-2 md:px-4 flex flex-col items-center justify-center gap-4">
                        {(gameState.phase === 'DRAWING' || gameState.activeCards.length > 0) && (
                          <TrickSlotMachine 
                            trickName={gameState.activeCards.length > 0 ? gameState.activeCards[0].name : "DRAWING..."} 
                            isDrawing={gameState.phase === 'DRAWING'} 
                            deckTricks={gameState.deck.length > 0 ? gameState.deck.map(c => c.name) : (gameState.activeCards.length > 0 ? [gameState.activeCards[0].name] : ["DRAWING..."])}
                          />
                        )}
                        {gameState.activeCards.length === 0 && gameState.phase === 'ROUND_END' && <div className="text-lg md:text-2xl font-rajdhani animate-pulse text-neutral-500 mt-4">Round Ended...</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="w-full max-w-md mt-2 md:mt-4 z-30 mb-4 md:mb-8 px-4 shrink-0">
                  {gameState.phase === 'PLAYING' ? (
                      <div className="flex space-x-3">
                          <button onClick={() => handleAttempt(false)} className="flex-1 py-3 md:py-4 bg-neutral-900/80 border border-neutral-700 text-neutral-400 font-orbitron hover:bg-red-900/30 hover:text-red-500 hover:border-red-500 transition-all rounded-lg flex flex-col items-center gap-1 backdrop-blur-sm">
                              <XCircle size={18} className="md:w-6 md:h-6" />
                              <span className="text-[10px] md:text-sm tracking-widest uppercase">Fail</span>
                          </button>
                          <button onClick={() => handleAttempt(true)} className={`flex-1 py-3 md:py-4 bg-neutral-900/80 border text-white font-orbitron transition-all rounded-lg flex flex-col items-center gap-1 backdrop-blur-sm ${currentPlayer.id === 0 ? 'border-red-500 hover:bg-red-500 hover:text-black shadow-[0_0_15px_rgba(239,68,68,0.3)]' : 'border-blue-500 hover:bg-blue-500 hover:text-black shadow-[0_0_15px_rgba(59,130,246,0.3)]'}`}>
                              <CheckCircle2 size={18} className="md:w-6 md:h-6" />
                              <span className="text-[10px] md:text-sm tracking-widest uppercase">Success</span>
                          </button>
                      </div>
                  ) : null}
                  {(gameState.phase === 'PLAYING' || gameState.phase === 'ROUND_END') && gameHistory.length > 0 && (
                      <button onClick={handleUndoAttempt} className="mt-3 w-full py-2 bg-neutral-900/70 border border-neutral-700 text-neutral-400 hover:bg-neutral-800 hover:text-white font-orbitron tracking-widest uppercase rounded-lg flex items-center justify-center gap-2 transition-colors text-xs md:text-sm">
                          <Undo2 size={14} className="md:w-4 md:h-4" />
                          <span>撤回 / Undo</span>
                      </button>
                  )}
                  {gameState.phase === 'ROUND_END' && <div className="w-full text-center py-2 text-neutral-400 font-orbitron tracking-widest animate-pulse text-xs md:text-base">ROTATING STARTER...</div>}
              </div>
            </>
          } />

          <Route path="/editor" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to manage custom decks. / 请登录后管理自定义招式表。')}
            >
              <div className="h-full w-full flex flex-col relative overflow-hidden">
                <CustomDeckEditor 
                  user={currentUser!} 
                  onUpdateUser={handleUpdateUser} 
                  onBack={() => navigate('/')} 
                />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/tournament" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Tournament Mode. / 请登录以访问锦标赛模式。')}
            >
              <div className="h-full w-full flex flex-col items-center justify-center relative overflow-hidden p-4">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="absolute left-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/50 text-white/70 backdrop-blur-sm active:scale-95 md:hidden"
                  aria-label="返回"
                >
                  <ArrowLeft size={18} />
                </button>
                <div className="absolute inset-0 pointer-events-none opacity-20 [background-image:radial-gradient(rgba(255,255,255,0.18)_0.8px,transparent_0.8px)] [background-size:12px_12px]"></div>

                <div className="relative mb-10 w-full max-w-3xl overflow-hidden rounded-3xl border border-yellow-500/15 bg-[linear-gradient(135deg,rgba(234,179,8,0.14),rgba(0,0,0,0.08),rgba(59,130,246,0.08))] px-5 py-6 md:px-7 md:py-7 shadow-[0_0_40px_rgba(234,179,8,0.08)]">
                  <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-yellow-500/10 blur-3xl"></div>
                  <div className="absolute -left-8 bottom-0 h-24 w-24 rounded-full bg-blue-500/10 blur-3xl"></div>

                  <h1 className="relative text-3xl md:text-5xl font-orbitron font-black text-yellow-500 tracking-widest text-center neon-glow-blue">
                      POINTS TOURNAMENT<br/>
                      <span className="text-xl md:text-3xl text-yellow-500/80">积分锦标赛</span>
                  </h1>

                  <p className="relative mt-4 text-center text-sm md:text-base text-zinc-400 font-rajdhani leading-relaxed">
                    Complete trick selection in the tournament settings, then tap Start to choose the round you want to play.
                    <br />
                    在积分赛设置中完成招式选择，完成后点击开始选择所要进行的比赛轮次
                  </p>
                </div>
                
                <div className="flex flex-col gap-6 w-full max-w-md">
                    <button 
                        onClick={() => navigate('/tournament/game')}
                        className="group relative overflow-hidden p-6 rounded-2xl bg-zinc-900 border border-zinc-700 hover:border-yellow-500 transition-all flex items-center gap-6"
                    >
                        <div className="p-4 bg-zinc-800 rounded-full group-hover:bg-yellow-600 group-hover:text-black transition-colors">
                            <Play size={32} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-2xl font-orbitron font-bold uppercase group-hover:text-yellow-400">Play / 开始</span>
                            <span data-i18n-skip="true" className="text-sm text-zinc-500 font-rajdhani">Start Round Timer</span>
                        </div>
                    </button>

                    <button 
                        onClick={() => alert('现在为非比赛时间')}
                        className="group relative overflow-hidden p-6 rounded-2xl bg-zinc-900 border border-zinc-700 hover:border-cyan-500 transition-all flex items-center gap-6"
                    >
                        <div className="p-4 bg-zinc-800 rounded-full group-hover:bg-cyan-600 group-hover:text-black transition-colors">
                            <UploadCloud size={32} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-2xl font-orbitron font-bold uppercase group-hover:text-cyan-400">Final Submit / 决赛提交</span>
                            <span data-i18n-skip="true" className="text-sm text-zinc-500 font-rajdhani">Build &amp; submit a separate finals trick list</span>
                        </div>
                    </button>
                    
                    <button 
                        onClick={() => navigate('/tournament/editor')}
                        className="group relative overflow-hidden p-6 rounded-2xl bg-zinc-900 border border-zinc-700 hover:border-blue-500 transition-all flex items-center gap-6"
                    >
                        <div className="p-4 bg-zinc-800 rounded-full group-hover:bg-blue-600 group-hover:text-black transition-colors">
                            <Settings size={32} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-2xl font-orbitron font-bold uppercase group-hover:text-blue-400">{language === 'en' ? 'Setup / Trick Settings' : language === 'ja' ? 'Setup / 技設定' : 'Setup / 招式设置'}</span>
                            <span data-i18n-skip="true" className="text-sm text-zinc-500 font-rajdhani">Configure Tricks &amp; Rounds</span>
                        </div>
                    </button>

                    <button 
                        onClick={() => navigate('/tournament/history')}
                        className="group relative overflow-hidden p-6 rounded-2xl bg-zinc-900 border border-zinc-700 hover:border-green-500 transition-all flex items-center gap-6"
                    >
                        <div className="p-4 bg-zinc-800 rounded-full group-hover:bg-green-600 group-hover:text-black transition-colors">
                            <History size={32} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-2xl font-orbitron font-bold uppercase group-hover:text-green-400">History / 历史</span>
                            <span data-i18n-skip="true" className="text-sm text-zinc-500 font-rajdhani">View Past Results</span>
                        </div>
                    </button>
                </div>
              </div>
            </ProtectedRoute>
          } />

          <Route path="/tournament/game" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Tournament Mode. / 请登录以访问锦标赛模式。')}
            >
              <div className="h-full w-full flex flex-col relative overflow-hidden">
                <TournamentGame
                  user={currentUser!}
                  onUpdateUser={handleUpdateUser}
                  onBack={() => navigate('/tournament')}
                  volume={volume}
                />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/tournament/editor" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Tournament Mode. / 请登录以访问锦标赛模式。')}
            >
              <div className="h-full w-full flex flex-col relative overflow-hidden">
                <TournamentEditor
                  user={currentUser!}
                  onUpdateUser={handleUpdateUser}
                  onBack={() => navigate('/tournament')}
                  onQuickStart={() => navigate('/tournament/game')}
                />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/tournament/history" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Tournament Mode. / 请登录以访问锦标赛模式。')}
            >
              <div className="h-full w-full flex flex-col relative overflow-hidden">
                <TournamentHistory
                  user={currentUser!}
                  onBack={() => navigate('/tournament')}
                />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/tournament/final-submit" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Tournament Mode. / 请登录以访问锦标赛模式。')}
            >
              <div className="h-full w-full flex flex-col relative overflow-hidden">
                <TournamentFinalSubmission
                  user={currentUser!}
                  onBack={() => navigate('/tournament')}
                />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/freestyle" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Freestyle Mode. / 请登录以访问自由式模式。')}
            >
              <FreestyleLobby 
                user={currentUser!} 
                onBack={() => navigate('/')} 
              />
            </ProtectedRoute>
          } />

          <Route path="/freestyle/room/:roomId" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Freestyle Rooms. / 请登录后访问自由式房间。')}
            >
              <FreestyleRoomComponent 
                user={currentUser!} 
              />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
          <ProtectedRoute
            allow={!!currentUser}
            isSessionLoading={isSessionLoading}
            onBlocked={() => redirectToLogin('Please Login to access User Center. / 请登录后访问个人中心。')}
          >
            <div className="h-full w-full flex flex-col relative overflow-hidden">
              <UserProfile 
                user={currentUser!} 
                onBack={() => navigate('/')} 
                onUpdateUser={handleUpdateUser}
                onOpenAdmin={() => navigate('/admin')}
                onLogout={() => { void handleLogout(); }}
                volume={volume}
                onVolumeChange={setVolume}
                initialTab={profileTabFromQuery}
              />
            </div>
          </ProtectedRoute>
        } />

          <Route path="/kendama-map" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KendamaMapMode onBack={() => navigate('/')} currentUser={currentUser} />
            </ProtectedRoute>
          } />

          <Route path="/kendama-archive" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KendamaArchive onBack={() => navigate('/')} currentUser={currentUser} onRequireLogin={redirectToLogin} />
            </ProtectedRoute>
          } />

          <Route path="/wiki" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KendamaWiki onBack={() => navigate('/')} />
            </ProtectedRoute>
          } />

          <Route path="/messages" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => redirectToLogin('Please Login to access Messages. / 请登录后查看消息。')}
            >
              <div className="h-full w-full flex flex-col relative overflow-hidden">
                <Messages user={currentUser!} onBack={() => navigate('/')} initialWith={messagesWithFromQuery} />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/kwc2026" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KWC2026ActivityMode
                user={currentUser}
                onBack={() => navigate('/')}
                onRequireLogin={redirectToLogin}
              />
            </ProtectedRoute>
          } />

          <Route path="/kwc2026/tricks" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KWC2026ActivityMode
                user={currentUser}
                onBack={() => navigate('/')}
                onRequireLogin={redirectToLogin}
                page="tricks"
              />
            </ProtectedRoute>
          } />

          <Route path="/kwc2026/teamup" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KWC2026ActivityMode
                user={currentUser}
                onBack={() => navigate('/')}
                onRequireLogin={redirectToLogin}
                page="teamup"
              />
            </ProtectedRoute>
          } />

          <Route path="/kwc2026/rules" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KWC2026ActivityMode
                user={currentUser}
                onBack={() => navigate('/')}
                onRequireLogin={redirectToLogin}
                page="rules"
              />
            </ProtectedRoute>
          } />

          <Route path="/kwc2026/travel" element={
            <ProtectedRoute
              allow={true}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {}}
            >
              <KWC2026ActivityMode
                user={currentUser}
                onBack={() => navigate('/')}
                onRequireLogin={redirectToLogin}
                page="travel"
              />
            </ProtectedRoute>
          } />

          <Route path="/kwc2026-point" element={
            <ProtectedRoute
              allow={canAccessWarmupAdmin(currentUser)}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {
                if (!currentUser) {
                  redirectToLogin('Please Login to access KWC Simulated Finals. / 请登录后进入 KWC 模拟决赛。');
                  return;
                }
                redirectToAccessDenied('KWC Simulated Finals is restricted to admins. / KWC 模拟决赛仅限管理员进入。');
              }}
            >
              <KWC2026PointMode
                user={currentUser!}
                onBack={() => navigate('/')}
              />
            </ProtectedRoute>
          } />

          <Route path="/wireless-progress" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {
                redirectToLogin('Please Login to access CKO2026 Warm-up Mode. / 请登录后进入 CKO2026 预热赛模式。');
              }}
            >
              <CKO2026WarmupMode
                user={currentUser!}
                onBack={() => navigate('/')}
                onUpdateUser={handleUpdateUser}
              />
            </ProtectedRoute>
          } />

          <Route path="/wireless-progress/my-tricks" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {
                redirectToLogin('Please Login to access CKO2026 Warm-up Mode. / 请登录后进入 CKO2026 预热赛模式。');
              }}
            >
              <CKO2026WarmupMode
                user={currentUser!}
                onBack={() => navigate('/')}
                onUpdateUser={handleUpdateUser}
                page="my-tricks"
              />
            </ProtectedRoute>
          } />

          <Route path="/wireless-progress/admin" element={
            <ProtectedRoute
              allow={canManageWarmup}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {
                if (!currentUser) {
                  redirectToLogin('Please Login to access CKO2026 Warm-up Admin. / 请登录后进入 CKO2026 预热赛管理。');
                  return;
                }
                redirectToAccessDenied('Access denied. / 禁止访问。');
              }}
            >
              <div className="h-full w-full flex flex-col relative overflow-y-auto overflow-x-hidden custom-scrollbar">
                <CKO2026WarmupAdmin user={currentUser!} onBack={() => navigate('/')} />
              </div>
            </ProtectedRoute>
          } />

          <Route path="/wireless-progress/player/:userId" element={
            <ProtectedRoute
              allow={!!currentUser}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {
                redirectToLogin('Please Login to access CKO2026 Warm-up Mode. / 请登录后进入 CKO2026 预热赛模式。');
              }}
            >
              <CKO2026WarmupMode
                user={currentUser!}
                onBack={() => navigate('/')}
                onUpdateUser={handleUpdateUser}
                page="player-detail"
              />
            </ProtectedRoute>
          } />

          <Route path="/admin" element={
            <ProtectedRoute
              allow={!!currentUser && !!currentUser.isAdmin}
              isSessionLoading={isSessionLoading}
              onBlocked={() => {
                if (!currentUser) {
                  redirectToLogin('Please Login to access Admin Dashboard. / 请登录后访问管理后台。');
                  return;
                }
                redirectToAccessDenied('Access denied. / 禁止访问。');
              }}
            >
              <div className="h-full w-full flex flex-col relative overflow-y-auto overflow-x-hidden custom-scrollbar p-4 md:p-8">
                <AdminDashboard user={currentUser!} onBack={() => navigate('/')} onOpenChat={(uid) => navigate(`/messages?with=${encodeURIComponent(uid)}`)} />
              </div>
            </ProtectedRoute>
          } />
        </Routes>

        {/* 数字雨背景层：启动动画结束后，数字雨在这里接着往下落。
            放在 main 的 -z-10，排在路由自带的页面底色之后，
            所以它压在页面底色之上、所有内容之下——真正的背景层。
            底部遮挡排在数字雨之前（也就是画在它下面），
            这样数字雨是背景层里最上面的一层，不会被遮挡压暗。 */}
        {isSplashExiting && (
          <>
            <div
              aria-hidden="true"
              className="app-rain-mask pointer-events-none fixed inset-x-0 bottom-0 -z-10 h-[46%] bg-[linear-gradient(to_top,rgba(4,8,14,0.96)_0%,rgba(4,8,14,0.74)_32%,rgba(4,8,14,0.3)_66%,rgba(4,8,14,0)_100%)]"
            />
            <MatrixRainCanvas
              transparent
              className="app-rain pointer-events-none fixed inset-0 -z-10 h-full w-full"
            />
          </>
        )}

        {/* 网页端底栏：原生液态玻璃底栏可用时这段不会渲染（见 nativeTabBarActive）。
            选中态用 cyan-400 对齐原生底栏的 tintColor #22D3EE，并加一层浅色 pill 作为选中指示。 */}
        {shouldShowMobileBottomNav && !nativeTabBarActive && !isSessionLoading && (
          <div data-i18n-skip="true" className="home-enter-row-6 fixed bottom-[calc(0.75rem_+_var(--safe-bottom))] left-0 right-0 z-50 mx-4 grid grid-cols-5 gap-1 rounded-[24px] border border-white/10 bg-black/35 px-1 py-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl md:hidden">
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-current={location.pathname === '/' ? 'page' : undefined}
              className={`flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-2xl py-0.5 transition-[background-color,color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:bg-white/[0.14] ${location.pathname === '/' ? 'bg-white/[0.08] font-semibold text-cyan-400' : 'text-zinc-400'}`}
            >
              <Home size={24} strokeWidth={1.6} />
              <span className="font-pingfang text-[11px] leading-tight">{mobileHomeUiText.tabCompetition}</span>
            </button>
            <button
              type="button"
              onClick={handleKendamaMapNavigation}
              aria-current={location.pathname === '/kendama-map' ? 'page' : undefined}
              className={`relative flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-2xl py-0.5 transition-[background-color,color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:bg-white/[0.14] ${location.pathname === '/kendama-map' ? 'bg-white/[0.08] font-semibold text-cyan-400' : 'text-zinc-400'}`}
            >
              <MapPinned size={24} strokeWidth={1.6} />
              <span className="font-pingfang text-[11px] leading-tight">{mobileHomeUiText.tabMap}</span>
              <span className="absolute right-1.5 top-0.5 rounded bg-cyan-500/25 px-1 py-0.5 font-pingfang text-[9px] font-medium leading-none text-cyan-200">NEW</span>
            </button>
            <button
              type="button"
              onClick={handleMessagesNavigation}
              aria-current={location.pathname === '/messages' ? 'page' : undefined}
              className={`relative flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-2xl py-0.5 transition-[background-color,color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:bg-white/[0.14] ${location.pathname === '/messages' ? 'bg-white/[0.08] font-semibold text-cyan-400' : 'text-zinc-400'}`}
            >
              <MessageCircle size={24} strokeWidth={1.6} />
              <span className="font-pingfang text-[11px] leading-tight">{mobileHomeUiText.tabMessages}</span>
            </button>
            <button
              type="button"
              onClick={handleEncyclopediaNavigation}
              aria-current={location.pathname === '/wiki' ? 'page' : undefined}
              className={`flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-2xl py-0.5 transition-[background-color,color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:bg-white/[0.14] ${location.pathname === '/wiki' ? 'bg-white/[0.08] font-semibold text-cyan-400' : 'text-zinc-400'}`}
            >
              <BookOpen size={24} strokeWidth={1.6} />
              <span className="font-pingfang text-[11px] leading-tight">{mobileHomeUiText.tabWiki}</span>
            </button>
            <button
              type="button"
              onClick={handleProfileNavigation}
              aria-current={location.pathname === '/profile' ? 'page' : undefined}
              className={`flex min-h-[44px] flex-col items-center justify-center gap-0.5 rounded-2xl py-0.5 transition-[background-color,color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:bg-white/[0.14] ${location.pathname === '/profile' ? 'bg-white/[0.08] font-semibold text-cyan-400' : 'text-zinc-400'}`}
            >
              <UserIcon size={24} strokeWidth={1.6} />
              <span className="font-pingfang text-[11px] leading-tight">{mobileHomeUiText.tabProfile}</span>
            </button>
          </div>
        )}
      </main>

      <AnimatePresence>
        {showWinnerModal && winnerSnapshotRef.current && (
          <WinnerModal
            key="winner-modal"
            winner={winnerSnapshotRef.current.winner}
            opponent={winnerSnapshotRef.current.opponent}
            onRestart={returnToMenu}
            onHome={() => { returnToMenu(); setView('MENU'); }}
          />
        )}
      </AnimatePresence>
    </div>
    )}
    </>
  );
};

export default App;
